export * from './app-link-contract';
export * from './app-update-payload';
export * from './asset-path';
export * from './attention-contract';
export * from './browser-contract';
export * from './browser-editor-tabs';
export * from './browser-url';
export * from './bundle';
export * from './caption-contract';
export * from './capture-region';
export * from './connectivity-contract';
export * from './contribution-curation';
export * from './device-type';
export * from './document-navigation';
export * from './error-cause';
export * from './event';
export * from './host-os';
export * from './http';
export * from './json-bytes';
export * from './jump-list-contract';
export * from './keybinding-contract';
export * from './launcher-contract';
export * from './log-contract';
export * from './module-link';
export * from './native-contents-contract';
export * from './network-proxy';
export * from './nls';
export * from './nls-contract';
export * from './notification-contract';
export * from './notification-prompt';
export * from './page-world-contract';
export * from './partition-contract';
export * from './platform-agent-session';
export * from './platform-product-config';
export * from './platform-push';
export * from './platform-service-endpoint';
export * from './platform-setting-layout';
export * from './platform-settings';
export * from './product-config-keys';
export * from './progress-contract';
export * from './search-contribution';
export * from './search-filter-option-affix';
export * from './search-recent-contract';
export * from './semver';
export * from './settings-window';
export * from './shell-menu-contract';
export * from './shell-target';
export * from './surface-target';
export * from './theme';
export * from './theme-accent';
export * from './theme-contract';
export * from './update-contract';
export * from './when';
export * from './when-context-contract';

import type { AppLinkApi, AppLinkProvider } from './app-link-contract';
import { isUnsafeModuleId } from './asset-path';
import {
  APP_VISIBILITY_CHANGED_EVENT,
  type AttentionApi,
} from './attention-contract';
import type { BrowserOpenOptions, UserActivationApi } from './browser-contract';
import {
  type AssetBase,
  type BundleApi,
  type BundleDeclaration,
  type DistBundleDeclaration,
  type DistBundleSource,
  isDistBundleTargetKey,
  isHttpUrl,
  isValidSha256,
  MODULE_INSTALL_PERMISSION,
  resolveViewUrl,
} from './bundle';
import type { CaptionApi } from './caption-contract';
import {
  CONNECTIVITY_CHANGED_EVENT,
  type ConnectivityApi,
} from './connectivity-contract';
import type {
  ContributionCuration,
  ResolvedContributionCuration,
} from './contribution-curation';
import type { Disposer } from './event';
import type { HostOs } from './host-os';
import type { JumpListApi } from './jump-list-contract';
import {
  GLOBAL_SHORTCUT_RUNTIMES,
  type KeybindingScope,
  type KeybindingSource,
} from './keybinding-contract';
import type {
  LaunchBadgeProvider,
  LauncherApi,
  LaunchItemProvider,
  RailPresetLayer,
} from './launcher-contract';
import type { LogInput, ModuleLogger } from './log-contract';
import {
  isReservedModuleId,
  isValidUrlOwnershipPrefix,
  type ModuleUrlHandler,
  PLATFORM_PATH_PREFIX,
} from './module-link';
import {
  type Locale,
  LOCALE_CHANGED_EVENT,
  type LocalePreference,
  type MessageCatalog,
} from './nls-contract';
import type { AppNotification } from './notification-contract';
import {
  isValidPageWorldChannelId,
  isValidPageWorldEntry,
  type PageWorldApi,
  type PageWorldScriptDeclaration,
} from './page-world-contract';
import type { PartitionSegment } from './partition-contract';
import type { PlatformServiceRelay } from './platform-agent-session';
import {
  isValidPushChannel,
  type ModuleAppUpdateChecker,
  type ModulePushHandler,
} from './platform-push';
import { isBuildOnlyConfigKey } from './product-config-keys';
import type { ProgressApi } from './progress-contract';
import type {
  ResolvedSearchFilter,
  ResolvedSearchSection,
  SearchContribution,
  SearchFilterContribution,
} from './search-contribution';
import { satisfies } from './semver';
import {
  THEME_CHANGED_EVENT,
  type ThemeBaseColors,
  type ThemePreference,
  type ThemeTokens,
  type ThemeType,
} from './theme-contract';
import {
  PLATFORM_CONTEXT_KEY_PREFIX,
  type WhenContextApi,
} from './when-context-contract';

export const PLATFORM_VERSION = '1.0.0';

/**
 * Version of the SDK <-> platform Channel protocol (架构方案 §5.2), independent
 * of the app `PLATFORM_VERSION`: it bumps when the interconnection contract
 * (commands/events/handshake shapes) changes, letting modules declare
 * `engines.protocol` and the platform negotiate compatibility separately from
 * the product version.
 */
export const PROTOCOL_VERSION = '1.0.0';

/**
 * Named protocol features the current platform/SDK offers (架构方案 §5.2 能力探测).
 * Single source of truth, returned by the handshake so a (sandboxed) module can
 * feature-detect (`hasCapability`) before using a feature and degrade gracefully
 * — the forward/backward-compat mechanism that does not require a version bump.
 *   - `commands`      reverse `executeCommand` (见 11/22)
 *   - `events`        `onEvent` subscription (见 11/23)
 *   - `reverse-emit`  `emit` onto the EventBus (见 6)
 *   - `theming`       read design tokens + observe theme changes (见 13 / §5.10)
 *   - `i18n`          read messages + observe locale changes (见 14 / §5.11)
 *   - `logging`       forward structured log records to the platform sink (见 15 / §5.16)
 *   - `storage`       per-module isolated key-value storage (见 11 / §5.13)
 *   - `offline`       network status + offline op queue (见 11 / §5.14)
 *   - `capabilities`  permission-gated system facade: notify/fetch/fs/openExternal (见 11 / §5.6)
 */
export const PLATFORM_CAPABILITIES = [
  'commands',
  'events',
  'reverse-emit',
  'theming',
  'i18n',
  'logging',
  'navigate',
  'storage',
  'offline',
  'capabilities',
] as const;

export type PlatformCapability = (typeof PLATFORM_CAPABILITIES)[number];

/**
 * Which window a view rendering is mounted in (见 8).
 *
 * `workbench` is the main window: editor, activity bar, layout, and an
 * overlay or gate opened there. `detached` is any other window — a detached
 * surface, the web inline stand-in for one, or a module shell.
 *
 * This is the host of *this* rendering, not `ViewProps.detached` (the list of
 * surfaces popped out of the origin view). A mirror keeps both: the inline
 * copy stays `workbench`, the window copy is `detached`. Fixed at mount —
 * detaching mounts a new surface rather than moving this one.
 */
export type SurfaceHostedIn = 'workbench' | 'detached';

/** Whether `value` is a {@link SurfaceHostedIn}. Invalid input is not coerced. */
export function isSurfaceHostedIn(value: unknown): value is SurfaceHostedIn {
  return value === 'workbench' || value === 'detached';
}

/**
 * Result of the SDK handshake (架构方案 §5.2 版本协商 + 能力探测). Carries the
 * live platform/protocol versions, the host runtime, the platform capability
 * list, and whether the calling module's declared `engines` are compatible
 * (with a `reason` when not).
 */
export interface PlatformHandshake {
  /** Live platform (app) version from the running host. */
  platformVersion: string;
  /** Live SDK <-> platform protocol version from the running host. */
  protocolVersion: string;
  /**
   * Which host this sandboxed surface is running in (见 18 / 19). Same value as
   * {@link ModuleContext.runtime}: Electron desktop app vs browser web client.
   *
   * iframe / external pages have no `ModuleContext`, so the handshake is how
   * they learn the host without sniffing UA or parent origin. Prefer
   * {@link runtimeSupports} / `hasCapability` when the question is "can I do
   * X"; compare this field when the page actually needs to know *which* host
   * it is (telemetry, desktop-vs-web chrome, vendored apps with no service).
   */
  runtime: ModuleRuntime;
  /**
   * Which OS the user is on (见 18 / 19). Same value as {@link ModuleContext.os}.
   * For UI flavor and per-OS packages — not a capability, not a runtime.
   * iframe / external pages have no `hibroHost`, so this is how they learn
   * the OS without sniffing UA.
   */
  os: HostOs;
  /** Named features the platform currently offers (for `hasCapability`). */
  capabilities: string[];
  /** Whether the calling module's `engines` are satisfied by the live versions. */
  compatible: boolean;
  /** Human-readable incompatibility reason when `compatible` is false. */
  reason?: string;
  /**
   * Which instance of its view this sandboxed surface is (架构方案 §5.7 视图多
   * 实例); `undefined` = the singleton. A page opened as one of several instances
   * (one per document) reads it at handshake time to know *which* document it is.
   *
   * Delivered over the channel rather than in the iframe URL, for the same
   * reason VS Code hands a custom editor its document over the message channel
   * and keeps the webview URL an opaque per-frame origin: identity is not part
   * of the address, and putting it there would make it origin-visible and pin
   * the URL shape into the contract. The subsequent `onNavigate` locations carry
   * *where inside* the document (点), never *which* document (面).
   */
  instanceKey?: string;
  /**
   * Which window this iframe connection was mounted in (见 8). Present on
   * in-client iframe connections; absent for an external process and for a
   * debug page that is not hosted in a window. Same meaning as
   * {@link ViewProps.hostedIn}. Not part of the iframe URL, and fixed at
   * connect: detaching mounts a new frame rather than moving this connection.
   */
  hostedIn?: SurfaceHostedIn;
}

/**
 * How a single view *renders*, decoupled from where the owning module's code
 * runs (架构方案 §5.8). `kind` (view-level) governs the render surface, and the
 * only constraint is that the manifest supplies the channel that kind needs
 * (供给能力 × kind, 见 {@link validateManifest}), so kinds may be mixed freely
 * within one module:
 * - `inline`: a renderer-side React component (`entry.ui` / in-repo glob);
 * - `iframe`: a sandboxed in-DOM `<iframe>` loaded over `hibro-assets://` (见 9);
 * - `external`: a control panel for an external-process module (见 10);
 * - `remote`: architecture-reserved, no inline render kind this phase.
 * Omitted on a {@link ViewContribution} → resolved by {@link resolveViewKind}.
 * The `url` field's meaning follows `kind`: an HTML page for `iframe`, a
 * downloaded JS bundle for `inline`.
 */
export type ViewRenderKind = 'inline' | 'iframe' | 'external' | 'remote';

/**
 * The kind a view actually renders as (架构方案 §5.8) — the one resolver every
 * consumer (renderer dispatch, `validateManifest`, bundle discovery) goes
 * through, so a view's kind is decided in exactly one place:
 *
 * 1. the view's declared `kind`, if any;
 * 2. else from what the manifest *supplies*: a view `url` means a sandboxed page
 *    (`iframe`); otherwise a module that owns a child process but ships no
 *    renderer UI gets its process control panel (`external`); everything else is
 *    plain renderer React (`inline`).
 *
 * There is no module-level render label any more (the former `integration`): a
 * module mixes kinds freely, so anything a view's own declaration + the manifest's
 * supply cannot express has to be written as an explicit `kind`.
 */
export function resolveViewKind(
  view: Pick<ViewContribution, 'kind' | 'url'>,
  manifest: Pick<ModuleManifest, 'entry'>,
): ViewRenderKind {
  if (view.kind) {
    return view.kind;
  }
  if (view.url) {
    return 'iframe';
  }
  const facets = deriveModuleFacets(manifest);
  return facets.hasExternalProcess && !facets.hasRendererUi
    ? 'external'
    : 'inline';
}

/**
 * Whether a view of this render kind can exist on `runtime` (见 18 / 19).
 *
 * The module-level {@link ModuleManifest.runtimes} declaration answers "can this
 * module run here"; this answers the finer question for a **mixed** module that
 * does run here but contributes some views that cannot. An `external` view is the
 * control panel for an OS child process (架构方案 §3.1/§5.8), so it needs the
 * runtime to be able to spawn one — otherwise it is dropped, along with the
 * launchers pointing at it, rather than rendering a surface whose process can
 * never start.
 *
 * Phrased as a {@link RuntimeCapabilityProfile} lookup rather than a comparison
 * against a runtime name: the requirement is "this needs a child process", not
 * "web is special", and a future runtime answers it by declaring its own row.
 *
 * Deliberately narrow: `iframe` views work on web (served same-origin instead of
 * `hibro-assets://`, 见 9), and `detachable` fragments are unimplemented rather than
 * impossible (`08`/`18` design them on `window.open`), so neither is filtered.
 */
export function isViewKindSupportedOnRuntime(
  kind: ViewRenderKind,
  runtime: ModuleRuntime,
): boolean {
  return kind !== 'external' || runtimeSupports(runtime, 'childProcess');
}

/**
 * 一个模块的正交执行事实（架构方案 §3/§5.6）。模块形态是这些事实的自由组合——一个模块
 * 可以同时有 in-process service、沙箱 iframe 视图和自带子进程（混合模块，见 `12`），所以
 * 平台不认任何「集成方式」粗标签，只判这些事实，信任判据落在「代码跑在哪 + 从哪部署」上。
 */
export interface ModuleFacets {
  /**
   * 有跑在 extension host 进程内的代码（声明了 `entry.service` 且 `serviceTier: "host"`，
   * 见 24）。这类代码运行期**无法沙箱**（全 Node 能力、与平台同进程），所以信任只能来自
   * 部署期：第一方随包，或下发模块经上架审核单独放行 host 档（§3/§5.6）。
   */
  runsHostCode: boolean;
  /**
   * 有跑在 service 沙盒里的代码（声明了 `entry.service`，`serviceTier` 为 `sandbox` 或不写，
   * 见 24）。代码在 QuickJS 里、零环境权限，`context.*` 全走门控，与 iframe / external
   * 同属**可**在运行期约束的一类。与 {@link runsHostCode} 互斥。
   */
  runsSandboxedService: boolean;
  /**
   * 有跑在渲染进程内的 React 代码（声明了 `entry.ui`）。与 in-process service 同一信任
   * 级（同进程、不可沙箱），只是执行环境是渲染端。
   */
  hasRendererUi: boolean;
  /**
   * 声明了由平台托管的子进程接入点（`entry.external`，一条或多条）。其代码在进程外，
   * 反向调用一律经门控 RPC（`connectGated`），是**可**在运行期约束的那一类（见 `10`）。
   * 只表示「声明了这个接入点」；当前平台上是否真能拉起还要看 `command`
   * （见 `toExternalLaunchSpec`）。
   */
  hasExternalProcess: boolean;
  /**
   * 随应用发布（仓内 / 随包）、非运行期安装，即发现时 `dynamic !== true`。下发、侧载与
   * dev 链接的模块都是 `dynamic: true`，故都不是第一方。
   */
  isFirstParty: boolean;
}

/**
 * 从 manifest（加发现来源）推导 {@link ModuleFacets}——所有「该模块是什么形态」的判断的
 * **单一真源**，避免同一判断在各处各写一遍。纯函数、可单测。
 *
 * 约定（第一方模块的隐含前提）：仓内模块若带 ext-host 代码，**必须**声明
 * `entry.service`。壳层静态发现按 `modules` 下各模块的 `src/index.ts` glob 配对加载器，不看
 * manifest，所以一个有 `src/index.ts` 却不声明 `entry.service` 的仓内模块会实际执行
 * in-process 代码而在这里被判为 `runsHostCode: false`。当前仓内模块全部满足该约定
 * （见 `12`）；新增模块请一并声明，且要进程内运行就显式写 `serviceTier: "host"`
 * （不写即 sandbox，见 24）。
 */
export function deriveModuleFacets(
  manifest: Pick<ModuleManifest, 'entry' | 'serviceTier'>,
  options: { dynamic?: boolean } = {},
): ModuleFacets {
  const entry = manifest.entry;
  // Desktop and web both count: a service entry for either is code this
  // repository's hosts load, whichever process is asking. `mobile` does not —
  // neither host resolves that key, so a mobile-only entry must not look like
  // host or sandbox code here.
  const hasService = LOADED_SERVICE_RUNTIMES.some(
    (runtime) => resolveServiceEntry(entry, runtime) !== undefined,
  );
  const hostTier = resolveServiceTier(manifest) === 'host';
  return {
    runsHostCode: hasService && hostTier,
    runsSandboxedService: hasService && !hostTier,
    hasRendererUi: isDeclaredEntryPath(entry?.ui),
    hasExternalProcess: listExternalEntries(entry).length > 0,
    isFirstParty: options.dynamic !== true,
  };
}

/** 入口路径是否真的声明了（非空、非空白），与各发现路径的 trim 判定一致。 */
function isDeclaredEntryPath(path: string | undefined): boolean {
  return typeof path === 'string' && path.trim().length > 0;
}

/**
 * 把 {@link ModuleFacets} 拼成一行可展示的形态摘要，供设置面板的模块列表等 UI 用。
 *
 * 输出各执行事实的短名（`service`/`ui`/`external`）；三者皆无即没有任何进程内代码或子进程，
 * 只有沙箱面（iframe 视图），记作 `sandboxed`。不含 `isFirstParty`——部署来源由列表本身的
 * 分区（已下发 / 已链接）表达。
 */
export function describeModuleFacets(facets: ModuleFacets): string {
  const parts: string[] = [];
  if (facets.runsHostCode) {
    parts.push('service');
  }
  if (facets.runsSandboxedService) {
    parts.push('sandboxed service');
  }
  if (facets.hasRendererUi) {
    parts.push('ui');
  }
  if (facets.hasExternalProcess) {
    parts.push('external');
  }
  return parts.length > 0 ? parts.join(' + ') : 'sandboxed';
}

/**
 * Namespace reserved for the platform's own built-in commands (架构方案 §5.9),
 * e.g. `workbench.openSettings`. Modules are confined to their own id namespace
 * regardless; naming the reservation here is what lets both ends agree —
 * `validateManifest` rejects a module claiming it, the renderer's built-in table
 * owns it.
 */
export const PLATFORM_COMMAND_PREFIX = 'workbench.';

/**
 * IPC channel: main -> renderer, run a workbench command by id (见 08 / 23 托盘).
 *
 * Native chrome the main process draws outside any window — the tray menu today,
 * a Dock or application menu later — has no other way to reach the workbench.
 * `navigate` is deliberately not that way: 见 08 draws the line at whether the
 * action has a destination *surface*, and "open settings" is the example it
 * gives of one that does not.
 *
 * Carries a command id plus its arguments — the latter because the tray's open
 * rows (需求 §4.6.2) all run `workbench.open` and differ only in the launchable
 * they name, exactly as a keybinding's declared `args` make one id serve many
 * targets (见 21). The receiving window dispatches it through the same path a
 * palette entry takes, so built-ins are handled locally and anything else falls
 * through to the extension host — no second code path, and no list of
 * tray-specific actions to keep in sync.
 */
export const RUN_COMMAND_IPC = 'hibro:workbench:run-command';

/**
 * A command a module contributes (架构方案 §5.9). The declaration describes
 * *what the command is*, never where it is surfaced: which presentation face
 * lists it (palette, global search, a menu) and how many it shows at a time are
 * host decisions, so no field here encodes a layout choice.
 */
export interface CommandContribution {
  /**
   * Command id, namespaced `<module>.<command>` — enforced by
   * `validateManifest`, not merely conventional: ids share one global registry,
   * ownership is resolved by manifest lookup (not by parsing the prefix), and
   * `executeCommandAsModule` exempts a caller from the permission gate for its
   * *own* commands. Without the rule a module could declare another's id and
   * inherit that exemption. It is also the key for `executeCommand`, the target
   * of menu and keybinding contributions, and the subject of a `command:<id>`
   * permission.
   */
  id: string;
  /**
   * Human-facing label shown wherever the command surfaces. Supports the `%key%`
   * NLS placeholder form.
   */
  title: string;
  /**
   * Grouping label, e.g. `Workspace` for `Workspace: Reset layout`. A face may
   * render it as a title prefix or as a group heading — that choice is the
   * host's. Supports `%key%`.
   */
  category?: string;
  /**
   * One-line explanation for the user, surfaced as secondary text or a tooltip.
   * Distinct from any description an AI tool needs: a command exposed to the
   * model does that through its `ai.tool` contribution `data` (见 17), so the
   * kernel contract stays business-agnostic. Supports `%key%`.
   */
  description?: string;
  /**
   * Search aliases — matched but never displayed, so a user can find "sign out"
   * by typing "logout" or "注销". Either a plain array, or a single `%key%`
   * placeholder resolving to a comma-separated list (one catalog entry per
   * command beats one per synonym for translators).
   */
  keywords?: string | string[];
  /**
   * Icon for faces that render one. Same manifest shorthand as an activityBar
   * item (bare name → builtin, path-like → module resource);
   * `listContributions` normalizes it to an {@link IconSpec}.
   */
  icon?: IconSpec | string;
  /**
   * Opt-in to user-facing lists (palette, search, menus). **Omitted or `true`
   * hides the command**; only an explicit `false` lists it. `executeCommand`
   * is unaffected either way. Orthogonal to {@link requiresPermission}: this
   * one decides who *sees* the command, that one decides who may *call* it.
   *
   * Default-hidden because most declared commands are UI-to-service bridges
   * (`getXxx` / `submitXxx`). Forgetting the flag must not leak machinery into
   * the user's list.
   */
  internal?: boolean;
  /**
   * Availability condition ({@link evaluateWhen} grammar) applying to *every*
   * presentation face and keybinding. Distinct from `MenuContribution.when`,
   * which gates one menu location only. Purely presentational: a programmatic
   * `executeCommand` is never blocked by it. How an unmet condition reads is up
   * to the face — a palette hides the entry, a menu greys it out.
   */
  enablement?: string;
  /**
   * Runtimes this command exists on, defaulting to the module's own `runtimes`.
   * Needed because a module may ship a narrower service entry on one runtime
   * (见 19) and register fewer commands there; without this the command would
   * be listed everywhere and only fail at invocation time.
   */
  runtimes?: ModuleRuntime[];
  /**
   * When true, a cross-module reverse-call of this command goes through the
   * permission control flow (declare `command:<id>` + grant); otherwise the
   * command is callable directly (default-open, 架构方案 §5.6). The owning
   * module marks the commands it wants gated.
   */
  requiresPermission?: boolean;
}

/**
 * Vertical slot of a view inside a rail-like area (架构方案 §5.7), currently
 * consumed by the `activityBar`: `top` and `bottom` pin the view to the fixed
 * ends; `middle` flows in contribution order inside the flexible, scrollable
 * center that fills the remaining height.
 */
export type RailPosition = 'top' | 'middle' | 'bottom';

/**
 * Editor-area keep-alive policy for a view (架构方案 §5.7 视图缓存): whether a
 * mounted instance is kept hidden (state preserved) when the user switches to
 * another editor view or another tab, instead of being unmounted immediately.
 * - `keep-alive`: hide but keep mounted *and in the DOM*; unmounted only on
 *   eviction (MRU cap, view disappearance, detach) — the deactivate lifecycle
 *   runs at that point. A hidden instance gets `ViewProps.visible === false`.
 * - `none`: stop rendering as soon as the view stops being the active one.
 * When omitted the platform defaults by the view's {@link ViewRenderKind}: the
 * inline-content kinds (`inline` / `iframe`) default to `keep-alive`, because
 * losing scroll position and in-progress input on every tab switch is the more
 * surprising behaviour; declare `cache: 'none'` on a view whose state is either
 * cheap to rebuild or actively wrong to preserve. `external` and the reserved
 * `remote` have no inline content and are never cached.
 */
export type ViewCachePolicy = 'keep-alive' | 'none';

export interface ViewContribution {
  /**
   * Stable view id. Namespaced by convention as the module id or
   * `<moduleId>.<view>` (e.g. `ai.panel`, see {@link isInModuleNamespace}).
   * An id outside that namespace still loads; {@link checkViewIdNamespace}
   * records an advisory rather than rejecting the module. It is the handle
   * everything else points at: `openView` actions, `navigate:view:<id>`
   * permissions, activity-bar `viewId`, and the renderer's mounted-instance
   * keys.
   */
  id: string;
  /**
   * Where the view wants to live; see {@link ViewArea}. Docked layout areas
   * render it inside the workbench, while `overlay` and `gate` hand it to a host
   * outside the layout. Some combinations with the resolved render kind are
   * rejected by `validateManifest` (e.g. `external` only in `editor`).
   */
  area: ViewArea;
  /**
   * Human-facing label for the view's tab or panel header, `%key%` NLS form
   * supported. Effectively required for a docked view (it is what the tab shows,
   * falling back to the raw `id`), but genuinely optional for `overlay` and
   * `gate` views: those are titled by the `openModal`/`openDrawer` call that
   * mounts them, so the platform never reads this field for them.
   */
  title?: string;
  /**
   * How this view renders (架构方案 §5.8); see {@link ViewRenderKind}. Omitted →
   * resolved by {@link resolveViewKind} from what the manifest supplies. Must be
   * a kind the manifest supplies —
   * `inline` needs renderer UI code, `iframe` a `url`, `external` an
   * `entry.external` — which {@link validateManifest} enforces fail-closed.
   */
  kind?: ViewRenderKind;
  /**
   * For `kind: 'external'` views (架构方案 §3.1/§5.8): which `entry.external`
   * entry this control panel drives, by its `id`. Required only when the module
   * declares several entries (naming none would be ambiguous — rejected at
   * validation); a module with a single entry omits it and the panel drives that
   * one. Two views naming different entries therefore control two independent
   * processes.
   */
  externalEntry?: string;
  /**
   * Where the view sits inside a rail-like area (架构方案 §5.7): `top`/`bottom`
   * are fixed ends, `middle` (default) flows in the scrollable center.
   */
  position?: RailPosition;
  /**
   * Keep-alive policy for this view's editor pane and instance tabs (架构方案
   * §5.7 视图缓存); see {@link ViewCachePolicy}. Omitted → default by the view's
   * render kind (`inline` / `iframe` keep alive, the rest never do).
   */
  cache?: ViewCachePolicy;
  /**
   * Whether this view may be popped out into a standalone window
   * (架构方案 §5.8 `detach`/`attach`). Defaults to false.
   */
  detachable?: boolean;
  /**
   * Content location for non-source views (架构方案 §5.8). For `iframe` (见 9)
   * the renderer mounts a sandboxed in-DOM `<iframe>` with this URL as `src`.
   * Ignored for `source` views (which render a component). An `iframe` view is
   * the only carrier for external web content, inline or detached (见 9).
   * Module-root-relative URLs are resolved to `hibro-assets://<moduleId>/<path>` by the
   * ext-host (见 12); eventually sourced from `entry`/`bundle` (§5.3/§5.4).
   */
  url?: string;
  /**
   * When true, a cross-surface navigate to this view (`navigate:view:<id>`)
   * goes through the permission control flow (declare + grant); otherwise it is
   * navigable directly (default-open, 架构方案 §5.6). The owning module marks
   * the views it wants gated.
   */
  requiresPermission?: boolean;
}

/**
 * A native dockview tab exposed to renderer-side module UI. This is a stable
 * platform abstraction: modules never receive DockviewApi or panel objects.
 */
export interface TabHostTab {
  readonly id: string;
  readonly area: LayoutArea;
  readonly viewId: string;
  readonly moduleId: string;
  readonly instanceKey?: string;
  readonly title: string;
  readonly active: boolean;
}

/** Open one view instance in a native tabbed workbench area. */
export interface TabHostOpenOptions {
  readonly viewId: string;
  readonly moduleId: string;
  readonly instanceKey?: string;
  readonly title: string;
  readonly activate?: boolean;
}

/**
 * Renderer-only native tab capability. Implementations may queue operations
 * while the persisted dockview layout is restoring.
 */
export interface TabHostApi {
  readonly area: LayoutArea;
  readonly phase: 'building' | 'restoring' | 'ready';
  /**
   * Increments after each completed layout build/restore/reset. Controllers use
   * it to distinguish a transient rebuild empty state from a user closing the
   * last tab.
   */
  readonly revision: number;
  readonly visible: boolean;
  getTabs(): readonly TabHostTab[];
  isOpen(viewId: string, instanceKey?: string): boolean;
  open(options: TabHostOpenOptions): void;
  focus(viewId: string, instanceKey?: string): void;
  close(viewId: string, instanceKey?: string): void;
  setTitle(
    viewId: string,
    instanceKey: string | undefined,
    title: string,
  ): void;
  setVisible(visible: boolean): void;
  subscribe(listener: () => void): Disposer;
}

/** Common module capabilities supplied to tab controllers and custom chrome. */
export interface TabHostModuleProps {
  readonly moduleId: string;
  readonly viewId: string;
  readonly host: TabHostApi;
  readonly executeCommand: (id: string, ...args: unknown[]) => Promise<unknown>;
  readonly onEvent: (
    event: string,
    handler: (data: unknown) => void,
  ) => Disposer;
  readonly t: (key: string, args?: Record<string, unknown>) => string;
}

export type TabHostControllerProps = TabHostModuleProps;

export interface TabHostTabProps extends TabHostModuleProps {
  readonly tab: TabHostTab;
}

export interface TabHostHeaderActionsProps extends TabHostModuleProps {
  readonly activeTab?: TabHostTab;
}

export type TabHostController = (props: TabHostControllerProps) => unknown;
export type TabHostTabRenderer = (props: TabHostTabProps) => unknown;
export type TabHostHeaderActionsRenderer = (
  props: TabHostHeaderActionsProps,
) => unknown;

/** Renderer-local custom chrome for one contributed view. */
export interface ViewTabChrome {
  readonly tab?: TabHostTabRenderer;
  readonly headerActions?: TabHostHeaderActionsRenderer;
}

/**
 * How a detached surface treats the origin region (架构方案 §5.8 通用 Detachable):
 * - `migrate`: unmount the inline region and show a placeholder (Focus/Attach),
 *   restored when the window closes — the classic whole-view detach behaviour;
 * - `mirror`: leave the origin region untouched and open an independent, ext-host
 *   synchronized duplicate window; the same source may be mirrored into several
 *   windows and closing one only closes that projection.
 */
export type DetachMode = 'migrate' | 'mirror';

/**
 * Identifies a detached surface (架构方案 §5.8 通用 Detachable). `fragmentId`
 * absent = the whole-view detach special case (backward compatible with the
 * original `detach(viewId)`); `instanceKey` distinguishes multiple windows of
 * the same fragment (e.g. one per conversation).
 */
export interface DetachHandle {
  viewId: string;
  fragmentId?: string;
  instanceKey?: string;
}

/**
 * Serialize a {@link DetachHandle} to a stable string key (架构方案 §5.8). Pure
 * so main-process registry and renderer origin-state comparisons derive an
 * identical key. Uses `\u0000` separators (never valid in ids) so the parts are
 * unambiguous regardless of `:`/`/` inside a viewId.
 */
export function serializeDetachHandle(handle: DetachHandle): string {
  return [
    handle.viewId,
    handle.fragmentId ?? '',
    handle.instanceKey ?? '',
  ].join('\u0000');
}

/**
 * Full options accepted by the platform detach mechanism (架构方案 §5.8 通用
 * Detachable) — the IPC/bridge shape carrying the owning `viewId`/`moduleId`.
 */
export interface DetachFragmentOptions {
  viewId: string;
  moduleId: string;
  fragmentId?: string;
  instanceKey?: string;
  mode?: DetachMode;
  payload?: unknown;
  width?: number;
  height?: number;
  /**
   * 是否渲染游离窗标准壳（CustomHeader + attach-back）。设 `false` 时裸挂载
   * 视图——无标题栏无内边距，视图自绘拖拽/控件（典型用于通话浮窗）。默认 `true`。
   */
  chrome?: boolean;
  /** 窗口是否始终置顶。默认 `false`。 */
  alwaysOnTop?: boolean;
  /** 窗口是否可缩放。默认 `true`。 */
  resizable?: boolean;
  /** 最小宽度（px）；省略时沿用平台默认。 */
  minWidth?: number;
  /** 最小高度（px）；省略时沿用平台默认。 */
  minHeight?: number;
  /** 初始 x 坐标（屏幕坐标，可选）。 */
  x?: number;
  /** 初始 y 坐标（屏幕坐标，可选）。 */
  y?: number;
}

/**
 * A detached surface plus how it treats its origin region (架构方案 §5.8). The
 * shape broadcast to renderers and returned by `listDetached`.
 */
export interface DetachedSurfaceInfo extends DetachHandle {
  mode: DetachMode;
}

/**
 * Detached-surface channel (架构方案 §5.8), consumed by the **trusted renderer
 * shell** over auto RPC — the counterpart of {@link GATE_CHANNEL}.
 *
 * Deliberately its own channel rather than methods on `IModuleService`:
 * `IGatedModuleService` *extends* that interface, so anything added there is
 * simultaneously handed to every sandboxed iframe/external caller.
 * Detaching is in-process-only by design (like `context.overlay`/`context.gate`),
 * and a separate channel makes that boundary a type-level fact instead of a rule
 * someone has to remember.
 */
export const DETACH_CHANNEL = 'detach';

/**
 * EventBus event: the set of detached surfaces changed (opened, closed, or
 * adopted after a host restart). Payload is {@link DetachedSurfaceInfo}`[]` —
 * the whole set, not a delta, so a late subscriber needs no replay.
 */
export const DETACHED_CHANGED_EVENT = 'platform.detached.changed';

/**
 * Detached-window facade for the renderer shell (架构方案 §5.8). The same three
 * verbs as {@link WindowApi} plus {@link IDetachService.list} for the initial
 * read — a window may already be detached before this renderer connected, so
 * boot reads the set rather than waiting for a broadcast (同 `IGateService`).
 *
 * Fire-and-forget like the service-side face: the promise resolves once the host
 * has taken the request, not once the OS window exists.
 */
export interface IDetachService {
  /** Open (or focus an already-open) detached window for these options. */
  open(options: DetachFragmentOptions): Promise<void>;
  /** Close the window hosting `handle` (= attach the surface back). */
  close(handle: DetachHandle): Promise<void>;
  /** Focus the window hosting `handle`. */
  focus(handle: DetachHandle): Promise<void>;
  /** The currently detached surfaces (read at boot / on reconnect). */
  list(): Promise<DetachedSurfaceInfo[]>;
}

/**
 * View-scoped detach options (架构方案 §5.8 通用 Detachable) passed to
 * {@link ViewProps.detach}. The owning `viewId`/`moduleId` are filled by the
 * platform, so a view only names the fragment/instance/behaviour; omitting
 * `fragmentId` detaches the whole view (the `migrate` special case).
 */
export interface ViewDetachOptions {
  fragmentId?: string;
  instanceKey?: string;
  mode?: DetachMode;
  payload?: unknown;
  width?: number;
  height?: number;
  /** 见 {@link DetachFragmentOptions.chrome}。 */
  chrome?: boolean;
  /** 见 {@link DetachFragmentOptions.alwaysOnTop}。 */
  alwaysOnTop?: boolean;
  /** 见 {@link DetachFragmentOptions.resizable}。 */
  resizable?: boolean;
  /** 见 {@link DetachFragmentOptions.minWidth}。 */
  minWidth?: number;
  /** 见 {@link DetachFragmentOptions.minHeight}。 */
  minHeight?: number;
  /** 见 {@link DetachFragmentOptions.x}。 */
  x?: number;
  /** 见 {@link DetachFragmentOptions.y}。 */
  y?: number;
}

/**
 * Service-side options for opening a detached window (架构方案 §5.8)。
 * `moduleId` 由平台从调用模块补全，调用方只给 view/fragment 三元组。
 */
export type WindowOpenDetachedOptions = Omit<DetachFragmentOptions, 'moduleId'>;

/**
 * Service-side window facade (架构方案 §5.8)：开/关/聚焦游离窗。
 * fire-and-forget（与 `platform.navigate` 同构）；关/焦按 {@link DetachHandle}。
 */
export interface WindowApi {
  /** 打开（或聚焦已有同 handle 的）游离窗。 */
  openDetached(options: WindowOpenDetachedOptions): Promise<void>;
  /** 关闭承载该 handle 的游离窗（= attach）。 */
  close(handle: DetachHandle): Promise<void>;
  /** 聚焦承载该 handle 的游离窗。 */
  focus(handle: DetachHandle): Promise<void>;
}

/**
 * OS 窗口控件（架构方案 §5.8）：裸壳游离窗自绘 chrome 时由平台注入。
 * 结构对齐渲染端 `WindowControlsBridge`，不新增主进程能力。
 */
export interface WindowControlsApi {
  minimize(): void;
  toggleMaximize(): void;
  close(): void;
  isMaximized(): Promise<boolean>;
  onMaximizeChange(listener: (maximized: boolean) => void): () => void;
  /** 订阅窗口失活（失焦）/激活（聚焦）变化（true=失活 / false=激活）；返回取消订阅函数。 */
  onWindowBlur(listener: (blurred: boolean) => void): () => void;
  /** 切换真全屏（区别于最大化：铺满显示器、隐藏系统任务栏）。 */
  toggleFullScreen(): void;
  /** 当前是否处于真全屏。 */
  isFullScreen(): Promise<boolean>;
  /** 设置/取消窗口置顶（始终在其它窗口之上）。 */
  setAlwaysOnTop(flag: boolean): void;
  /** 当前是否置顶。 */
  isAlwaysOnTop(): Promise<boolean>;
  /** 设置窗口尺寸（宽/高，CSS 像素）。 */
  setSize(width: number, height: number): void;
  /** 设置窗口**最小尺寸**（拖拽缩放下限，CSS 像素）；传 0 = 解除该轴下限。
   *  qv 浮窗需要它：浮窗（258×258）比默认窗下限还小，而 min 只能在开窗时定一次、
   *  两种模式又是同一窗原地切换 —— 开窗若按默认窗下限定死，浮窗会被系统
   *  `WM_GETMINMAXINFO` 夹成竖条。故进浮窗时临时降 min、退出时恢复。 */
  setMinimumSize(width: number, height: number): void;
  /** 读取窗口当前尺寸（宽/高）。 */
  getSize(): Promise<{ width: number; height: number }>;
  /** 设置窗口相对当前显示器工作区的位置（CSS margin 语义：left/top 为距左/顶边、
   *  right/bottom 为距右/底边）；四边都传 0 = 完全居中。 */
  setPosition(margin: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  }): void;
}

/**
 * Click behavior of a default-rendered activityBar item (架构方案 §5.7). A
 * launcher needs no view of its own: `command` runs a command, `openView` opens
 * a content view as a tab. Omitting the action makes the item non-interactive.
 *
 * `openView.resolveInstance` (§5.7 视图多实例) keeps an instance-keyed view
 * declarative: instead of opening a bare singleton, the platform runs this
 * command — expected to return an {@link OpenViewRequest} — to resolve *which*
 * instance to open/focus. The item still carries `viewId`, so it keeps the `openView`
 * affordances: the active-tab highlight matches by *base* `viewId`, and the
 * detach menu / run-state dot still apply.
 */
export type ActivityBarItemAction =
  | { type: 'openView'; viewId: string; resolveInstance?: string }
  | { type: 'command'; command: string };

/**
 * A command result asking the platform to open (or focus) a view instance in the
 * editor area (架构方案 §5.7 视图多实例). Returned by a command wired to an
 * `activityBar` `command` action so it can create fresh, independently-keyed
 * instances of the same `viewId` — e.g. `ai.newSession` returns
 * `{ __appOpenView: { viewId: 'ai.panel', instanceKey: <sessionId> } }` and the
 * workbench opens a new session tab. Omitting `instanceKey` opens/focuses the
 * singleton. A tagged, JSON-serializable envelope (it crosses the RPC boundary);
 * the platform only routes it — the command still owns the underlying state.
 */
export interface OpenViewRequest {
  readonly __appOpenView: {
    readonly viewId: string;
    readonly instanceKey?: string;
  };
}

/** Whether a command result is an {@link OpenViewRequest} envelope (§5.7). */
export function isOpenViewRequest(value: unknown): value is OpenViewRequest {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const envelope = (value as OpenViewRequest).__appOpenView;
  return (
    typeof envelope === 'object' &&
    envelope !== null &&
    typeof envelope.viewId === 'string'
  );
}

/** Build an {@link OpenViewRequest} for a command to return (§5.7). */
export function openViewRequest(
  viewId: string,
  instanceKey?: string,
): OpenViewRequest {
  return {
    __appOpenView:
      instanceKey === undefined ? { viewId } : { viewId, instanceKey },
  };
}

/**
 * Normalized icon descriptor for a contribution (架构方案 §5.7 / 见 16). Two
 * sources, both resolved with **no module activation**:
 * - `builtin`: a semantic name looked up in the renderer's static icon registry
 *   (`@hibro/ui-client`'s `ICON_REGISTRY`), so platform-owned icons stay swappable
 *   in one place;
 * - `resource`: a module-shipped image already resolved to a loadable
 *   `hibro-assets://<moduleId>/<path>` URL (relative paths normalized at `listContributions`
 *   time, traversal dropped fail-closed). `dark` falls back to `light` when absent.
 *
 * In a manifest the field may also be written as a string shorthand
 * (`ActivityBarItemContribution.icon` / `iconActive`): a bare name → `builtin`,
 * a path-like value (contains `/` or a file extension) → `resource`. The string
 * is normalized to this shape before reaching the renderer.
 */
export type IconSpec =
  | { type: 'builtin'; id: string }
  | { type: 'resource'; light: string; dark?: string; mono?: boolean };

/**
 * Normalize a manifest icon shorthand to an `IconSpec` with **no module
 * activation** (架构方案 §5.7 / 见 16), called at `listContributions`:
 * - a bare string (no `/`, no file extension) → `{ type: 'builtin', id }`;
 * - a path-like string → `{ type: 'resource' }` whose `light` is the path
 *   resolved to `hibro-assets://<moduleId>/...` via `resolveViewUrl`;
 * - an object `resource` re-resolves its `light`/`dark` module-relative paths the
 *   same way; an object `builtin` passes through.
 *
 * Fail closed like view URLs: when a `resource` path traverses/escapes the module
 * root (`resolveViewUrl` → `undefined`) the whole icon is dropped (`undefined`),
 * so a bad path never yields an unresolved or escaping reference.
 */
export function normalizeIconSpec(
  icon: IconSpec | string | undefined,
  moduleId: string,
  assetBase?: AssetBase,
): IconSpec | undefined {
  if (icon === undefined) {
    return undefined;
  }
  if (typeof icon === 'string') {
    // Path-like (has a separator or a file extension) → module resource;
    // otherwise a bare semantic name into the builtin registry.
    const pathLike = /[\\/]/.test(icon) || /\.[a-z0-9]+$/i.test(icon);
    if (!pathLike) {
      return { type: 'builtin', id: icon };
    }
    const light = resolveViewUrl(icon, moduleId, assetBase);
    return light === undefined ? undefined : { type: 'resource', light };
  }
  if (icon.type === 'builtin') {
    return icon;
  }
  const light = resolveViewUrl(icon.light, moduleId, assetBase);
  if (light === undefined) {
    return undefined;
  }
  const dark =
    icon.dark === undefined
      ? undefined
      : resolveViewUrl(icon.dark, moduleId, assetBase);
  // `mono` opts the resource into CSS-mask tinting (renderer paints it with the
  // ambient `currentColor` so it follows selection/hover like a builtin icon,
  // 见 16); preserved verbatim, undefined stays absent.
  return dark === undefined
    ? { type: 'resource', light, mono: icon.mono }
    : {
        type: 'resource',
        light,
        dark,
        mono: icon.mono,
      };
}

/**
 * A launcher/icon in the activityBar rail (架构方案 §5.7), decoupled from views:
 * an item may have no view at all (a `command` action), open a view
 * (`openView`), or render its own component (`custom`). `position` slots it into
 * the rail's top/middle/bottom section (defaults to `middle`).
 */
export interface ActivityBarItemContribution {
  /** Item id, namespaced `<module>.<item>` by convention. */
  id: string;
  /**
   * Human-facing label, used as the item's tooltip and — when no `icon` is
   * declared — as the source of the default button's first letter. Supports the
   * `%key%` NLS placeholder form.
   */
  title: string;
  position?: RailPosition;
  /**
   * When true the owning module renders its own rail component (provided by the
   * `viewId` view) and controls its own click behavior — e.g. the account
   * avatar. When false/omitted the platform draws a default first-letter button
   * driven by `action`.
   */
  custom?: boolean;
  /** The view whose component renders this item when `custom` is true. */
  viewId?: string;
  /**
   * Whether the platform shows `title` beside the icon when the activityBar is
   * expanded. Defaults to `true`. Set `false` for self-explanatory custom faces
   * such as an account avatar; the title remains available as a tooltip /
   * accessible label and is not removed from the item.
   */
  showExpandedTitle?: boolean;
  /** Click behavior for the default-rendered button (ignored when `custom`). */
  action?: ActivityBarItemAction;
  /**
   * Tooltip hint for the item's *re-activation* gesture (见 21): clicking an
   * already-active item additionally invokes the handler its owner registered
   * through {@link ModuleUiContext.registerRailActivation}. Supports the `%key%`
   * NLS placeholder form.
   *
   * The gesture is otherwise invisible, so the platform appends this line to the
   * tooltip — but only while the item is active *and* a handler is registered, so
   * the hint never promises something that would not happen. The text has to come
   * from the module: the platform knows a handler exists, not what it does.
   */
  reactivateHint?: string;
  /**
   * Idle glyph for the default-rendered button (ignored when `custom`). In a
   * manifest this is usually a string shorthand (a bare semantic name → builtin
   * registry, a path-like value → a module-shipped `resource`); `listContributions`
   * normalizes it to an `IconSpec`. When absent the platform falls back to the
   * title's first letter.
   */
  icon?: IconSpec | string;
  /**
   * Selected glyph for the default-rendered button. Same shorthand and
   * normalization as {@link icon}. When omitted the selected item keeps `icon`
   * plus the existing accent chrome. Ignored for `custom` items and for
   * app-link / `openUrl` items, which never read this field.
   */
  iconActive?: IconSpec | string;
  /**
   * Optional visibility clause (架构方案 §5.9), evaluated against the workbench
   * `when` context. Omitted = always shown.
   *
   * The context holds platform keys (`workbench.online`, `workbench.activeArea`,
   * …) plus whatever installed modules publish through `context.setContextKey`,
   * which is how a clause can depend on another module's state
   * (`<otherModuleId>.<key> == value`). **Fail-closed**: a clause naming a key
   * nobody published is false, so the item stays hidden.
   */
  when?: string;
}

/**
 * Shared fields of a menu placement (架构方案 §5.3). The location key is the
 * parent map key (see {@link MENU_LOCATION}); `when` is an optional visibility
 * clause evaluated by the platform context.
 *
 * A placement is how a module says "put this at that entry point", so no command
 * field ever encodes one: a new entry point is a new location, not a new flag on
 * every command. Exactly one of {@link MenuCommandPlacement.command} or
 * {@link MenuSubmenuPlacement.submenu} must be set — a folder is a submenu
 * declaration, not a dummy command (a command with no behavior would still
 * enter search and contradict "menus and search show the same set").
 */
export interface MenuPlacementBase {
  when?: string;
  /**
   * Position within the location, ascending, default `0`; ties break on the
   * resolved label so the order never depends on module load order.
   *
   * A location gathers entries from several modules that know nothing of each
   * other, so without this the list would reshuffle whenever a module is
   * installed or activated. Same semantics as an activityBar item's `order`.
   */
  order?: number;
}

/**
 * A command surfaced into a menu location. The command must be declared by some
 * module — the menu entry carries no behavior of its own. A command that did
 * not opt in with {@link CommandContribution.internal} `false` does not render
 * here either: the user never sees it, placement included, or the same command
 * would be clickable in a menu yet unfindable by search.
 */
export interface MenuCommandPlacement extends MenuPlacementBase {
  /** Id of the command this menu item runs. */
  command: string;
}

/**
 * A submenu placeholder placed into a parent location. `submenu` names a
 * {@link SubmenuContribution.id} (which is itself a location other modules can
 * contribute into). Aligns with VS Code `contributes.menus[].submenu`.
 */
export interface MenuSubmenuPlacement extends MenuPlacementBase {
  /** Id of a {@link SubmenuContribution} to render as a nested menu. */
  submenu: string;
}

/**
 * One row in a menu location: either a command or a nested submenu. Structural
 * XOR (`command` vs `submenu`) is enforced by {@link validateManifest}; a
 * placement naming a missing command or submenu is left to the resolver so a
 * typo stays distinguishable from a `when` that did not hold.
 */
export type MenuContribution = MenuCommandPlacement | MenuSubmenuPlacement;

/** Whether this placement runs a command (as opposed to opening a submenu). */
export function isMenuCommandPlacement(
  item: MenuContribution,
): item is MenuCommandPlacement {
  return 'command' in item;
}

/** Whether this placement opens a nested submenu. */
export function isMenuSubmenuPlacement(
  item: MenuContribution,
): item is MenuSubmenuPlacement {
  return 'submenu' in item;
}

/**
 * A submenu placeholder other placements can point at (架构方案 §5.3).
 *
 * The id *is* a location: modules that know nothing of each other put commands
 * (or further submenus) under `contributes.menus[id]`. The declaring module
 * owns the label and icon shown in the parent. Aligns with VS Code
 * `contributes.submenus` — contents are more placements, never computed at
 * display time (no `registerMenuItem`; new modules appear via
 * `CONTRIBUTIONS_CHANGED_EVENT`, visibility via `when` / `setContextKey`).
 */
export interface SubmenuContribution {
  /**
   * Location key this submenu occupies. Convention `<moduleId>.<name>` so two
   * modules do not silently claim one folder; a collision is first-wins plus
   * an advisory, not a load failure. Other modules contribute *into* this id;
   * they do not redeclare it.
   */
  id: string;
  /**
   * Parent-row label. Supports `%key%`. Comes from this declaration, not from
   * a dummy command — a command with no behavior would still enter search.
   */
  label: string;
  /**
   * Icon for the parent row. Same shorthand as a command icon;
   * `listContributions` normalizes it to an {@link IconSpec}.
   */
  icon?: IconSpec | string;
}

/**
 * Menu locations the platform itself draws (架构方案 §5.3).
 *
 * A location key is just the map key under `contributes.menus`, and nothing
 * rejects an unknown one — a typo therefore surfaces as "my entry never
 * appears". These constants exist so the platform's own locations have one
 * spelling that both the declaring manifest and the rendering face can name.
 */
export const MENU_LOCATION = {
  /**
   * The header's Create button (见 03 header 槽位). Actions a user reaches for
   * to *make* something — a workspace, a conversation, a group. The button is
   * subject to the usual occupancy rule: no entry survives its `when`, no
   * button.
   */
  headerCreate: 'header/create',
} as const;

/**
 * Value type of a user-facing setting (架构方案 §5.12), which also picks the
 * control the settings UI renders: a switch, a text field, a number field, or a
 * dropdown. `enum` additionally requires {@link SettingContribution.enum} to
 * supply the allowed values.
 */
export type SettingType = 'boolean' | 'string' | 'number' | 'enum';

/**
 * The configuration layer a `setValue` write lands in (架构方案 §5.12 分层).
 * This is the `id` of a registered scope layer. Two layers are built in:
 * `SCOPE_DEFAULT` (priority 0, read-only schema defaults) and `SCOPE_USER`
 * (priority 100, writable + persisted). Additional ordered layers (e.g.
 * `workspace`, `space`) are contributed at runtime via
 * `ModuleContext.registerScopeLayer` and merged by descending priority.
 * Defaults to `SCOPE_USER` when omitted.
 */
export type SettingScope = string;

/** Built-in read-only layer that yields each setting's schema `default`. */
export const SCOPE_DEFAULT = 'default';

/** Built-in writable, persisted layer that user edits land in by default. */
export const SCOPE_USER = 'user';

/** A configurable setting declared by a module (架构方案 §5.3 / §5.12). */
export interface SettingContribution {
  /**
   * Dotted setting key, namespaced `<module>.<setting>` by convention. It is what
   * `getValue`/`setValue` address and what the settings UI lists.
   */
  key: string;
  /** Value type, which also picks the UI control; see {@link SettingType}. */
  type: SettingType;
  /**
   * Value yielded by the built-in read-only `default` layer, i.e. what reads
   * return until some writable layer overrides it.
   */
  default?: unknown;
  /**
   * Human-facing label in the settings UI, `%key%` NLS form supported. Falls back
   * to the raw `key` when absent.
   */
  title?: string;
  /** Allowed values when `type` is `enum`. */
  enum?: string[];
  /** Which layer user edits land in (forward-looking; see `SettingScope`). */
  scope?: SettingScope;
  /**
   * Runtimes this setting exists on, defaulting to the declaring module's own
   * `runtimes`. Same narrowing as {@link CommandContribution.runtimes} and there
   * for the same reason: a setting whose effect is implemented on one runtime
   * only (the desktop tray, 见 23) would otherwise be listed everywhere, and a
   * switch that visibly does nothing is worse than an absent one.
   */
  runtimes?: ModuleRuntime[];
  /**
   * The declaring side renders this setting in its own UI, so the settings
   * panel's generic list skips it (见 05 / 23).
   *
   * **Only presentation changes.** The key stays in `listSettings()`, so reads,
   * writes and type validation all still go through the configuration service —
   * one source of truth, one validator. Declaring it and then *not* rendering it
   * anywhere just hides the setting; that is the declarer's bug to notice, and a
   * cheaper failure than the alternative of a second storage mechanism.
   *
   * Two kinds of declarer use it: the shell, for keys that belong in a purpose-
   * built section (the notification group), and a module with a settings page of
   * its own, which reaches them through `ViewProps.settings`.
   */
  ownedUi?: boolean;
  /**
   * One line under the title when a preferences-window `keys` section renders
   * this setting. `%key%` NLS form supported.
   */
  description?: string;
  /**
   * Labels for {@link enum} values, index-aligned, `%key%` NLS form supported.
   * Without it a `keys` section shows the raw values.
   */
  enumTitles?: string[];
}

/**
 * A sidebar group of the preferences window (`contributes.setting.groups`).
 * Referenced from pages and actions by its global id `<moduleId>.<id>`.
 */
export interface ModuleSettingGroupContribution {
  /** Local id, unique within this module's groups. No `.`. */
  id: string;
  /** Group header. Supports `%key%`. */
  title: string;
  /** Sort key among all groups, ascending. Platform groups sit at 100-900. */
  order?: number;
}

/** One sidebar page (`contributes.setting.pages`). Its body is sections. */
export interface ModuleSettingPageContribution {
  /** Local id, unique within this module's pages. No `.`. */
  id: string;
  /** Global id of the group this page sits in (a `platform.*` group or `<moduleId>.<id>`). */
  group: string;
  /** Sidebar label and pane title. Supports `%key%`. */
  title: string;
  /** Sidebar glyph. Same shorthand as an activityBar item. */
  icon: IconSpec | string;
  /** Sort key within the group, ascending. */
  order?: number;
}

/**
 * One block of a page (`contributes.setting.sections`). A page may collect
 * sections from several modules; they stack by `order`. Exactly one of
 * {@link viewId} (the module draws it) or {@link keys} (the window renders
 * the settings by type) is set.
 */
export interface ModuleSettingSectionContribution {
  /** Local id, unique within this module's sections. No `.`. */
  id: string;
  /** Global id of the host page (a `platform.*` page or `<moduleId>.<id>`). */
  page: string;
  /**
   * Scroll-anchor label. When two or more sections of a page carry one, the
   * sidebar lists them under the page. A `keys` section also shows it as its
   * heading; a view section draws its own. Supports `%key%`.
   */
  title?: string;
  /** Sort key within the page, ascending. */
  order?: number;
  /** View this module declared in `contributes.views`, hosted in place. */
  viewId?: string;
  /** Keys this module declared in `contributes.settings`, rendered by type. */
  keys?: string[];
}

/**
 * A sidebar row that runs a command instead of opening a page
 * (`contributes.setting.actions`), e.g. Log Out.
 */
export interface ModuleSettingActionContribution {
  /** Local id, unique within this module's actions. No `.`. */
  id: string;
  /** Global id of the group whose tail this row sits in. */
  group: string;
  /** Row label. Supports `%key%`. */
  title: string;
  /** Row glyph. Same shorthand as an activityBar item. */
  icon?: IconSpec | string;
  /** Sort key among the group's actions, ascending. */
  order?: number;
  /** Command run on click, with no arguments. */
  command: string;
  /** Paint the row in the error color. */
  danger?: boolean;
  /** Close the preferences window once the command resolves. */
  closesWindow?: boolean;
  /**
   * Ask before running the command. All three strings support `%key%`.
   * Absent means the row runs immediately.
   */
  confirm?: ModuleSettingActionConfirm;
}

/**
 * Copy for the confirmation shown before a settings action runs.
 * Same chrome as the preferences confirm sheet.
 */
export interface ModuleSettingActionConfirm {
  /** Dialog title. Supports `%key%`. */
  title: string;
  /** Dialog body. Supports `%key%`. */
  body: string;
  /** Confirm-button label. Supports `%key%`. */
  confirm: string;
}

/**
 * The block above the sidebar search (`contributes.setting.header`). One
 * provider per deployment, pinned by the product key
 * `workbench.settings.headerProvider`.
 */
export interface ModuleSettingHeaderContribution {
  /** View this module declared in `contributes.views`. */
  viewId: string;
}

/**
 * Preferences-window contribution for one module (`contributes.setting`).
 * Distinct from {@link SettingContribution} (`contributes.settings`): that is
 * the key/value schema; this places the module in the window (见 21).
 *
 * The window only owns the frame: groups, pages, sections, ordering, anchors
 * and the declarative row renderer. Content comes from here.
 */
export interface ModuleSettingContribution {
  groups?: ModuleSettingGroupContribution[];
  /** Pages in `platform.apps` also need a non-`custom` activityBar item. */
  pages?: ModuleSettingPageContribution[];
  sections?: ModuleSettingSectionContribution[];
  actions?: ModuleSettingActionContribution[];
  header?: ModuleSettingHeaderContribution;
}

/** A group with its global id. */
export interface ResolvedSettingGroup {
  readonly id: string;
  readonly moduleId: string;
  readonly title: string;
  readonly order: number;
}

/** A page with its global id and a normalized icon. */
export interface ResolvedSettingPage {
  readonly id: string;
  readonly moduleId: string;
  readonly group: string;
  readonly title: string;
  /** Absent when a module-relative icon path could not be resolved. */
  readonly icon?: IconSpec;
  readonly order: number;
}

/** A section with its global id. */
export interface ResolvedSettingSection {
  readonly id: string;
  readonly moduleId: string;
  readonly page: string;
  readonly title?: string;
  readonly order: number;
  readonly viewId?: string;
  readonly keys?: readonly string[];
}

/** An action row with its global id and a normalized icon. */
export interface ResolvedSettingAction {
  readonly id: string;
  readonly moduleId: string;
  readonly group: string;
  readonly title: string;
  readonly icon?: IconSpec;
  readonly order: number;
  readonly command: string;
  readonly danger: boolean;
  readonly closesWindow: boolean;
  /** Present when the row must be confirmed before the command runs. */
  readonly confirm?: ModuleSettingActionConfirm;
}

/** {@link ModuleSettingContribution} of one module, ids made global. */
export interface ResolvedModuleSetting {
  readonly moduleId: string;
  readonly groups: readonly ResolvedSettingGroup[];
  readonly pages: readonly ResolvedSettingPage[];
  readonly sections: readonly ResolvedSettingSection[];
  readonly actions: readonly ResolvedSettingAction[];
  readonly header?: { readonly viewId: string };
}

/**
 * 产品/开发配置类型（见 05 产品配置）。区别于 {@link SettingType}：不含 `enum`，
 * 因为产品配置来自文件 / env / argv 的部署期取值，不走设置界面的下拉控件。
 *
 * `array` 是补记的既成事实而非新能力：解析层一直是 `unknown`，基座 `product.json`
 * 的 `bundle.allowedOrigins` 早就是数组，只是声明侧此前表达不了。
 */
export type ProductConfigType = 'boolean' | 'string' | 'number' | 'array';

/**
 * The schema of one product/deployment configuration key a module declares
 * (见 05 产品配置). Unlike {@link SettingContribution} (end-user settings,
 * editable in the settings UI and persisted to userData), product config defines
 * how *this build/run* behaves — resolved at boot from `product.json` /
 * `product.local.json` / env / argv, read-only at runtime, never shown in the
 * settings UI. The declared `default` is the lowest merge layer, overridden by
 * those sources. Declared as a map keyed by the dotted config key
 * ({@link ModuleContributions.productConfig}), mirroring VSCode's
 * `contributes.configuration.properties`.
 */
export interface ProductConfigContribution {
  /** Value type; see {@link ProductConfigType} (no `enum` — not a UI control). */
  type: ProductConfigType;
  /**
   * Lowest merge layer for this key: the value used when no `product.json` /
   * `product.local.json` / env / argv source overrides it at boot.
   */
  default?: unknown;
  /** What this key controls, for maintainers reading the manifest. */
  description?: string;
}

/** A keybinding declared by a module (架构方案 §5.3 / §5.9). */
export interface KeybindingContribution {
  /** Id of the command this chord dispatches. */
  command: string;
  /**
   * Key chord, `+`-joined and case-insensitive (e.g. `ctrl+shift+p`). Recognized
   * modifiers are `ctrl`/`control`, `cmd`/`meta`/`super`/`win`, `shift`,
   * `alt`/`option`, and `mod` — which resolves to `cmd` on macOS and `ctrl`
   * everywhere else, so one declaration covers both platforms instead of two
   * `when`-gated copies. The remaining part is the key itself, matched by
   * *physical* key rather than by produced character (见 03): `ctrl+,` fires on
   * whichever key sits where `,` is on a US layout, which is what keeps a chord
   * reachable on layouts where that character moved or is a dead key.
   */
  key: string;
  when?: string;
  /**
   * Arguments passed to the command, making one declared id serve a set of
   * targets (`workbench.open <launchable>`, 见 21). Without it a chord can only
   * ever fire the bare command, so "ctrl+1 opens chat" was not expressible.
   */
  args?: unknown[];
  /**
   * Where the chord is listened for; defaults to `window` (in-page).
   *
   * `global` is an OS-level grab that fires with the app in the background. It
   * exists only on runtimes in `GLOBAL_SHORTCUT_RUNTIMES` and is gated by
   * {@link GLOBAL_SHORTCUT_PERMISSION} — taking a chord away from every other
   * application is not something a module should be able to do unasked.
   */
  scope?: KeybindingScope;
  /**
   * Runtimes this binding exists on, defaulting to the declaring module's own
   * `runtimes`. Same narrowing as {@link CommandContribution.runtimes}: a chord
   * whose command is not registered on this runtime would be listed in the
   * shortcuts page and do nothing when pressed.
   */
  runtimes?: ModuleRuntime[];
  /**
   * Let the chord fire while a text field has focus. Default `false`, because a
   * binding that reaches the user mid-sentence takes the keystroke away from
   * what they were typing. Modifier-carrying chords are unaffected by this flag
   * (they are not text input); it exists for the bare and single-modifier keys.
   */
  allowInInput?: boolean;
}

/**
 * A generic, module-defined contribution point (见 17 / 架构方案 §5.5). A module
 * declares a point it *owns* so other modules can contribute entries to it,
 * while the platform kernel stays business-agnostic — it never interprets what
 * `id` means. Mirrors VSCode's philosophy of extensible contribution points, but
 * generalized so the *owner is another module* (a "mini-platform"), not core.
 */
export interface ContributionPointDeclaration {
  /** Namespaced point id the owning module defines, e.g. `ai.tool`. */
  id: string;
  description?: string;
  /**
   * When true, contributing to this point goes through the permission control
   * flow (contributor declares `contribution:<id>` + is granted); otherwise it
   * is open (default-open, 架构方案 §5.6).
   */
  requiresPermission?: boolean;
}

/**
 * A declarative entry a module contributes to some (possibly another module's)
 * contribution point (见 17). Declared in manifest so it is discoverable
 * *without activating the contributor* (mirrors `listContributions`); the actual
 * behavior (handler/live value) is bound at activation via
 * {@link ContributionRegistryFacade.register}. `data` is arbitrary
 * structured-cloneable metadata the owning point interprets (the kernel does
 * not).
 */
export interface ContributionEntry {
  /** Target point id this entry contributes to, e.g. `ai.tool`. */
  point: string;
  /** Entry id, unique within (moduleId, point). */
  id: string;
  /** Serializable declarative metadata; the owning point defines its shape. */
  data?: Record<string, unknown>;
  /** Optional: express the handler as a command id (dispatched via executeCommand). */
  command?: string;
  /** Optional `when` clause (reuses `evaluateWhen`). */
  when?: string;
}

/** A {@link ContributionEntry} resolved with its contributor + a stable key. */
export interface ResolvedContribution extends ContributionEntry {
  /** The contributing module id. */
  moduleId: string;
  /** Stable key `${moduleId}:${id}`, unique across contributors of a point. */
  key: string;
}

/**
 * A package that must resolve to ONE instance across the shell and every
 * dynamically-loaded module (见 12 共享运行时单例注册表) — because it owns a React
 * `createContext`, a runtime registry, or similar cross-boundary identity.
 *
 * A module declares its own contract package here (or in its `package.json` under
 * `hibro.runtimeSingleton`) and the build-time codegen aggregates it, so the
 * kernel hardcodes no module package name. Constraint: a singleton PROVIDER must
 * be bundled into the shell at build time, so only first-party/in-repo packages
 * qualify; a downloaded module may only CONSUME an already-registered singleton.
 */
export interface RuntimeSingletonDescriptor {
  /** Bare specifier consumers import, e.g. `@hibro/ui-client`. */
  specifier: string;
  /** The `hibro-assets://_runtime/<path>` tail this singleton is served under. */
  path: string;
  /** The `globalThis` key the renderer exposes this singleton's namespace on. */
  globalKey: string;
  /**
   * Repo-root-relative barrel entry whose exports the shim re-exports. Read at
   * build time (TS compiler API) to extract the named-export list. The package
   * must be resolvable from the shell (a workspace dependency) so the generated
   * `import * as` in the globals module resolves.
   */
  pkgEntry: string;
}

export interface ModuleContributions {
  commands?: CommandContribution[];
  views?: ViewContribution[];
  /**
   * Top-level renderer shells owned by this module. A shell is selected by the
   * internal `?shell=<id>` URL and renders one inline view outside the normal
   * workbench layout (for example the built-in browser chrome).
   */
  shells?: ShellContribution[];
  activityBar?: ActivityBarItemContribution[];
  menus?: Record<string, MenuContribution[]>;
  /**
   * Submenu placeholders this module owns (架构方案 §5.3). Each id is a
   * location other modules can contribute into; the owner supplies the parent
   * row's label and icon. Cross-module contribution into someone else's
   * submenu does **not** require redeclaring it here.
   */
  submenus?: SubmenuContribution[];
  settings?: SettingContribution[];
  /**
   * Preferences-window surface for this module (见 21). Object, not array:
   * one module is at most one Apps first-level row. Distinct from {@link settings},
   * which is the key/value schema consumed by `getConfig`.
   */
  setting?: ModuleSettingContribution;
  /**
   * Product/deployment configuration keys this module declares (见 05 产品配置),
   * as a map keyed by the dotted config key (mirrors VSCode's
   * `contributes.configuration.properties`). A separate line from end-user
   * `settings`: resolved at boot from files/env/argv over these declared defaults,
   * read-only at runtime, absent from the settings UI. Read via
   * `context.getProductConfig(key)`.
   */
  productConfig?: Record<string, ProductConfigContribution>;
  keybindings?: KeybindingContribution[];
  /**
   * Global-search result sections this module answers queries for
   * (见 `search-contribution.ts`). Declared here rather than as a generic
   * contribution point because the Omnibar is platform-rendered (见 17 两层).
   */
  search?: SearchContribution[];
  /**
   * Filters the global search offers on this module's own tab
   * (见 `search-contribution.ts`). Declared per module rather than per section
   * because a tab draws one filter bar; which sections honour a value is the
   * module's business, not the platform's.
   */
  searchFilters?: SearchFilterContribution[];
  /**
   * Areas this module wants **hidden by default** while it is the active module
   * (架构方案 §5.7). A blacklist: most modules omit it and every area shows;
   * only the few that don't want a given area list it here. This is a *soft*
   * default, not a hard gate — the user can still reveal a listed area via its
   * header toggle. Because area visibility is a single global state (no
   * per-module memory), switching the active module re-seeds the governed areas
   * to the incoming module's declaration.
   *
   * Only *toggleable* areas (those with a header toggle to reveal them again —
   * `panel` / `statusBar` / `auxiliaryBar`) are honoured; listing load-bearing
   * chrome (`editor` / `header` / `activityBar` / `sidebar`) is ignored.
   */
  hiddenAreas?: LayoutArea[];
  /**
   * External http(s) address spaces this module owns (见 20 外链归属路由), each
   * an origin plus a path prefix (`https://docs.example.com/doc/`).
   *
   * Clicking such an address anywhere in the app routes it to this module
   * instead of a browser: the platform activates it through `onUrl:<moduleId>`
   * and hands its `registerUrlHandler` an `app-link` {@link ModuleLink}, which
   * the handler presents (typically by navigating its own view) or declines.
   * This claims ownership of an address space rather than relaxing anything
   * about requests to it — hence no wildcards, and see
   * {@link isValidUrlOwnershipPrefix} for the full rule.
   */
  urlOwnership?: string[];
  /**
   * Platform push channels this module owns (见 26), e.g. `["im"]`.
   *
   * The configured provider module holds the long-lived connection (an
   * OS-resident agent on desktop, a wasm client on web). Declaring a channel
   * here routes every push on it to this module: the host activates it through
   * the derived `onPush:<moduleId>` and hands its `registerPushHandler` the
   * {@link PlatformPush}. Notifying, badging and navigating are then the
   * module's own calls — the platform never reads `p`.
   *
   * Two modules claiming one channel cancel each other out, and only in-repo /
   * vendored modules are honoured; see {@link matchPushOwner} and
   * {@link isValidPushChannel} for the full rule.
   */
  pushChannels?: string[];
  /**
   * Scripts this module injects into the MAIN world of built-in browser pages
   * (见 22), e.g. a wallet provider a dapp expects to find on `window`.
   *
   * Each entry is built by the dedicated page-world chain into a self-contained
   * IIFE, which the platform evaluates in every http(s) page at document start
   * and connects to this module's service over the declared channel. The
   * platform relays frames without reading them; the protocol is the module's.
   *
   * **Only in-repo / vendored source modules are honoured** — a dynamically
   * delivered module cannot claim one. The bundle runs inside an untrusted
   * document with no sandbox between it and the page, so the declaration is a
   * statement that this code was reviewed as first-party, not a permission a
   * module may grant itself; see {@link isValidPageWorldChannelId} and
   * {@link isValidPageWorldEntry} for the per-entry rules.
   */
  pageWorldScripts?: PageWorldScriptDeclaration[];
  /** Generic contribution points this module owns/defines (见 17). */
  contributionPoints?: ContributionPointDeclaration[];
  /** Entries this module contributes to points (owned by any module, 见 17). */
  contributions?: ContributionEntry[];
  /**
   * Curations this module applies to module-defined contribution points when a
   * consumer passes this module's id as the scope (见 17). Each entry names one
   * point and exactly one of `allow` or `deny`. Platform contribution points
   * (menus, activityBar, commands, views, keybindings, settings) are not read
   * from this field. Omitting a point means this scope does not curate it.
   */
  contributionCurations?: ContributionCuration[];
  /**
   * Shared runtime singleton this module provides (见 12); see
   * {@link RuntimeSingletonDescriptor}. Consumed only by the build-time codegen
   * (`scripts/runtime-singletons-codegen.ts`), which reads the raw JSON while
   * assembling the shim registry, import map and global injection — the runtime
   * platform never looks at it. `package.json`'s `hibro.runtimeSingleton` takes
   * precedence when both are declared.
   */
  runtimeSingleton?: RuntimeSingletonDescriptor;
}

/**
 * Declares a module's entrypoint files (架构方案 §5.3).
 *
 * IMPORTANT — the in-process fields (`service`/`ui`) mean different things
 * depending on **how the module was discovered**:
 * - For a statically discovered module (in-repo / packaged) they are **advisory
 *   only**. Its code is bundled by the main project and discovered via a *static*
 *   `import.meta.glob` over conventional paths (`SOURCE_MODULE_ENTRY`): a
 *   build-time constraint requires glob patterns to be string literals, so the
 *   manifest `entry` cannot drive bundling. Code is always loaded from the
 *   conventional locations regardless of what `entry` declares; a divergent
 *   value is reported as a non-fatal advisory (see `checkEntryConventions`).
 * - For a `dynamic` module (downloaded / sideloaded / dev-linked, §5.4) there is
 *   no compile-time glob, so `entry` becomes **authoritative**: the loader reads
 *   it to locate the entrypoint inside the fetched bundle.
 *
 * `external` is always authoritative — child processes are launched from
 * `entry.external` in both cases (见 10).
 */
export interface ModuleEntry {
  /**
   * Extension-host service entrypoint. Either one path used on every runtime, or
   * a per-runtime map when the module ships different code per runtime (见 19
   * 运行端分层):
   *
   * ```jsonc
   * "service": "./src/index.ts"
   * "service": { "desktop": "./src/index.ts", "web": "./src/index.web.ts" }
   * ```
   *
   * **The map form is declared the same way whether the module is source or
   * delivered**, even though source modules only load it from the conventional
   * path (the advisory/authoritative split above still applies). Keeping one
   * shape means a module that starts in-repo and later ships as a bundle does not
   * change how it says this, and every reader goes through
   * {@link resolveServiceEntry} rather than branching on which kind it is.
   *
   * A runtime absent from the map has no service there: the module contributes
   * its views/commands but activates to a no-op.
   */
  service?: string | ModuleServiceEntry;
  ui?: string;
  /**
   * Child-process entrypoint(s) this module owns (架构方案 §3.1/§5.8): the
   * executable/script to spawn (resolved relative to the module directory) and
   * its args. The platform's `ExternalProcessHost` spawns it and speaks JSON-RPC
   * over stdio.
   *
   * One object = a single unnamed entry (the historical form): its process key is
   * the bare `moduleId`. An **array** declares several named entries (each needs
   * an `id`), so one module can own more than one process — e.g. an IDE plus a
   * headless agent — each keyed `<moduleId>#<id>` and bound to a view via
   * {@link ViewContribution.externalEntry}.
   */
  external?: ExternalEntry | ExternalEntry[];
}

/**
 * One spawnable child-process entry of a module (架构方案 §3.1/§5.8), i.e. a
 * single `entry.external` declaration. Resolved to a launch spec by
 * `toExternalLaunchSpec` and hosted by `ExternalProcessHost` under its process
 * key ({@link externalProcessKey}).
 */
export interface ExternalEntry {
  /**
   * Name of this entry, required when the module declares several (the array
   * form) and optional for a lone one. It keys the process
   * (`<moduleId>#<id>`, {@link externalProcessKey}) and is what an
   * `external` view points at via {@link ViewContribution.externalEntry}. An
   * unnamed lone entry keeps the bare `moduleId` as its key, so single-process
   * modules behave exactly as before.
   */
  id?: string;
  /**
   * Executable/script to spawn, resolved relative to the module directory.
   * Optional only when every declared build target supplies its own `command`
   * via {@link ExternalLaunchOverride}. With no `platform` map this command
   * runs on every architecture. With a map, a target absent from it is not
   * launched even if this command is set; a present key may omit `command`
   * and inherit this one. `${platform}` (`process.platform`) and `${arch}`
   * (`process.arch`) tokens are substituted at launch.
   */
  command?: string;
  args?: string[];
  /**
   * Local-communication transport for the spawned process (架构方案 §3.2).
   * Defaults to {@link DEFAULT_EXTERNAL_TRANSPORT} (`ipc`). `ipc` uses the Node
   * IPC channel (Electron/Node children, e.g. Code-OSS); `socket` uses a local
   * named pipe / Unix domain socket (language-agnostic, cross-platform);
   * `stdio` uses NDJSON over stdin/stdout (legacy; unreliable under Electron on
   * Windows). The platform sets {@link ENV_EXTERNAL_TRANSPORT} so the child SDK
   * selects the matching transport.
   */
  transport?: ExternalTransportKind;
  /** Extra environment variables forwarded to the spawned process. */
  env?: Record<string, string>;
  /**
   * Whether the platform should auto-restart this process when it exits
   * unexpectedly (架构方案 §3.1/§4.3). Defaults to **false**: the process is
   * not kept alive, so closing it (e.g. quitting a launched editor) is final.
   * Opt in with `true` for long-lived service-style externals that should
   * recover from crashes (bounded retries; a deliberate `close`/dispose never
   * restarts regardless).
   */
  restart?: boolean;
  /**
   * Whether opening the `external` view bound to this entry auto-launches the
   * process on mount (架构方案 §3.1/§5.8). Defaults to **false**: opening the
   * tab shows an idle control panel and the process is spawned only when the
   * user clicks Launch (or the module is focused while already running). Opt in
   * with `true` for long-lived service-style externals that should come up as
   * soon as their tab opens. Independent of {@link restart} (crash recovery):
   * `autoLaunch` governs the *first* spawn, `restart` governs respawn after an
   * unexpected exit.
   */
  autoLaunch?: boolean;
  /**
   * Per-build-target launch overrides keyed by {@link distBundleTargetKey}
   * (`${process.platform}-${process.arch}`, e.g. `win32-x64`, `darwin-arm64`,
   * 架构方案 §3.1/§5.8) — the same key as `distBundle.platform`. The two maps
   * stay independent: a module may declare only this one when the binary ships
   * inside the base package.
   *
   * The effective launch spec merges the base fields with the current target's
   * override — the override wins for `command`/`args`/`transport`, and `env`
   * is shallow-merged (override keys win). A target absent from the map is not
   * launched and does not fall back to the base `command` or another target.
   * Omit `platform` entirely when one command covers every architecture.
   * `validateManifest` rejects OS-only keys (`win32`, `darwin`, `linux`).
   */
  platform?: Record<string, ExternalLaunchOverride>;
}

/**
 * Separator between a module id and an `entry.external` entry id inside a
 * process key ({@link externalProcessKey}). Chosen because module ids are
 * dot/kebab identifiers and never contain it, so a key parses unambiguously.
 */
export const EXTERNAL_ENTRY_SEPARATOR = '#';

/**
 * The id the platform hosts one external process under (架构方案 §3.1/§5.8):
 * the bare `moduleId` for a module's single unnamed entry — so single-process
 * modules keep their historical key, surface id (`external:<moduleId>`) and
 * `navigate:external:<moduleId>` permission byte-for-byte — and
 * `<moduleId>#<entryId>` for a named one. `ExternalProcessHost`'s process table,
 * the launch-spec map and the renderer's run-state set are all keyed by this.
 */
export function externalProcessKey(moduleId: string, entryId?: string): string {
  return entryId
    ? `${moduleId}${EXTERNAL_ENTRY_SEPARATOR}${entryId}`
    : moduleId;
}

/**
 * Split a {@link externalProcessKey} back into its parts. Permissions, log
 * attribution and the gated ext-host relay are module-scoped, so anything that
 * needs "which module owns this process" parses the key instead of assuming the
 * key *is* the module id.
 */
export function parseExternalProcessKey(key: string): {
  moduleId: string;
  entryId?: string;
} {
  const at = key.indexOf(EXTERNAL_ENTRY_SEPARATOR);
  if (at < 0) {
    return { moduleId: key };
  }
  return {
    moduleId: key.slice(0, at),
    entryId: key.slice(at + EXTERNAL_ENTRY_SEPARATOR.length),
  };
}

/**
 * Normalize `entry.external` (single object or array) to a list of entries —
 * the single reader every consumer goes through, so the two declaration forms
 * never fork logic. Empty when the module declares no child process.
 */
export function listExternalEntries(
  entry: ModuleEntry | undefined,
): readonly ExternalEntry[] {
  const external = entry?.external;
  if (!external) {
    return [];
  }
  return Array.isArray(external) ? external : [external];
}

/**
 * Resolve which {@link ExternalEntry} a consumer means (架构方案 §3.1/§5.8).
 * With an `entryId`, the entry declaring that id; without one, the module's lone
 * entry. Returns undefined when nothing matches or when the module declares
 * several and the caller named none — that ambiguity is rejected at validation
 * time, and staying undefined here keeps the runtime fail-closed rather than
 * silently driving the wrong process.
 */
export function findExternalEntry(
  entry: ModuleEntry | undefined,
  entryId?: string,
): ExternalEntry | undefined {
  const entries = listExternalEntries(entry);
  if (entryId !== undefined) {
    return entries.find((candidate) => candidate.id === entryId);
  }
  return entries.length === 1 ? entries[0] : undefined;
}

/**
 * Local-communication transport for an `external` module's process (架构方案
 * §3.2). `ipc` (default) = Node IPC channel, for Electron/Node children; `socket`
 * = local named pipe (Windows) / Unix domain socket, language-agnostic and the
 * choice for non-Node native modules; `stdio` = NDJSON over stdin/stdout, kept
 * for backward compat but unreliable under Electron on Windows (stdin 父→子).
 */
export type ExternalTransportKind = 'ipc' | 'socket' | 'stdio';

/**
 * Per-build-target launch override for an `external` module (架构方案 §3.1/§5.8).
 * Merged onto the base `entry.external` fields for the current
 * `${process.platform}-${process.arch}` target; every field is optional so a
 * target entry can override just the `command`.
 */
export interface ExternalLaunchOverride {
  command?: string;
  args?: string[];
  transport?: ExternalTransportKind;
  env?: Record<string, string>;
}

/** Default external transport when a manifest does not declare one (§3.2). */
export const DEFAULT_EXTERNAL_TRANSPORT: ExternalTransportKind = 'ipc';

/**
 * Env var the platform sets on a spawned external process to tell its SDK which
 * transport to use (架构方案 §3.2). Value is an {@link ExternalTransportKind}.
 */
export const ENV_EXTERNAL_TRANSPORT = 'HIBRO_EXTERNAL_TRANSPORT';

/** Env var carrying the socket path / named-pipe name for `socket` transport. */
export const ENV_EXTERNAL_SOCKET = 'HIBRO_EXTERNAL_SOCKET';

/** Env var carrying the one-time handshake token for `socket` transport (§3.2 安全). */
export const ENV_EXTERNAL_TOKEN = 'HIBRO_EXTERNAL_TOKEN';

/**
 * Env var carrying the main-process-resolved product-config overrides (见 05
 * 产品配置) as a JSON object of flat dotted keys. Set on the ext-host
 * `utilityProcess` fork so modules read the same product config the main process
 * resolved from files/env/argv; the host layers it over module-declared defaults.
 */
export const ENV_PRODUCT_CONFIG_RESOLVED = 'HIBRO_CONFIG_RESOLVED';

/**
 * Env var carrying the user's OS "Downloads" folder (见 18), set on the ext-host
 * `utilityProcess` fork to back {@link ModuleContext.downloadsPath}. Unset means
 * this host has no such folder.
 *
 * Forwarded rather than derived: the host has no Electron `app` to ask
 * `app.getPath('downloads')`, the folder is unrelated to `HIBRO_USER_DATA`, and
 * `~/Downloads` is a guess — on Windows it is a Known Folder the user may have
 * redirected to another drive.
 */
export const ENV_DOWNLOADS_DIR = 'HIBRO_DOWNLOADS_DIR';

/**
 * Env var that forces {@link ModuleManifest.devOnly} modules to be included even
 * in a packaged/formal build (见 12 模块发现过滤). Set to `'1'` to opt in;
 * anything else (or unset) leaves the default (dev includes dev-only modules,
 * packaged excludes them). Useful for smoke-testing a demo against a packaged
 * artifact without a source change.
 */
export const ENV_INCLUDE_DEV_MODULES = 'HIBRO_INCLUDE_DEV_MODULES';

/**
 * A host the platform runs on (见 18 / 19). `desktop` and `web` are the hosts
 * in this repository. `mobile` is a legal manifest token for the native kernel;
 * this process never reports it as `ModuleContext.runtime` and never loads
 * `entry.service.mobile`.
 */
export type ModuleRuntime = 'desktop' | 'web' | 'mobile';

/**
 * Every known {@link ModuleRuntime}, for validation and iteration. Kept next to
 * the type so adding a runtime is one edit.
 */
export const MODULE_RUNTIMES: readonly ModuleRuntime[] = [
  'desktop',
  'web',
  'mobile',
];

/**
 * Runtimes whose `entry.service` the desktop and web hosts load.
 * {@link deriveModuleFacets} scans this set, not {@link MODULE_RUNTIMES}:
 * `mobile` is a known token, but counting that key would mark a mobile-only
 * service as code running in this process.
 */
const LOADED_SERVICE_RUNTIMES: readonly ModuleRuntime[] = ['desktop', 'web'];

/**
 * The runtime a module runs on when it says nothing (见 18). Desktop predates
 * every other runtime, so "unqualified" has always meant it — which is also why
 * the conventional default entrypoint doubles as the desktop entrypoint (见 19).
 */
export const DEFAULT_MODULE_RUNTIME: ModuleRuntime = 'desktop';

/** A module's ext-host service entrypoint per runtime (见 19 运行端分层). */
export type ModuleServiceEntry = Partial<Record<ModuleRuntime, string>>;

/**
 * The service entrypoint to load on `runtime`, or `undefined` when the module
 * declares none there (见 19 运行端分层).
 *
 * The **single reader** of {@link ModuleEntry.service}, so the plain-string and
 * per-runtime-map forms never fork logic at a call site — and so a delivered
 * module and a source module are read by literally the same function. A bare
 * string means "the same entry on every runtime", which is what every existing
 * manifest says and why none of them had to change.
 *
 * Authoritative for delivered/prebuilt modules (the loader imports exactly this
 * path); advisory for source modules, whose code comes from the conventional
 * paths instead (see {@link sourceServiceEntryCandidates}).
 */
export function resolveServiceEntry(
  entry: ModuleEntry | undefined,
  runtime: ModuleRuntime = DEFAULT_MODULE_RUNTIME,
): string | undefined {
  const service = entry?.service;
  const declared = typeof service === 'string' ? service : service?.[runtime];
  const trimmed = declared?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * What a runtime can actually do (见 19 运行端分层).
 *
 * A {@link ModuleRuntime} on its own is just a name, so any code needing to know
 * whether something is possible there would have to compare against that name —
 * and `runtime === 'web'` states *which runtime is special* rather than *what the
 * code actually needs*. Declaring capabilities instead means a consumer asks its
 * real question (`childProcess`), and adding a runtime is answering that question
 * once here rather than auditing every comparison in the repo.
 *
 * **Only axes with a live consumer belong here.** Several more differences are
 * known (detached windows, a custom asset scheme) but nothing reads them yet,
 * and a capability nobody queries is a guess about the future written as if it
 * were a fact. Each gets its row when the feature that consumes it lands (见 18
 * 的能力矩阵).
 */
export interface RuntimeCapabilityProfile {
  /**
   * Can spawn an OS child process (架构方案 §3.1/§5.8, 见 10). `external` views
   * are that process's control panel, so they cannot exist without it.
   */
  readonly childProcess: boolean;
  /**
   * Has an OS file manager to reveal a file in (见 18). Gates
   * `capabilities.showItemInFolder` — and, more usefully, the menu entry that
   * would call it: without asking, a module leaves an item that does nothing on
   * a runtime that has no file manager, the same dead control
   * `window.hibroLog.openFolder`'s absence is designed to avoid.
   *
   * The alternative on such a runtime is not "reveal, worse" but a different
   * verb — `capabilities.saveStorageFile`, which hands the file to the user
   * instead of pointing at it. So this is a fork in the UI, which is why the
   * question has to be answerable rather than merely degraded.
   */
  readonly nativeFileManager: boolean;
  /**
   * Whether a file can be addressed by **absolute OS path** — both that such
   * paths exist and that this runtime's asset scheme will serve one (见 18 / 12
   * 的 `@local` 路由). Gates {@link ModuleContext.localFileUrl}.
   *
   * The consumer is IM's zero-copy send: a picture the user attaches is uploaded
   * from wherever it already lies, so the bubble that renders it has an OS path
   * and no module-relative one. A browser page has neither half — its `File`
   * never exposes a path, and its Service Worker route answers `@local` with 404
   * — so there the module must keep using `blob:`/object URLs instead.
   */
  readonly nativeFilePath: boolean;
}

/**
 * Capabilities per runtime — the single table adding a runtime must extend.
 * `Record` (not `Partial<Record>`) on purpose: a new runtime fails to compile
 * until its capabilities are stated, rather than silently inheriting whatever
 * `undefined` happens to mean at each call site.
 */
export const RUNTIME_PROFILES: Record<ModuleRuntime, RuntimeCapabilityProfile> =
  {
    desktop: {
      childProcess: true,
      nativeFileManager: true,
      nativeFilePath: true,
    },
    // A browser cannot spawn processes and has no file manager to reveal into;
    // neither has an equivalent to fall back to. Nor are there OS paths to
    // address: a page's `File` hides its origin, and `@local` 404s there.
    web: {
      childProcess: false,
      nativeFileManager: false,
      nativeFilePath: false,
    },
    // Same three facts as web. This row keeps the table total; this process
    // does not run as mobile and does not load `entry.service.mobile`.
    mobile: {
      childProcess: false,
      nativeFileManager: false,
      nativeFilePath: false,
    },
  };

/** Whether `runtime` supports `capability` (见 19). */
export function runtimeSupports(
  runtime: ModuleRuntime,
  capability: keyof RuntimeCapabilityProfile,
): boolean {
  return RUNTIME_PROFILES[runtime][capability];
}

/**
 * The runtimes a module runs on, defaulting **desktop-only** when it declares
 * none (见 18): a module that never said it works in a browser must not be
 * pulled into the web build, where a Node-only dependency (`im`'s koffi/DLL,
 * `ai`'s `node:fs`) would fail to compile. Matches VS Code, where an extension
 * without a `browser` entrypoint is simply unavailable on web.
 */
export function resolveModuleRuntimes(
  manifest: Pick<ModuleManifest, 'runtimes'>,
): readonly ModuleRuntime[] {
  return manifest.runtimes ?? [DEFAULT_MODULE_RUNTIME];
}

/**
 * Service execution environment (见 24). Ordered: `sandbox` is a strict subset
 * of `host`, so a module declares the narrowest one it runs in.
 */
export type ServiceTier = 'host' | 'sandbox';

export const SERVICE_TIERS: readonly ServiceTier[] = ['host', 'sandbox'];

/**
 * Tier assumed when a manifest declares none. The safe one: host authority has
 * to be asked for in the manifest, where store review can see it (见 24).
 */
export const DEFAULT_SERVICE_TIER: ServiceTier = 'sandbox';

/** The module's effective {@link ServiceTier}, defaulting {@link DEFAULT_SERVICE_TIER}. */
export function resolveServiceTier(
  manifest: Pick<ModuleManifest, 'serviceTier'>,
): ServiceTier {
  return manifest.serviceTier ?? DEFAULT_SERVICE_TIER;
}

/**
 * Whether a contributed command exists on `runtime` (架构方案 §5.9 / 见 19). A
 * module may ship a narrower service entry per runtime and register only some
 * of its commands there; the command narrows its module's own runtimes, so
 * omitting the field means "wherever my module runs".
 */
export function isCommandSupportedOnRuntime(
  command: Pick<CommandContribution, 'runtimes'>,
  manifest: Pick<ModuleManifest, 'runtimes'>,
  runtime: ModuleRuntime,
): boolean {
  return (command.runtimes ?? resolveModuleRuntimes(manifest)).includes(
    runtime,
  );
}

/**
 * Whether a contributed keybinding exists on `runtime` (架构方案 §5.9 / 见 19),
 * the chord counterpart of {@link isCommandSupportedOnRuntime}.
 *
 * Two independent reasons a declared chord can be absent here, and both must be
 * visible rather than silently dropped at press time: the binding (or its
 * module) does not run on this runtime, and `scope: 'global'` has no meaning
 * outside {@link GLOBAL_SHORTCUT_RUNTIMES}.
 */
export function isKeybindingSupportedOnRuntime(
  keybinding: Pick<KeybindingContribution, 'runtimes' | 'scope'>,
  manifest: Pick<ModuleManifest, 'runtimes'>,
  runtime: ModuleRuntime,
): boolean {
  if (
    keybinding.scope === 'global' &&
    !GLOBAL_SHORTCUT_RUNTIMES.includes(runtime)
  ) {
    return false;
  }
  return (keybinding.runtimes ?? resolveModuleRuntimes(manifest)).includes(
    runtime,
  );
}

/**
 * Whether a contributed setting exists on `runtime` (见 05 / 19), the settings
 * counterpart of {@link isCommandSupportedOnRuntime}.
 *
 * `manifest` is absent for the platform shell's own keys ({@link
 * PLATFORM_SETTINGS}), which belong to no module: the shell runs on every
 * runtime, so an undeclared shell setting is available everywhere and only its
 * own `runtimes` can narrow it.
 */
export function isSettingSupportedOnRuntime(
  setting: Pick<SettingContribution, 'runtimes'>,
  manifest: Pick<ModuleManifest, 'runtimes'> | undefined,
  runtime: ModuleRuntime,
): boolean {
  const declared =
    setting.runtimes ??
    (manifest ? resolveModuleRuntimes(manifest) : undefined);
  return declared ? declared.includes(runtime) : true;
}

/**
 * Whether a module should be discovered/loaded in the current build (见 12
 * 模块发现过滤). Pure predicate shared by every discovery site (ext-host module
 * + nls discovery, main-process `hibro-assets://` roots / external launch specs,
 * forge packaging staging) so dev-only modules are filtered consistently.
 *
 * `includeDevOnly` reflects the build/runtime: development (`import.meta.env.DEV`
 * in the bundled processes, `!app.isPackaged` in main, `HIBRO_INCLUDE_DEV_MODULES`
 * at package time) includes them; a packaged/formal build drops them. A module
 * without `devOnly` is always enabled.
 *
 * `runtime` is the second, orthogonal dimension (见 18): when supplied, the
 * module must declare that runtime in {@link ModuleManifest.runtimes} (absent =
 * desktop-only). Omitting it skips the check, which keeps call sites that
 * predate the runtime dimension behaving exactly as before.
 */
export function isModuleEnabled(
  manifest: Pick<ModuleManifest, 'devOnly' | 'runtimes'>,
  opts: { includeDevOnly: boolean; runtime?: ModuleRuntime },
): boolean {
  if (!opts.includeDevOnly && manifest.devOnly === true) {
    return false;
  }
  if (opts.runtime && !resolveModuleRuntimes(manifest).includes(opts.runtime)) {
    return false;
  }
  return true;
}

/**
 * Resolve `includeDevOnly` from an env bag for build-time contexts that lack an
 * `import.meta.env.DEV` / `app.isPackaged` signal (notably forge packaging, 见
 * 12): dev-only modules are excluded unless {@link ENV_INCLUDE_DEV_MODULES} is
 * explicitly `'1'`. Pure (env injected) so it stays unit-testable.
 */
export function includeDevOnlyFromEnv(
  env: Record<string, string | undefined>,
): boolean {
  return env[ENV_INCLUDE_DEV_MODULES] === '1';
}

/**
 * Conventional entrypoint paths a source module's code is loaded from (see
 * `ModuleEntry`). These are the literal paths the shell's `import.meta.glob`
 * matches (`modules/* /src/index.ts` and `modules/* /ui/index.tsx`).
 *
 * `service` is the **default** entry: the one used on any runtime the module has
 * not overridden (see {@link sourceServiceEntry}).
 */
export const SOURCE_MODULE_ENTRY = {
  service: './src/index.ts',
  ui: './ui/index.tsx',
} as const;

/**
 * A source module's per-runtime **override** entry (见 19 运行端分层):
 * `./src/index.web.ts` for `web`, and so on.
 *
 * The shape mirrors VS Code's `main` / `browser` pair — a default entry plus one
 * override per non-default environment — rather than requiring every module to
 * enumerate an entry for each runtime. Desktop needs no override file because it
 * *is* the default runtime ({@link resolveModuleRuntimes} defaults to
 * desktop-only), so `./src/index.ts` already means "the desktop entry" for every
 * existing module and nothing had to move.
 *
 * A discovery site resolves the override first and falls back to
 * {@link SOURCE_MODULE_ENTRY}`.service`. Only the browser build consumes this
 * today; it lives here because the convention is what **module authors** must
 * follow (见 integration/docs), and because the browser codegen previously kept a
 * hand-copied duplicate of the constant above.
 *
 * Note this covers the module's *entry file* only. Its runtime-specific
 * implementation belongs in `src/<runtime>/` — a directory rather than a suffix,
 * because that is what the lint guard against `node:*` imports can match (见 19).
 */
export function sourceServiceEntry(runtime: ModuleRuntime): string {
  return `./src/index.${runtime}.ts`;
}

/**
 * The paths a source module's service may actually be loaded from on `runtime`,
 * most specific first (见 19 运行端分层).
 *
 * This is the **conventional** counterpart to {@link resolveServiceEntry}: the
 * discovery site probes these in order, and {@link checkEntryConventions}
 * validates a declaration against the very same list — so "what the manifest
 * says" and "what actually loads" cannot drift apart.
 *
 * {@link DEFAULT_MODULE_RUNTIME} gets only the default entry, and that is a
 * fact about the build rather than a preference: the desktop shell discovers
 * source modules through a literal `import.meta.glob` fixed on `src/index.ts`,
 * so an `index.desktop.ts` would simply never be loaded. Declaring one would be
 * a lie, hence it is not among the candidates.
 */
export function sourceServiceEntryCandidates(
  runtime: ModuleRuntime,
): readonly string[] {
  return runtime === DEFAULT_MODULE_RUNTIME
    ? [SOURCE_MODULE_ENTRY.service]
    : [sourceServiceEntry(runtime), SOURCE_MODULE_ENTRY.service];
}

/**
 * Whether this runtime should load the module's service through the compile-time
 * bundle (glob / codegen) rather than importing a prebuilt file from disk
 * (见 19 清单优先).
 *
 * The manifest decides the **route**: a declared path that is one of the
 * conventional source entries is bundled (it is TypeScript; only a build can
 * load it). Any other declared path is a prebuilt artifact. An omitted
 * declaration falls back to the conventional entries.
 */
export function usesBundledServiceEntry(
  entry: ModuleEntry | undefined,
  runtime: ModuleRuntime = DEFAULT_MODULE_RUNTIME,
): boolean {
  const declared = resolveServiceEntry(entry, runtime);
  return (
    declared === undefined ||
    sourceServiceEntryCandidates(runtime).includes(declared)
  );
}

/**
 * Conventional subdir a module's lazy build artifacts live in (见 12 分层约定):
 * `dist/` holds the view `index.html`/JS, `entry.*` payloads and other heavy
 * files served over `hibro-assets://<id>/dist/...`. Since the platform no longer joins
 * an output dir onto the module root (the `hibro-assets://<id>` root IS the module dir),
 * this is a **build/staging convention** — manifest paths reference it explicitly
 * (e.g. `url: "dist/index.html"`), not an implicit resolution join.
 */
export const MODULE_OUTPUT_DIR = 'dist';

/**
 * Directory that holds per-module **isolated storage** roots — a module's own
 * data, distinct from its code/assets (架构方案 §5.13, 见 07).
 *
 * Deliberately runtime-neutral and defined here rather than in any one runtime,
 * because three places must agree on it or a module's files go missing: the
 * extension host's filesystem adapter resolving each module root
 * (`<userData>/<dir>/<id>` on desktop, an OPFS directory of the same name in the
 * browser), the desktop main
 * process serving `hibro-assets://<id>/@storage/...` from that same root, and forge
 * packaging. It used to be duplicated as a separate constant per runtime, which
 * made the agreement a convention rather than a fact.
 */
export const MODULE_STORAGE_DIRNAME = 'hibro-modules';

/**
 * Ceiling for the **whole-file** fs capability (`readFile`/`writeFile` and their
 * `*Bytes` counterparts, 见 07 / 18): 64 MiB.
 *
 * Exists because those calls materialize the entire file in memory (and, for a
 * sandboxed caller, clone it across a port). Rejecting loudly at a known size is
 * the difference between a diagnosable error and an out-of-memory host — the
 * same reason VS Code's file service raises `FileTooLarge` instead of reading on.
 *
 * A file over this size is not "unsupported", it is addressed differently:
 * `ModuleContext.storageUrl` hands out a ranged URL that the main process (or the
 * web Service Worker) streams straight off disk, so the bytes never enter a JS
 * heap. The limit is what makes that fork discoverable at runtime rather than
 * only in the docs.
 */
export const MODULE_FILE_BYTES_LIMIT = 64 * 1024 * 1024;

/**
 * The module-relative build-output subdir: the declared `manifest.output` or, by
 * convention, {@link MODULE_OUTPUT_DIR}. Advisory only (build/staging hint) — the
 * runtime no longer joins it onto the module root; manifest paths address `dist/`
 * explicitly (见 12).
 */
export function moduleOutputDir(
  manifest: Pick<ModuleManifest, 'output'>,
): string {
  return manifest.output ?? MODULE_OUTPUT_DIR;
}

/**
 * Declares one public API namespace a module exports for cross-module reverse
 * lookup (架构方案 §5.5). A module may export several, so
 * {@link ModuleManifest.exportsApi} is an array of these. Consumers resolve a
 * namespace via `platform.getApi(namespace)`; each declared namespace also
 * derives an `onApi:<namespace>` activation event so `getApi` can lazily
 * activate the owner (架构方案 §5.4). Capability differences are expressed by
 * feature detection at the call site, not by a platform version range.
 */
export interface ExportsApiDeclaration {
  /**
   * Namespace consumers pass to `getApi`, dotted for sub-capabilities (a module
   * may export several, e.g. `account` plus `account.http`). Each one also
   * derives an `onApi:<namespace>` activation event.
   */
  namespace: string;
}

export interface ModuleManifest {
  /**
   * Editor-only pointer to the manifest JSON Schema (`schemas/module.manifest.schema.json`,
   * generated from this very interface by `pnpm gen:manifest-schema`). Purely a
   * tooling affordance for completion and hover docs — the platform never reads
   * it, and declaring it is optional.
   */
  $schema?: string;
  /**
   * Globally unique module id, `kebab-case` by convention. It is the handle the
   * whole platform keys off: the `hibro-assets://<id>` resource root, the external
   * process key, the bundle cache dir, and the `moduleId` stamped onto every
   * contribution this module declares.
   *
   * Must be a single safe path segment: `validateManifest` rejects `.`, `..`,
   * `/`, `\`, `:`, and C0 controls (the same confinement used when the id is
   * joined under `hibro-modules/`, 见 07), ids starting with `_` (reserved
   * for platform paths, 见 20), and the exact id `host` (reserved for
   * host-directed iframe emit, 见 11).
   */
  id: string;
  /**
   * This module's own version (semver). Keys the bundle cache directory
   * (`<id>@<version>`). Unrelated to `engines`, which describes what the
   * module requires *of the platform*.
   */
  version: string;
  /**
   * Compatibility ranges (semver). `platform` gates against the app version
   * (`PLATFORM_VERSION`); optional `protocol` gates against the SDK <-> platform
   * protocol version (`PROTOCOL_VERSION`, 架构方案 §5.2). Both are hard gates:
   * `validateManifest` skips modules whose ranges are not satisfied.
   */
  engines?: { platform: string; protocol?: string };
  entry?: ModuleEntry;
  /**
   * Build-output subdir convention for a module's lazy artifacts (见 12 分层约定).
   * Advisory: the platform does NOT join this onto the module root — the
   * `hibro-assets://<id>` root is the module dir and manifest paths reference `dist/`
   * explicitly (e.g. `url: "dist/index.html"`, `command: "./dist/x.cjs"`). Kept
   * as a "convention over config" hint (defaults to {@link MODULE_OUTPUT_DIR},
   * `dist`) for tooling/scaffolds. Must be a relative path without `..`.
   */
  output?: string;
  /**
   * Downloadable bundle for this module's artifacts (架构方案 §5.4.1, 见 12):
   * where to fetch the self-contained archive of its output root and how to
   * verify it (sha256 + origin allowlist, §5.6 供应链). Only consumed on the
   * dynamic-download path; a statically compiled module's code comes from the
   * shell's build-time glob, so a declaration there is simply unused.
   */
  bundle?: BundleDeclaration;
  /**
   * Optional **lazy dist bundle** for two-phase delivery (见 12 两阶段下载): the
   * base package ships/downloads the module root (manifest + eager `assets/`/`nls/`)
   * so it registers immediately, while these heavy `dist/` artifacts are fetched
   * and unpacked into `<moduleRoot>/dist` on first activation (opening a view,
   * spawning an external process). Exactly one of remote `url` (allowlist +
   * sha256), local `path` (module-relative self-carried archive), or a
   * `platform` map carrying one such source per `${process.platform}-${process.arch}`
   * build target; source modules cannot declare it. See
   * {@link DistBundleDeclaration}.
   */
  distBundle?: DistBundleDeclaration;
  activationEvents?: string[];
  permissions?: string[];
  contributes?: ModuleContributions;
  /**
   * Public API namespaces this module exports for cross-module reverse lookup
   * (架构方案 §5.5). One entry per namespace: a module may export several (e.g.
   * `account` + `account.http`).
   */
  exportsApi?: ExportsApiDeclaration[];
  /**
   * Events this module owns (emits) that require the permission control flow to
   * receive (架构方案 §5.6/§5.15). Event subscription is default-open: a
   * consumer may receive any event unless its owner lists it here (a namespace
   * prefix `<ns>.*` form is not supported — list exact names). The gate applies
   * at the sandboxed EventBus boundary only (iframe/external must declare
   * `event:<name>`/`event:*`; low-risk, never prompts); in-process `source`
   * modules and renderer views are trusted and exempt (§5.6 信任模型).
   */
  protectedEvents?: string[];
  /**
   * Development-only module marker (见 12 模块发现过滤). Modules flagged
   * `devOnly: true` (e.g. the in-repo `sample*` demos) are discovered in
   * development but excluded from a packaged/formal build, so they stay in the
   * repo for future work without shipping. Every discovery site filters via
   * {@link isModuleEnabled}: dev builds include them, packaged builds drop them
   * unless {@link ENV_INCLUDE_DEV_MODULES} forces inclusion. Absent = a normal
   * module (always included).
   */
  devOnly?: boolean;
  /**
   * Runtimes this module can run on (见 18 Web 运行端). **Absent means
   * `["desktop"]`** — a module must opt in to the web build explicitly, because
   * the default has to be the safe one: a Node-only dependency (native addon,
   * `node:fs`, child process) pulled into a browser bundle fails to compile.
   * Same shape as VS Code, where an extension without a `browser` entrypoint is
   * unavailable on web.
   *
   * Filtered by {@link isModuleEnabled} at every discovery site, so this governs
   * both in-repo modules (the web build only generates loaders for modules
   * declaring `web`) and dynamically delivered ones (rejected at registration).
   * `mobile` is legal alongside `desktop` / `web`. Listing it does not make
   * this host load that entry; the default stays desktop-only.
   */
  runtimes?: ModuleRuntime[];
  /**
   * The narrowest service execution environment this module's `entry.service`
   * runs in (见 24). **Absent means `"sandbox"`**: the service runs in a QuickJS
   * engine with no ambient authority, reaching the platform only through the
   * gated capability surface. `"host"` runs it in-process with full host
   * authority (Node, shared heap, live objects) and must be declared
   * explicitly — first-party modules do, and a store package declaring it goes
   * through separate review. The client honours the declaration as written; it
   * does not narrow it by install source.
   *
   * Orthogonal to {@link runtimes}: `runtimes` says which platform, this says
   * how much of it. `"host"` means that runtime's own host environment, not
   * "Node on every runtime" — the web host has no Node. A manifest may list
   * `mobile` and `"host"` together; this client does not reject the pair. The
   * mobile kernel has no host environment and refuses that tier itself.
   * Irrelevant for modules without `entry.service`.
   */
  serviceTier?: ServiceTier;
  /**
   * Build targets this module supports, keyed by {@link distBundleTargetKey}
   * (`${process.platform}-${process.arch}`, e.g. `win32-x64`, `darwin-arm64`).
   * Orthogonal to {@link runtimes}, which only names the host (`desktop` /
   * `web` / `mobile`).
   *
   * **Absent means no architecture restriction.** A single entry script may
   * still select a binary inside `dist/` itself; that choice is not visible
   * here. When declared, this list is the authority for "which machines": a
   * catalog hides every other target, and launch / dist download refuse a
   * machine that is not listed ({@link supportsBuildTarget}). Keys in
   * `entry.external.platform` and `distBundle.platform` must be a subset —
   * those maps may be narrower or omitted. `validateManifest` rejects an empty
   * list and OS-only keys (`win32`, `darwin`, `linux`).
   */
  targets?: string[];
}

/**
 * Contribution annotated with the module that declared it. The renderer uses
 * these to render contribution points without activating module code
 * (架构方案 §5.3 / §5.7 / §5.9).
 */
export interface ResolvedCommand extends CommandContribution {
  moduleId: string;
  /** Normalized at `listContributions` from the manifest string/object shorthand. */
  icon?: IconSpec;
}

export interface ResolvedView extends ViewContribution {
  moduleId: string;
  /**
   * The view's resolved render kind (架构方案 §5.8), from
   * {@link resolveViewKind}. The renderer's `SurfaceView` dispatches on this.
   */
  kind: ViewRenderKind;
  /**
   * For `kind: 'external'` views only (架构方案 §3.1/§5.8): whether the control
   * panel auto-launches the process on mount. Resolved by `listContributions`
   * from the bound entry's `autoLaunch` (default `false` = lazy). The renderer's
   * `ExternalSurface` reads it to decide mount-time launch vs. idle.
   */
  autoLaunch?: boolean;
  /**
   * For `kind: 'external'` views only (架构方案 §3.1/§5.8): the id of the
   * `entry.external` entry this panel drives, resolved by `listContributions`
   * (the declared {@link ViewContribution.externalEntry}, else the module's lone
   * entry's own `id`). The renderer combines it with `moduleId` into the process
   * key ({@link externalProcessKey}) it sends over the external bridge.
   */
  externalEntry?: string;
}

/** A module-owned top-level renderer shell. */
export interface ShellContribution {
  /** Stable query id consumed by `?shell=<id>`. */
  id: string;
  /** Inline view that paints this shell's body. */
  viewId: string;
  /** Optional title painted in the shared window header. */
  title?: string;
}

/** A shell contribution annotated with its declaring module. */
export interface ResolvedShell extends ShellContribution {
  moduleId: string;
}

/** An activityBar item annotated with its declaring module (架构方案 §5.7). */
export interface ResolvedActivityBarItem extends ActivityBarItemContribution {
  moduleId: string;
  /** Normalized at `listContributions` from the manifest string/object shorthand. */
  icon?: IconSpec;
  /** Normalized like {@link icon}; absent when undeclared or dropped fail-closed. */
  iconActive?: IconSpec;
}

/** A menu contribution paired with the location key it was declared under. */
export type ResolvedMenu = MenuContribution & {
  location: string;
  moduleId: string;
};

/**
 * A submenu declaration annotated with its owning module. Icon is normalized
 * at `listContributions` the same way as a command icon; a colliding id
 * (another module already declared it) is dropped, first module wins.
 */
export interface ResolvedSubmenu {
  id: string;
  label: string;
  moduleId: string;
  icon?: IconSpec;
}

/**
 * A keybinding annotated with its declaring module, its stable identity and
 * where it came from (架构方案 §5.9).
 *
 * `id` and `source` are what let the three inputs — the platform's built-in
 * table, module manifests and the user's edits — merge into one list that the
 * shortcuts page can render and the resolver can order.
 */
export interface ResolvedKeybinding extends KeybindingContribution {
  moduleId: string;
  /** {@link keybindingId} of the *declared* binding, stable across a rebind. */
  id: string;
  source: KeybindingSource;
}

/** A setting schema entry annotated with its declaring module (§5.12). */
export interface ResolvedSetting extends SettingContribution {
  moduleId: string;
}

/** A product-config schema entry annotated with its key + declaring module (见 05). */
export interface ResolvedProductConfig extends ProductConfigContribution {
  key: string;
  moduleId: string;
}

/**
 * Flat, dotted-key map of resolved product-config values (见 05 产品配置). The
 * main process resolves the override layers (product.json < product.local.json <
 * env < argv) into this shape and forwards it to the extension host, which layers
 * it over module-declared defaults.
 */
export type ProductConfigValues = Record<string, unknown>;

/**
 * Cross-process read-only product/deployment configuration contract (见 05).
 * The extension host owns the effective boot-time values; renderers consume
 * them through the RPC proxy without mixing them into user settings.
 */
export const PRODUCT_CONFIG_CHANNEL = 'product-config';

export interface IProductConfigService {
  /** Read the effective boot-time value for `key` (or `undefined` if unknown). */
  getValue(key: string): Promise<unknown>;
}

/**
 * A module's default-hidden area declaration, annotated with the declaring
 * module so the renderer can look it up by the active module (架构方案 §5.7).
 * Derived from `ModuleContributions.hiddenAreas`.
 */
export interface ResolvedHiddenAreas {
  moduleId: string;
  areas: LayoutArea[];
}

/**
 * A module's renderer-side UI bundle served at runtime over `hibro-assets://` (见 12 /
 * 17). Declared via manifest `entry.ui`; used for downloaded source inline-dom
 * views and for hybrid modules whose iframe main app contributes React components
 * to owner views via `registerContribution` (no in-repo compile-time glob).
 */
export interface ResolvedModuleRuntimeUi {
  moduleId: string;
  /** Resolved absolute `hibro-assets://<moduleId>/<path>` URL (fail-closed on traversal). */
  url: string;
}

/**
 * The aggregate of all renderable contribution points across active modules,
 * computed from manifests only (no module code is evaluated). The renderer
 * fetches this once to drive layout, menus and keybindings.
 */
export interface ResolvedContributions {
  commands: ResolvedCommand[];
  views: ResolvedView[];
  shells: ResolvedShell[];
  activityBar: ResolvedActivityBarItem[];
  menus: ResolvedMenu[];
  /**
   * Declared submenu placeholders, first module wins on id collision.
   * Pure manifest data like `menus` — the Create face (and any later
   * location) can recurse without activating anyone.
   */
  submenus: ResolvedSubmenu[];
  keybindings: ResolvedKeybinding[];
  /**
   * Module-contributed global-search sections. Drives the Omnibar's app tab
   * strip, which is therefore computable without activating anything.
   */
  search: ResolvedSearchSection[];
  /**
   * Module-contributed global-search filters, keyed to the app tab they appear
   * on. Pure manifest data like `search`, so the filter bar draws without
   * activating anything; the candidate rows behind a `picker` are what needs a
   * live module, and only once the user opens it.
   */
  searchFilters: ResolvedSearchFilter[];
  /**
   * Preferences-window contributions (`contributes.setting`), one record per
   * module that declared the object. The renderer builds Apps / Notifications
   * from this without activating the module.
   */
  setting: ResolvedModuleSetting[];
  /**
   * Per-module default-hidden areas (blacklist). Only modules that declared
   * `contributes.hiddenAreas` appear here. The renderer applies the *active*
   * module's entry as a soft default (架构方案 §5.7).
   */
  hiddenAreas: ResolvedHiddenAreas[];
  /**
   * Per-module `entry.ui` bundles loadable at runtime (见 17). Feeds
   * `UiRegistry.activateForModule` when no compile-time UI loader exists.
   */
  runtimeUiEntries: ResolvedModuleRuntimeUi[];
}

/**
 * Workbench layout areas (架构方案 §5.7). Source-module views slot into an area
 * via their `ViewContribution.area`; the layout shell is plain DOM (inline-dom
 * surface, §5.8) and identical on web and desktop.
 */
export type LayoutArea =
  | 'header'
  | 'activityBar'
  | 'sidebar'
  | 'editor'
  | 'auxiliaryBar'
  | 'panel'
  | 'statusBar';

/**
 * Where a view declares it wants to live ({@link ViewContribution.area}). A
 * superset of {@link LayoutArea}: besides the docked layout areas, a view may
 * target one of three hosts that sit *outside* the layout (架构方案 §5.8) and are
 * therefore filtered out of `layoutViews`:
 * - `overlay`: mounted on demand by the OverlayHost via `openModal`/`openDrawer`;
 * - `gate`: full-screen replaces the whole workbench via the GateHost (App Gate).
 * - `shell`: top-level window chrome mounted by ModuleShellHost via `?shell=<id>`
 *   (见 08 / 22); never docked into workbench layout.
 *
 * Note this is a *declaration* vocabulary, not a runtime gate: `validateManifest`
 * deliberately does not reject unknown area strings (an unknown area simply
 * renders nowhere), so the enum lives here and in the generated JSON Schema for
 * authoring assistance.
 */
export type ViewArea = LayoutArea | 'overlay' | 'gate' | 'shell';

/**
 * Per-area *policy* config — only the business knobs that a build / role /
 * permission overlay edits (架构方案 §5.7). Structural and behavioural concerns
 * (placement, custom tab, drag/resize/drop capabilities) are dockview mechanism
 * and live with the renderer's `AREA_SPECS`, keyed by the same `LayoutArea`.
 */
export interface LayoutAreaConfig {
  area: LayoutArea;
  /**
   * Whether this area is available in the current build / version. A disabled
   * area is never created and cannot be toggled on; its toggle control is
   * hidden. Use this to ship areas progressively across versions. Defaults to
   * `true` when omitted.
   */
  enabled?: boolean;
  /**
   * Whether the area is shown by default. An enabled area with `visible: false`
   * is created but starts hidden (size / position preserved) and can be toggled
   * on at runtime. Defaults to `true` when omitted.
   */
  visible?: boolean;
  /**
   * Default fixed size in px: width for side columns (activityBar/sidebar/
   * auxiliaryBar), height for horizontal bars (header/statusBar). The `editor`
   * area is flexible and ignores `size`.
   */
  size?: number;
}

export interface LayoutSchema {
  areas: LayoutAreaConfig[];
}

export const DEFAULT_LAYOUT: LayoutSchema = {
  // Policy only: `enabled` / `visible` (both default to true) are stated
  // explicitly as a template for future per-area toggling — flip to false to
  // ship an area progressively or start it collapsed. Placement / tab / drag /
  // resize / drop are renderer mechanism (see `AREA_SPECS`) (架构方案 §5.7).
  areas: [
    { area: 'header', size: 36, enabled: true, visible: true },
    // Labeled magnetic stop (icon + title). 60 is the collapsed icon-only
    // stop; starting there hid titles on first paint / reset (见 03).
    // Must equal the renderer's `RAIL_LABEL_SIZE`, which this package cannot import.
    { area: 'activityBar', size: 162, enabled: true, visible: true },
    // Occupancy still gates this (见 03): no `area === 'sidebar'` view means the
    // column is never created. panel stays off as a template (架构 §5.7).
    { area: 'sidebar', size: 260, enabled: false, visible: true },
    { area: 'editor', enabled: true, visible: true },
    { area: 'panel', enabled: false, visible: false },
    { area: 'auxiliaryBar', size: 324, enabled: true, visible: true },
    { area: 'statusBar', size: 6, enabled: true, visible: true },
  ],
};

/**
 * Cross-process contract for the module service exposed by the extension host
 * (架构方案 §5.1 / §5.4). The renderer consumes it via an auto-generated RPC
 * proxy (`createServiceProxy`). The channel name is the only thing that crosses
 * the boundary; DI identifiers are created per-process.
 */
export const MODULE_CHANNEL = 'module';

/**
 * EventBus event broadcast when the set of active modules (and thus the
 * aggregate contributions) changes at runtime (见 12 / §5.4) — e.g. a
 * bundle was installed and hot-registered without a restart. A platform public
 * event (see {@link PLATFORM_PUBLIC_EVENTS}) so every surface, including
 * sandboxed iframes, can react without declaring it. Consumers re-fetch
 * {@link IModuleService.listContributions} on receipt rather than reading the
 * payload, which only names the affected modules.
 */
export const CONTRIBUTIONS_CHANGED_EVENT = 'platform.contributions.changed';

/** Payload of {@link CONTRIBUTIONS_CHANGED_EVENT}. */
export interface ContributionsChange {
  /** The module ids that were registered/updated. */
  moduleIds: string[];
}

/**
 * EventBus event when the workbench navigation context changes (Activity rail,
 * editor active view, settings, etc.). A platform public event so modules with
 * floating UI (app-center list, org-menu dropdown) can dismiss without
 * workbench↔module coupling.
 */
export const WORKBENCH_NAVIGATION_CHANGED_EVENT =
  'platform.workbench.navigationChanged';

/** Who initiated the navigation (fact only; modules decide policy). */
export type WorkbenchNavigationSource =
  | 'activityBar'
  | 'activityCustom'
  | 'editor'
  | 'search'
  | 'settings';

/** Payload of {@link WORKBENCH_NAVIGATION_CHANGED_EVENT}. */
export interface WorkbenchNavigationChangedPayload {
  source: WorkbenchNavigationSource;
  /** Target module when known (e.g. editor view owner, custom Activity owner). */
  moduleId?: string | null;
  /** Base contributed view id when known. */
  viewId?: string | null;
}

/**
 * EventBus event asking every workbench window to reset its editor area
 * (架构方案 §5.7 视图缓存): drop the keep-alive tree and MRU cache built for the
 * previous scope, then reopen `workbench.startupEditorView`.
 *
 * Raised by {@link PlatformApi.resetEditorScope}, never emitted by a module
 * directly — `canEmitEvent`'s namespace rule bars the `platform.` prefix, which
 * is what keeps "reset the workbench" a platform decision rather than something
 * any module can broadcast.
 *
 * No payload: the platform does not model *what* the scope is. Whoever owns a
 * scope (a workspace, an organisation, a signed-in account) knows when it
 * changed; the platform only knows editor state built against the old one is
 * stale.
 */
export const WORKBENCH_EDITOR_SCOPE_RESET_EVENT =
  'platform.workbench.editorScopeReset';

export interface IModuleService {
  /**
   * All renderable contribution points across active modules (no activation).
   * Replaces the earlier `listCommands` with the full contribution aggregate.
   */
  listContributions(): Promise<ResolvedContributions>;
  /**
   * Declared contributions to a module-defined contribution point `point`
   * (见 17), aggregated from manifests across active modules — no contributor is
   * activated. Lets a renderer-side owner enumerate contributions (the metadata
   * is serializable; the runtime handler/value stays in its owning process).
   */
  listModuleContributions(point: string): Promise<ResolvedContribution[]>;
  /**
   * Curations declared against a module-defined contribution point `point`
   * (见 17), from manifests only — no module is activated, and the list is not
   * applied. The consumer passes a scope id to `applyContributionCuration`.
   */
  listContributionCurations(
    point: string,
  ): Promise<ResolvedContributionCuration[]>;
  executeCommand(id: string, ...args: unknown[]): Promise<unknown>;
  /**
   * Activate the host-side service module that owns `viewId` (架构方案 §5.4).
   * Called by the renderer when a contributed view mounts, so a view opening
   * activates its service module in lockstep with its renderer UI module
   * (`onView` activation, unifying the two activation chains; 见 04).
   */
  activateView(viewId: string): Promise<void>;
  /**
   * If `moduleId` has a staged update waiting, move it into the live cache and
   * hot-register it. Resolves true when the running module was replaced.
   * Trusted renderer only: a sandboxed caller must not be able to swap another
   * module's bytes by naming its id.
   */
  applyStagedUpdate?(moduleId: string): Promise<boolean>;
  /**
   * Deactivate the host-side service module that owns `viewId` once the view is
   * no longer visible (架构方案 §5.4). Called by the renderer when a contributed
   * view unmounts; the module is torn down only when all of its contributed
   * views are hidden (reference-counted by the owner). A no-op for views with no
   * owning module or whose owner is not active.
   */
  deactivateView(viewId: string): Promise<void>;
  /**
   * Trusted renderer only: report workbench navigation so modules can react
   * (e.g. dismiss floating panels). Absent on gated webview/external connections.
   */
  reportNavigation?(payload: WorkbenchNavigationChangedPayload): Promise<void>;
  /**
   * Ask the preferences window to show `pageId` (a global settings page id).
   * Trusted renderer only: the window is shell UI, and a sandboxed caller has
   * no business steering it. Opening the window itself stays
   * `workbench.openSettings`.
   */
  selectSettingsPage?(pageId: string): Promise<void>;
  /**
   * Open an http(s) link wherever it belongs (见 20 外链归属路由): the module
   * that declared `contributes.urlOwnership` over the address, else the built-in
   * browser. Resolves whether a module took it.
   *
   * `callerModuleId` attributes the call — the browser fallback runs as a
   * capability, which is per-module for auditing. The renderer is trusted
   * in-process code (§5.6 信任模型), so it states its own identity the same way
   * it does when scoping `t()`.
   *
   * **Trusted renderer only, absent on gated connections** (like
   * `reportNavigation`): a sandboxed caller could otherwise make any owning
   * module present its view, which is precisely what `navigate:<target>` gates.
   */
  openLink?(url: string, callerModuleId: string): Promise<boolean>;
  /**
   * Ask which module claims an http(s) address (见 20 外链归属路由), without
   * presenting anything. Resolves the owning module id, or `null` when nobody
   * claimed it or two modules contest it.
   *
   * The addressing half of {@link openLink}, split out for callers that need the
   * owner *before* there is a click to route: a view that renders a card for a
   * link has to ask the owning module for the card's contents. Without this they
   * would each pattern-match domains themselves, which is the per-caller route
   * table `20` exists to prevent.
   *
   * **Trusted renderer only, absent on gated connections**, for the reason
   * `openLink` is: the ownership table is part of how a click gets routed, and a
   * sandboxed surface has no business enumerating or probing it.
   */
  resolveUrlOwner?(url: string, callerModuleId: string): Promise<string | null>;
  /**
   * Read one of the calling module's **own** declared settings (见 05 / 23), so a
   * module can carry its own settings page instead of contributing rows to the
   * platform panel.
   *
   * Together with {@link setModuleSetting} / {@link resetModuleSetting} this is
   * the whole of that capability. It exists because the configuration service
   * must stay the single source of truth: the alternative — a module keeping its
   * user settings in `context.storage` — splits them across two stores, loses
   * type validation and the layered scopes, and makes `ownedUi` a lie.
   *
   * `callerModuleId` attributes the call exactly as {@link openLink} does, and is
   * supplied by the host from the view's own record rather than by view code. The
   * scope check ("is this key declared by that module") is enforced on the
   * extension-host side, because a check in the renderer is not one.
   *
   * **Trusted renderer only, absent on gated connections**: a sandboxed surface
   * writing settings on behalf of its module has no user gesture behind it and no
   * UI the user recognizes as settings.
   */
  getModuleSetting?(callerModuleId: string, key: string): Promise<unknown>;
  /** Write one of the calling module's own declared settings (见 05 / 23). */
  setModuleSetting?(
    callerModuleId: string,
    key: string,
    value: unknown,
  ): Promise<void>;
  /** Drop the user override for one of the module's own settings (见 05 / 23). */
  resetModuleSetting?(callerModuleId: string, key: string): Promise<void>;
}

/**
 * Module service exposed to a sandboxed caller (`iframe` / `external`) over the
 * gated connection (见 6 / §5.15). Extends {@link IModuleService} with a
 * permission-gated reverse `emitEvent` so sandboxed content can publish events
 * in its own namespace; the renderer keeps using the plain `IModuleService`.
 */
export interface IGatedModuleService extends IModuleService {
  /**
   * Publish an event onto the platform EventBus on behalf of the caller's
   * module. Gated by {@link canEmitEvent}: rejects with an "Event denied" error
   * unless the event stays in the module's own namespace. Own-namespace events
   * need no declaration; declaration only gates which events it can receive.
   *
   * Names under {@link HOST_EVENT_PREFIX} (`host.close`, `host.back`, …) are
   * host-directed: they resolve without hitting the EventBus so the carrying
   * host can act (or no-op). See {@link isHostDirectedEvent}.
   */
  emitEvent(event: string, data?: unknown): Promise<void>;
  /**
   * Navigate a target surface to an opaque `location` on behalf of this module
   * (架构方案 §5.8 跳转, 见 8). Gated like {@link emitEvent}/`executeCommand`:
   * navigating one's own view/external is allowed; a cross-surface target
   * (`view:<id>` / `external:<id>` / `window:<id>`) requires the caller declared
   * and was granted `navigate:<target>`, else rejects with `NavigateDeniedError`.
   */
  navigate(target: string, location: unknown): Promise<void>;
  /**
   * Rename the workbench tab of the view instance this connection belongs to
   * (见 3 / 11). The caller passes only the title: `viewId` and `instanceKey`
   * are the connection's own, captured when the iframe was brokered, so a
   * page cannot address another tab or the activity-bar label. Rejects when
   * this connection is not a view (an external process has none) or `title`
   * is empty. The write is `TabHostApi.setTitle` on that one panel.
   */
  setTitle(title: string): Promise<void>;
  /**
   * Negotiate with the platform (架构方案 §5.2): returns the live platform/
   * protocol versions, the host {@link ModuleRuntime}, the capability list, and
   * whether this module's declared `engines` are compatible. Lets sandboxed
   * content feature-detect and branch on versions / runtime instead of assuming.
   */
  negotiate(): Promise<PlatformHandshake>;
  /**
   * The current effective theme's design tokens (见 13 / §5.10). Exposed on the
   * gated surface (rather than the full {@link IThemeService} channel) so a
   * sandboxed iframe / external module can paint with the platform palette
   * without widening its attack surface. Pairs with the `THEME_CHANGED_EVENT`
   * broadcast (a platform public event, delivered regardless of declaration) so
   * content can re-read tokens on theme switches.
   */
  getThemeTokens(): Promise<ThemeTokens>;
  /**
   * The effective theme's base (primitive) color palette (见 13 / §5.10).
   * Exposed on the same gated surface as {@link getThemeTokens} so a sandboxed
   * iframe / external module can reference the raw Figma primitives directly
   * (e.g. apply them as `--app-base-*`), not just the flattened semantic tokens.
   */
  getThemeBaseColors(): Promise<ThemeBaseColors>;
  /**
   * The effective theme's kind (见 13 / §5.10): `light` or `dark`. A direct
   * pull for the scalar that otherwise only rides on `THEME_CHANGED_EVENT`, so a
   * sandboxed surface can branch its own styling (e.g. pick a logo/illustration)
   * on first paint without waiting for a theme switch. `system` is already
   * resolved to the concrete kind here; use {@link getThemePreference} for the
   * raw user preference.
   */
  getThemeType(): Promise<ThemeType>;
  /**
   * The user's theme preference (见 13 / §5.10): `light` / `dark` / `system`
   * (unresolved). Companion to {@link getThemeType} (the resolved kind) for
   * surfaces that want to reflect the raw setting.
   */
  getThemePreference(): Promise<ThemePreference>;
  /**
   * The active message catalog for this caller (见 14 / §5.11): the
   * platform shell strings plus this module's own namespaced keys
   * (`<moduleId>:<key>`) — not other modules' messages. Pairs with the public
   * `LOCALE_CHANGED_EVENT` broadcast so content can re-read on locale switches.
   */
  getMessages(): Promise<MessageCatalog>;
  /**
   * The current effective locale (见 14 / §5.11), e.g. `en-US`. A direct pull
   * for the scalar that otherwise only rides on `LOCALE_CHANGED_EVENT`, so a
   * sandboxed surface can format ICU / pick number-date conventions correctly on
   * first paint. `system` is already resolved to a concrete locale here; use
   * {@link getLocalePreference} for the raw user preference.
   */
  getLocale(): Promise<Locale>;
  /**
   * The user's locale preference (见 14 / §5.11): `system` (unresolved) or an
   * explicit locale. Companion to {@link getLocale} (the resolved locale).
   */
  getLocalePreference(): Promise<LocalePreference>;
  /**
   * Forward a structured observability record to the platform log sink
   * (见 15 / §5.16). Exposed on the gated surface (rather than a full
   * service channel) so a sandboxed iframe / external module can emit
   * diagnostics into the unified log without widening its attack surface. The
   * host stamps `source` (iframe/external) and `scope` (the caller's module id)
   * — the caller cannot spoof another module's records.
   */
  log(record: LogInput): Promise<void>;
  /**
   * Read a value from this caller's isolated storage namespace (架构方案 §5.13,
   * 见 11). Async (unlike in-process {@link ModuleStorage.get}, which reads a
   * preloaded cache) because the sandbox has no local cache — the value crosses
   * the process boundary. Scoped to the caller's module id by the host, so one
   * module can never read another's data.
   */
  storageGet(key: string): Promise<unknown>;
  /** Snapshot of all entries in this caller's storage namespace (见 11). */
  storageGetAll(): Promise<Record<string, unknown>>;
  /** All keys in this caller's storage namespace (见 11). */
  storageKeys(): Promise<string[]>;
  /** Persist a value into this caller's storage namespace (见 11). */
  storageUpdate(key: string, value: unknown): Promise<void>;
  /** Remove a value from this caller's storage namespace (见 11). */
  storageDelete(key: string): Promise<void>;
  /**
   * Current shared connectivity (见 11 / §5.14). The renderer is the
   * source of truth (`navigator.onLine`); pairs with the public
   * {@link NETWORK_STATUS_EVENT} broadcast so content can observe changes.
   */
  getNetworkStatus(): Promise<boolean>;
  /**
   * Enqueue an opaque offline operation for this caller (见 11 / §5.14).
   * Persisted in the caller's storage namespace; drained by the in-process owner
   * module's `context.offline.process` when connectivity returns.
   */
  offlineEnqueue(op: unknown): Promise<void>;
  /** Snapshot of this caller's pending offline operations (见 11). */
  offlinePending(): Promise<unknown[]>;
  /**
   * Show a notification toast on behalf of this caller (架构方案 §5.6/§5.15,
   * 见 11). Permission-gated by the host (`notification`): rejects with a
   * capability-denied error unless the module declared and was granted it.
   */
  capabilityNotify(message: string): Promise<void>;
  /** Set the app badge count, 0 clears it; gated by `notification` (见 11). */
  capabilitySetBadge(count: number): Promise<void>;
  /**
   * Read a file as raw bytes; gated by `fs` (见 11 / 18). Whole-file, capped at
   * {@link MODULE_FILE_BYTES_LIMIT}.
   */
  capabilityReadFile(path: string): Promise<Uint8Array>;
  /** Write raw bytes; gated by `fs` (见 11 / 18). Same cap as the read side. */
  capabilityWriteFile(path: string, content: Uint8Array): Promise<void>;
  /**
   * Delete a file under the caller's module storage root; gated by `fs` (见 11).
   * Missing path is a no-op (idempotent).
   */
  capabilityDeleteFile(path: string): Promise<void>;
  /** Open a URL in the OS default browser; gated by `open-external` (见 11). */
  capabilityOpenExternal(url: string): Promise<void>;
  /** Open an HTTP(S) URL in the built-in browser; gated by `open-browser` (见 22). */
  capabilityOpenBrowser(options: BrowserOpenOptions): Promise<void>;
}

/**
 * Channel name for the platform -> external-process protocol (架构方案 §5.8).
 * The reverse direction (external -> platform) reuses {@link MODULE_CHANNEL} and
 * {@link IGatedModuleService} (gated executeCommand/emitEvent/negotiate).
 */
export const EXTERNAL_CHANNEL = 'external';

/**
 * Platform -> external-process control surface (见 8 / §5.8): the
 * platform asks the external app (e.g. an IDE) to navigate to an opaque location,
 * focus its window, or shut down. Implemented by the external process; the
 * platform consumes it via an RPC proxy over the stdio Channel. `navigate`'s
 * `location` is an opaque, module-defined payload the platform routes but never
 * interprets (见 8).
 */
export interface IExternalProcess {
  /**
   * Navigate the external app to an opaque, structured-cloneable `location`
   * (架构方案 §5.8 跳转, 见 8). The external module interprets it; the platform
   * does not inspect the shape.
   */
  navigate(location: unknown): Promise<void>;
  /** Bring the external app's window to the foreground. */
  activate(): Promise<void>;
  /** Ask the external app to shut down gracefully. */
  close(): Promise<void>;
}

/**
 * Event delivered to a single navigated view (架构方案 §5.8 跳转, 见 8).
 * Carries the opaque `location` for the target `viewId`. Unlike platform public
 * events, this is NOT broadcast: the platform emits it only on the target view's
 * gated connection (sandboxed iframe/external) or its renderer window, so a
 * navigation location is delivered point-to-point and never observed by other
 * surfaces. The iframe SDK surfaces it as `onNavigate`.
 */
export const VIEW_NAVIGATE_EVENT = 'platform.view.navigate';

/** Payload of {@link VIEW_NAVIGATE_EVENT}. */
export interface ViewNavigate {
  /** The view the location targets. */
  viewId: string;
  /**
   * Which instance of that view is addressed (架构方案 §5.7 视图多实例);
   * `undefined` = the singleton instance. Delivery is per instance, so two
   * documents of the same view do not receive each other's locations.
   */
  instanceKey?: string;
  /** Opaque, module-defined location payload (the platform does not interpret it). */
  location: unknown;
}

/** Target-id prefix for navigating a contributed view (`view:<viewId>`, 见 8). */
export const NAVIGATE_VIEW_PREFIX = 'view:';
/** Target-id prefix for navigating an external process (`external:<moduleId>`, 见 8). */
export const NAVIGATE_EXTERNAL_PREFIX = 'external:';
/** Target-id prefix for navigating (focusing) a window (`window:<windowId>`, 见 8). */
export const NAVIGATE_WINDOW_PREFIX = 'window:';

/**
 * Separator between a `viewId` and its `instanceKey` in a composite surface
 * instance id (架构方案 §5.7 视图多实例).
 *
 * View ids are dotted (`ai.panel`) and never contain `::`, so a composite id
 * round-trips losslessly. Defined here rather than in the renderer because the
 * id became a **cross-process key**: the editor addresses tabs by it, `navigate`
 * targets carry it, and the extension host records each sandboxed connection
 * under it. It used to live only in the renderer's editor tabs, which is exactly
 * the shape that grows a second, hand-copied definition.
 */
export const SURFACE_INSTANCE_SEPARATOR = '::';

/**
 * Compose a surface instance id from a `viewId` and optional `instanceKey`
 * (§5.7). A singleton yields the **bare `viewId`**, so an id built without a key
 * is byte-identical to the plain view id every existing call site already passes
 * around — which is what keeps instance addressing backward compatible.
 */
export function surfaceInstanceId(
  viewId: string,
  instanceKey?: string,
): string {
  return instanceKey === undefined
    ? viewId
    : `${viewId}${SURFACE_INSTANCE_SEPARATOR}${instanceKey}`;
}

/** Split a surface instance id back into `viewId` + optional `instanceKey` (§5.7). */
export function parseSurfaceInstanceId(id: string): {
  viewId: string;
  instanceKey?: string;
} {
  const index = id.indexOf(SURFACE_INSTANCE_SEPARATOR);
  if (index === -1) {
    return { viewId: id };
  }
  return {
    viewId: id.slice(0, index),
    instanceKey: id.slice(index + SURFACE_INSTANCE_SEPARATOR.length),
  };
}

/**
 * Build a `navigate` target addressing a contributed view (见 8 / §5.7):
 * `view:<viewId>` for the singleton, `view:<viewId>::<instanceKey>` for one
 * instance among several.
 *
 * The instance rides **inside the target** rather than as a third `navigate`
 * parameter: `navigate(target, location)` is a published SDK contract, and `08`
 * already defines `target` as a namespaced surface id — so extending the id
 * grammar keeps one concept where a new parameter would add a second.
 */
export function viewNavigateTarget(
  viewId: string,
  instanceKey?: string,
): string {
  return `${NAVIGATE_VIEW_PREFIX}${surfaceInstanceId(viewId, instanceKey)}`;
}

/**
 * Parse a `view:` navigate target into the view + instance it addresses (见 8).
 * `undefined` when `target` is not a view target (or names no view), so callers
 * can use it as the routing branch itself.
 */
export function parseViewNavigateTarget(
  target: string,
): { viewId: string; instanceKey?: string } | undefined {
  if (!target.startsWith(NAVIGATE_VIEW_PREFIX)) {
    return undefined;
  }
  const id = target.slice(NAVIGATE_VIEW_PREFIX.length);
  return id.length > 0 ? parseSurfaceInstanceId(id) : undefined;
}

/**
 * Strip any instance from a navigate target, yielding the surface it belongs to
 * (见 8 / §5.6).
 *
 * This is the form the **permission gate** uses: a grant is per *surface*, not
 * per document. Without it, `navigate:view:docs.editor::doc-42` would ask the
 * user to authorize every single document, and the owning module would need a
 * grant to reach its own instances.
 */
export function baseNavigateTarget(target: string): string {
  const view = parseViewNavigateTarget(target);
  return view === undefined ? target : viewNavigateTarget(view.viewId);
}

/**
 * Lifecycle + last-reverse-call snapshot of an external process (架构方案 §5.8).
 * Returned by the platform's `ExternalProcessHost` and surfaced to the renderer
 * control panel; `lastReverse` records the most recent command the external app
 * reverse-called (its id + result), the evidence the external -> platform path
 * works.
 */
export interface ExternalStatus {
  running: boolean;
  pid?: number;
  lastReverse?: { command: string; result: unknown };
}

/**
 * SDK debug-mode wire defaults (架构方案 §5.2 调试期接入；见 11). The debug
 * transport lets a module run *outside* the client (an author's own node
 * debugger or a standalone iframe dev page) and reverse-connect to the running
 * platform over WebSocket to obtain a working SDK. These constants are the
 * single source of truth shared by both the SDK (`@hibro/sdk`, which bundles
 * `@hibro/shared`) and the platform main process, so that with no extra config
 * each side only has to *turn the mode on* to interoperate.
 *
 * This is a dedicated interconnection debug mode, distinct from a generic
 * `--inspect` breakpoint debugger: it does not change how a module runs, it just
 * opens the "connect back to the platform" link. It is OFF by default and binds
 * to loopback only; the token is a basic anti-stray-connection guard for the
 * local dev link (生产环境默认关闭或强制鉴权).
 */
export const SDK_DEBUG_DEFAULT_HOST = '127.0.0.1';
/** Default loopback port the platform debug WebSocket server listens on. */
export const SDK_DEBUG_DEFAULT_PORT = 9230;
/** Default shared token so "just enable both sides" works with zero config. */
export const SDK_DEBUG_DEFAULT_TOKEN = 'hibro-debug';

/** Connection kind a debug client declares, routing it to the matching gate. */
export type SdkDebugKind = 'external' | 'iframe';
/** Debug connection kind for an `external` (node/IDE) module's process. */
export const SDK_DEBUG_KIND_EXTERNAL: SdkDebugKind = 'external';
/** Debug connection kind for a standalone `iframe` dev page. */
export const SDK_DEBUG_KIND_IFRAME: SdkDebugKind = 'iframe';

/** Query-parameter names the debug handshake carries on the WebSocket URL. */
export const SDK_DEBUG_PARAM_KIND = 'kind';
export const SDK_DEBUG_PARAM_ID = 'id';
export const SDK_DEBUG_PARAM_TOKEN = 'token';

/** Inputs for {@link buildDebugUrl}. */
export interface DebugUrlOptions {
  /** Connection kind (external/iframe); routes to the matching platform gate. */
  kind: SdkDebugKind;
  /**
   * Target module/view id: the module id for `external`, the view id for
   * `iframe` (scopes the gated service the platform brokers).
   */
  id: string;
  host?: string;
  port?: number;
  token?: string;
}

/**
 * Build the `ws://host:port?kind=..&id=..&token=..` URL a debug client connects
 * to. Defaults come from the shared `SDK_DEBUG_DEFAULT_*` constants so that, when
 * unmodified, the SDK and platform agree on the address without any config.
 */
export function buildDebugUrl(options: DebugUrlOptions): string {
  const host = options.host ?? SDK_DEBUG_DEFAULT_HOST;
  const port = options.port ?? SDK_DEBUG_DEFAULT_PORT;
  const token = options.token ?? SDK_DEBUG_DEFAULT_TOKEN;
  const query = [
    `${SDK_DEBUG_PARAM_KIND}=${encodeURIComponent(options.kind)}`,
    `${SDK_DEBUG_PARAM_ID}=${encodeURIComponent(options.id)}`,
    `${SDK_DEBUG_PARAM_TOKEN}=${encodeURIComponent(token)}`,
  ].join('&');
  return `ws://${host}:${String(port)}/?${query}`;
}

/** The kind of surface a {@link SurfaceDescriptor} represents (架构方案 §5.8). */
export type SurfaceKind = 'window' | 'external';

/**
 * Unified descriptor for a platform surface (架构方案 §5.8): either a renderer
 * window or an external process, enumerated/activated through one facade
 * (`ISurfaceManager`). `id` is namespaced (`window:<winId>` / `external:<moduleId>`)
 * so a single `activate(id)` routes to the owning manager.
 */
export interface SurfaceDescriptor {
  id: string;
  kind: SurfaceKind;
  /** Owning module id, for `external` surfaces. */
  moduleId?: string;
  /**
   * Which `entry.external` entry this surface's process came from, for a module
   * that declares several (架构方案 §3.1/§5.8). Absent for a single-entry
   * module. With `moduleId` it rebuilds the process key ({@link
   * externalProcessKey}) that `id` carries, so consumers need no string surgery.
   */
  externalEntry?: string;
  running: boolean;
}

/** Effective (merged) configuration values keyed by setting key (§5.12). */
export type ConfigValues = Record<string, unknown>;

/**
 * Cross-process contract for the configuration service (架构方案 §5.12). Lives
 * single-instance in the extension host (like `IModuleService`) and is consumed
 * by the renderer via an auto-generated RPC proxy. Value changes broadcast on
 * the EventBus as `CONFIG_CHANGED_EVENT` so every window stays consistent.
 */
export const CONFIG_CHANNEL = 'config';

/** EventBus event emitted when a setting value changes (§4.3 cross-window). */
export const CONFIG_CHANGED_EVENT = 'config.changed';

/** Payload of `CONFIG_CHANGED_EVENT`: the changed key and its new effective value. */
export interface ConfigChange {
  key: string;
  value: unknown;
}

/**
 * A pluggable, ordered configuration source (架构方案 §5.12 分层). The
 * `ConfigurationService` merges all registered layers by descending `priority`
 * (higher wins). The built-in `default` (priority 0, read-only) and `user`
 * (priority 100, writable) layers are always present; modules contribute extra
 * layers (e.g. `workspace`) via `ModuleContext.registerScopeLayer`.
 *
 * Lives in `shared` (common layer): a layer is just a bag of functions, so it
 * carries no platform dependency and can be authored by modules through the SDK.
 */
export interface ScopeLayer {
  /** Layer identity; also the value a `SettingContribution.scope` targets. */
  readonly id: string;
  /** Merge order — higher priority shadows lower (`default`=0, `user`=100). */
  readonly priority: number;
  /**
   * Read this layer's value for `key`. Returning an object signals "this layer
   * holds the key" (distinguishing a stored `undefined` from "no value"); return
   * `undefined` when the layer does not contribute a value for `key`.
   */
  read(key: string): { value: unknown } | undefined;
  /** Persist a value into this layer (writable layers only). */
  write?(key: string, value: unknown): void | Promise<void>;
  /** Drop this layer's value for `key`, if any (writable layers only). */
  reset?(key: string): void | Promise<void>;
  /**
   * Subscribe to changes driven from outside the service (e.g. the layer's
   * backing store changed). `key` is omitted when many keys may have changed.
   */
  onDidChange?(listener: (key?: string) => void): () => void;
}

/** One layer's contribution for a key, used by `IConfigurationService.inspect`. */
export interface ConfigLayerValue {
  id: string;
  priority: number;
  /** Whether this layer holds a value for the key (vs. contributing nothing). */
  has: boolean;
  /** The value this layer holds for the key, or `undefined` if it holds none. */
  value: unknown;
}

/** Per-key view across all layers: the effective value and each layer's value. */
export interface ConfigInspect {
  key: string;
  effective: unknown;
  /** The id of the highest-priority layer that holds the key (the winner). */
  effectiveLayerId?: string;
  /** Each registered layer (descending priority) and whether it holds a value. */
  layers: ConfigLayerValue[];
}

export interface IConfigurationService {
  /** The aggregated settings schema across active modules (no activation). */
  getSchema(): Promise<ResolvedSetting[]>;
  /** All effective values (highest-priority layer merged over the rest). */
  getAll(): Promise<ConfigValues>;
  /** The single effective value for `key` (or `undefined` if unknown). */
  getValue(key: string): Promise<unknown>;
  /** Write into the setting's target scope layer (validated) and persist. */
  setValue(key: string, value: unknown): Promise<void>;
  /** Drop the override for `key` in its target scope layer. */
  resetValue(key: string): Promise<void>;
  /** The effective value plus each layer's contribution for `key` (§5.12). */
  inspect(key: string): Promise<ConfigInspect>;
}

/**
 * A capability a module may request (架构方案 §5.6), e.g. `notification`, `fs`,
 * or a cross-boundary command such as `command:im.send`. Declared in the
 * manifest's `permissions`; only declared permissions can ever be granted
 * (least privilege). Only gates sandboxed callers — in-process `source` modules
 * are trusted and exempt (§5.6 信任模型).
 */
export type Permission = string;

/** Prefix for the permission that gates a sandbox reverse-calling a command (§5.6). */
export const COMMAND_PERMISSION_PREFIX = 'command:';

/**
 * The permission a (sandboxed) module must hold to reverse-call the contributed
 * command `commandId` across the boundary (架构方案 §5.6). A single source of
 * truth so the gate, the broker and module authors agree on the name (e.g.
 * `command:im.send`). Own-module commands are exempt; only cross-module command
 * execution is gated.
 */
export function commandPermission(commandId: string): string {
  return `${COMMAND_PERMISSION_PREFIX}${commandId}`;
}

/** Prefix for the permission that gates navigating another surface (见 8 / §5.6). */
export const NAVIGATE_PERMISSION_PREFIX = 'navigate:';

/**
 * The permission a (sandboxed) module must hold to navigate a surface it does
 * not own (见 8 / §5.6). Mirrors {@link commandPermission}: a single
 * source of truth so the gate and module authors agree on the name (e.g.
 * `navigate:external:ide`). Navigating one's own view/external is exempt; only
 * cross-surface navigation is gated.
 */
export function navigatePermission(target: string): string {
  return `${NAVIGATE_PERMISSION_PREFIX}${target}`;
}

/**
 * Prefix for the permission that gates contributing to a module-defined
 * contribution point (见 17 / §5.6). Only enforced when the point's owner
 * declares it `requiresPermission`.
 */
export const CONTRIBUTION_PERMISSION_PREFIX = 'contribution:';

/**
 * The permission a (sandboxed) module must hold to contribute to the
 * (permission-gated) contribution point `point` (见 17). Mirrors
 * {@link commandPermission}: a single source of truth so the gate, the broker
 * and module authors agree on the name (e.g. `contribution:ai.tool`).
 */
export function contributionPermission(point: string): string {
  return `${CONTRIBUTION_PERMISSION_PREFIX}${point}`;
}

/** Prefix for the permission that gates subscribing to an event (§5.6/§5.15). */
export const EVENT_PERMISSION_PREFIX = 'event:';

/** Wildcard permission granting receipt of every event (subscription only). */
export const EVENT_PERMISSION_WILDCARD = 'event:*';

/**
 * The permission a module must hold to subscribe to the event `name`
 * (架构方案 §5.6/§5.15). Mirrors {@link commandPermission}: a single source of
 * truth so the EventBus filter and the source `context.onEvent` gate agree on
 * the name (e.g. `event:im.message`). Note: emitting an own-namespace event
 * needs no declaration (see {@link canEmitEvent}); declaration gates receipt.
 */
export function eventPermission(name: string): string {
  return `${EVENT_PERMISSION_PREFIX}${name}`;
}

/**
 * Whether a module that declared `declared` may receive event `eventName`
 * (架构方案 §5.6/§5.15). True if it declared the exact `event:<name>` or the
 * `event:*` wildcard. Pure so the sandbox EventBus filter and the source
 * `context.onEvent` gate share one policy; declaration-driven, no prompt.
 */
export function canSubscribeEvent(
  eventName: string,
  declared: string[],
): boolean {
  return (
    declared.includes(EVENT_PERMISSION_WILDCARD) ||
    declared.includes(eventPermission(eventName))
  );
}

/**
 * Whether `callerModuleId` may receive (subscribe to) `eventName` (架构方案
 * §5.6/§5.15). Single source of truth shared by the ext-host `context.onEvent`
 * gate and the renderer `ViewProps.onEvent` gate so the two never diverge. The
 * policy, in order:
 *   1. own-namespace events are always allowed (you own them — mirrors
 *      {@link canEmitEvent}; no declaration needed);
 *   2. a non-protected event is default-open;
 *   3. a protected event requires an `event:<name>`/`event:*` declaration
 *      ({@link canSubscribeEvent}).
 * `isProtected` is precomputed by the caller (which resolves the owner's
 * `protectedEvents`), keeping this pure and usable on both sides.
 */
export function canReceiveEvent(
  callerModuleId: string,
  eventName: string,
  declared: string[],
  isProtected: boolean,
): boolean {
  if (canEmitEvent(callerModuleId, eventName)) {
    return true;
  }
  if (!isProtected) {
    return true;
  }
  return canSubscribeEvent(eventName, declared);
}

/**
 * Platform "public" events delivered to every connection regardless of the
 * declaration gate (见 13 / §5.15). These carry non-sensitive,
 * universally-useful platform state that every surface should observe without
 * each module having to declare `event:<name>` — the theme change being the
 * original case, so a sandboxed iframe repaints on a theme switch with zero
 * ceremony. Deliberately tiny and platform-owned (the `canEmitEvent` namespace
 * rule still prevents modules from spoofing these names).
 */
export const PLATFORM_PUBLIC_EVENTS: readonly string[] = [
  THEME_CHANGED_EVENT,
  LOCALE_CHANGED_EVENT,
  CONTRIBUTIONS_CHANGED_EVENT,
  WORKBENCH_NAVIGATION_CHANGED_EVENT,
  APP_VISIBILITY_CHANGED_EVENT,
  CONNECTIVITY_CHANGED_EVENT,
];

/** Whether `eventName` is a platform public event (bypasses the subscribe ACL). */
export function isPublicEvent(eventName: string): boolean {
  return PLATFORM_PUBLIC_EVENTS.includes(eventName);
}

/**
 * Whether `moduleId` may emit event `eventName` (架构方案 §5.6/§5.15). The only
 * rule is namespace confinement: the event must lie in the module's own
 * namespace (`name === id` or `id.` prefix). Emitting an own-namespace event
 * needs no declaration (publishing your own events is safe — only opted-in
 * subscribers receive them, and the namespace rule prevents spoofing
 * `permission.resolved`, `platform.*` or another module's events even with
 * `event:*`). This mirrors 见 11's "own commands are exempt" and the ungated
 * source `context.emitEvent`; declaration (`canSubscribeEvent`) gates receipt.
 */
export function canEmitEvent(moduleId: string, eventName: string): boolean {
  return isInModuleNamespace(moduleId, eventName);
}

/**
 * Reserved first segment for iframe → current-host signals (见 11). Sibling of
 * `platform.*` (host → subscribers, on the EventBus): `host.*` is the opposite
 * direction and never lands on the bus. Suffixes are host-defined; the SDK
 * does not enumerate them.
 */
export const HOST_EVENT_PREFIX = 'host.';

/**
 * Module id reserved so a real module cannot own {@link HOST_EVENT_PREFIX}
 * (`validateManifest` rejects it, same closed-rule idea as `_` path ids).
 */
export const HOST_MODULE_ID = 'host';

/**
 * Whether `eventName` is a host-directed signal (`host.<non-empty>`). Checked
 * *before* {@link canEmitEvent} on the gated emit path so these names never
 * become EventBus events. Bare `host` / `host.` and unprefixed names are not.
 */
export function isHostDirectedEvent(eventName: string): boolean {
  return (
    eventName.startsWith(HOST_EVENT_PREFIX) &&
    eventName.length > HOST_EVENT_PREFIX.length
  );
}

/**
 * Whether the dotted `id` lies inside `moduleId`'s namespace — it either *is*
 * the module id or is prefixed with `<moduleId>.`. The single rule behind every
 * "a module may only claim its own names" gate: event emission
 * ({@link canEmitEvent}) and contributed command ids ({@link validateManifest}).
 */
export function isInModuleNamespace(moduleId: string, id: string): boolean {
  return id === moduleId || id.startsWith(`${moduleId}.`);
}

/**
 * Why `moduleId` may not publish `when` context key `key`, or `undefined` when it
 * may (见 03 §5.9). Sibling of {@link canEmitEvent}: same namespace-confinement
 * rule, plus the platform's own key namespace is off limits.
 *
 * Returns the reason rather than a boolean so the caller can throw something
 * actionable — the two failure modes need different fixes (rename the key vs.
 * stop shadowing a platform key), and both are otherwise invisible: a rejected
 * key just means the `when` clause is never true.
 */
export function contextKeyProblem(
  moduleId: string,
  key: string,
): string | undefined {
  if (key.startsWith(PLATFORM_CONTEXT_KEY_PREFIX)) {
    return `"${PLATFORM_CONTEXT_KEY_PREFIX}" is reserved for platform keys`;
  }
  if (!isInModuleNamespace(moduleId, key)) {
    return `must be "${moduleId}" or start with "${moduleId}."`;
  }
  return undefined;
}

/**
 * Whether receiving event `eventName` is permission-gated (架构方案 §5.6/§5.15).
 * Event subscription is default-open: an event is gated only when its owning
 * module lists it in `manifest.protectedEvents` (passed here as `protectedEvents`
 * once the owner is resolved by namespace). Pure so the sandbox EventBus filter
 * and the source `context.onEvent` gate share one policy; a gated event then
 * requires `canSubscribeEvent` (declaration), a non-gated one is freely received.
 */
export function isProtectedEvent(
  eventName: string,
  protectedEvents: string[],
): boolean {
  return protectedEvents.includes(eventName);
}

/**
 * Permission gating the notification capability — `capabilities.notify` and
 * `capabilities.setBadge` (架构方案 §5.6/§5.15). The notification service groups
 * toast/system notification/badge under one capability, so the badge reuses
 * this permission rather than declaring its own.
 */
export const NOTIFICATION_PERMISSION = 'notification';

/** Permission gating the filesystem capability (`capabilities.readFile`/`writeFile`/`deleteFile`, §5.6). */
export const FS_PERMISSION = 'fs';

/**
 * Permission gating opening a URL in the OS default browser
 * (`capabilities.openExternal`, §5.6).
 */
export const OPEN_EXTERNAL_PERMISSION = 'open-external';

/**
 * Permission gating opening an untrusted URL inside the desktop built-in
 * browser (`capabilities.openBrowser`, 见 22).
 */
export const OPEN_BROWSER_PERMISSION = 'open-browser';

/**
 * Permission gating an OS-level chord grab (`contributes.keybindings` with
 * `scope: 'global'`, 架构方案 §5.9).
 *
 * Gated where an in-window chord is not: a global grab takes the combination
 * away from every other application on the machine and fires while this app is
 * in the background, so it is a claim on the user's whole desktop rather than
 * on this window.
 *
 * Checked against the **manifest declaration** at `listContributions`, not
 * through the broker's prompt flow: keybindings are resolved without activating
 * anything, so there is no call site to await a grant in. An undeclared global
 * binding is dropped with an advisory rather than degraded to a window chord —
 * silently narrowing the scope would look like "my shortcut works, but only
 * sometimes".
 */
export const GLOBAL_SHORTCUT_PERMISSION = 'global-shortcut';

/**
 * Platform-maintained set of system capability permissions that are enforced
 * (架构方案 §5.6). The capability facade (notify/setBadge/readFile/
 * writeFile/deleteFile/openExternal/openBrowser) is default-open: a capability
 * runs directly unless its backing permission is listed here, in which case it
 * goes through the permission control flow (declare + grant). Add a permission
 * here to gate it platform-wide.
 *
 * 当前保护装模块（见 12）与应用内打开不可信网页（见 22）。前者等价于把代码搬进
 * 本进程；后者进入平台持有的 WebContentsView，二者都不能沿用 default-open。
 */
export const PROTECTED_CAPABILITIES: ReadonlySet<string> = new Set<string>([
  MODULE_INSTALL_PERMISSION,
  OPEN_BROWSER_PERMISSION,
]);

/**
 * Thrown by a `capabilities.*` facade method when the module is not authorized
 * for the backing permission (架构方案 §5.6). Unlike `getApi` (which degrades to
 * `undefined`), platform capabilities have no meaningful degraded value, so they
 * fail closed with this error; modules may try/catch to degrade gracefully.
 */
export class CapabilityDeniedError extends Error {
  constructor(readonly permission: Permission) {
    super(`Capability denied: missing permission "${permission}"`);
    this.name = 'CapabilityDeniedError';
  }
}

/**
 * Thrown when a module calls a cross-module command it is not authorized for
 * (架构方案 §5.6) — both from a sandboxed reverse-call and from an in-process
 * service's {@link PlatformApi.executeCommand}, which share one gate. The
 * calling module's own commands are always allowed; a cross-module command
 * requires the caller declared and was granted `command:<id>`. Crosses the
 * boundary as a plain error message.
 */
export class CommandDeniedError extends Error {
  constructor(readonly commandId: string) {
    super(
      `Command denied: missing permission "${commandPermission(commandId)}" to execute "${commandId}"`,
    );
    this.name = 'CommandDeniedError';
  }
}

/**
 * Thrown when a (sandboxed) module reverse-emits an event outside its own
 * namespace (架构方案 §5.6/§5.15 anti-spoofing). Emitting own-namespace events
 * needs no declaration; this fires only when the event escapes the module's
 * namespace. Crosses the boundary as a plain error message.
 */
export class EventDeniedError extends Error {
  constructor(readonly eventName: string) {
    super(
      `Event denied: cannot emit "${eventName}" outside your module namespace`,
    );
    this.name = 'EventDeniedError';
  }
}

/**
 * Thrown when a (sandboxed) module navigates a surface it does not own without
 * the grant (见 8 / §5.6). Navigating one's own view/external is
 * always allowed; a cross-surface target requires the caller declared and was
 * granted `navigate:<target>`. Crosses the boundary as a plain error message.
 */
export class NavigateDeniedError extends Error {
  constructor(readonly target: string) {
    super(
      `Navigate denied: missing permission "${navigatePermission(target)}" to navigate "${target}"`,
    );
    this.name = 'NavigateDeniedError';
  }
}

/**
 * Thrown when a `sandbox`-tier service (见 24) reaches past what that tier is
 * given: another module's config / product config / overlay view, or a
 * protected event it did not declare. The host tier has no equivalent — its
 * code is trusted — so this names the tier rule that refused, not a missing
 * grant. Crosses the boundary as a plain error message.
 */
export class SandboxDeniedError extends Error {
  constructor(
    readonly member: string,
    readonly reason: string,
  ) {
    super(`Sandbox denied: ${member} ${reason}`);
    this.name = 'SandboxDeniedError';
  }
}

/**
 * The rejection a `sandbox`-tier service gets for a `context` member this host
 * does not implement in that tier and whose signature returns a result (见 24
 * §4.1). Explicit so "not implemented" can never pass for a business answer —
 * a `confirm` resolving `false` would read as the user cancelling.
 */
export class CapabilityUnsupportedError extends Error {
  constructor(readonly member: string) {
    super(`${member} is not available to sandbox-tier services on this host`);
    this.name = 'CapabilityUnsupportedError';
  }
}

/**
 * A desktop capture source (screen or window) offered by
 * {@link PlatformCapabilities.screenSources}. Structured-cloneable across the
 * ext-host → main-process boundary: `thumbnail` is already a PNG dataURL string.
 */
export interface ScreenSourceInfo {
  /** desktopCapturer source id, e.g. `screen:0:0` / `window:12345:0`. */
  id: string;
  /** Human-readable name (window title or "Entire Screen"). */
  name: string;
  /** Whether this source is a whole screen (vs a window). */
  isScreen: boolean;
  /** `display_id` for screens; empty for windows. */
  displayId: string;
  /** PNG dataURL preview (empty when the source has no thumbnail). */
  thumbnail: string;
}

/**
 * Platform system capabilities exposed to modules via `context.capabilities`
 * (架构方案 §5.6/§5.14/§5.15). Each method auto-authorizes against its backing
 * permission before executing (prompting on first use), and rejects with
 * `CapabilityDeniedError` when denied/undeclared. The actual side effects are
 * provided by the host (a `CapabilityBridge`), keeping this contract free of
 * any platform dependency.
 */
export interface PlatformCapabilities {
  /**
   * Show a bare in-app toast (gated by `notification`).
   *
   * For transient feedback about something the user just did ("已保存"), which is
   * why it takes a string and has no click destination. Telling the user about
   * something that arrived on its own is {@link notifyUser} — that one reaches
   * them when they are not looking at the app.
   */
  notify(message: string): Promise<void>;
  /**
   * Tell the user about something (gated by `notification`, 见 23).
   *
   * **The platform picks the surface**, not the caller: an OS notification when
   * the user is elsewhere, an in-app toast when they are looking at the app. A
   * module cannot see cross-window visibility, so it is not in a position to
   * choose — it says how urgent the event is (`urgency`) and the platform
   * applies the visibility gate and the user's Do Not Disturb setting.
   *
   * Clicking either surface brings the app to the foreground and, when `target`
   * is set, routes the carried `location` there via the navigate router
   * (见 8 / §5.8) — one click path for both.
   */
  notifyUser(notification: AppNotification): Promise<void>;
  /**
   * @deprecated Renamed to {@link notifyUser}, whose semantics are wider: the
   * platform now degrades to an in-app toast instead of dropping the
   * notification when the app is visible. Kept as an alias so existing callers
   * and downloaded modules keep working; it forwards verbatim.
   */
  notifyOS(notification: AppNotification): Promise<void>;
  /**
   * Withdraw a notification this module raised, by its `tag` (gated by
   * `notification`, 见 23).
   *
   * What makes "通知留在操作中心直到用户主动清除**或在应用内已读**" (需求 §4.4.3)
   * implementable: only the owning module knows when its content has been read.
   * An unknown tag is a no-op — the user may have dismissed it already.
   */
  dismissNotification(tag: string): Promise<void>;
  /** Set the app badge count, 0 clears it (gated by `notification`). */
  setBadge(count: number): Promise<void>;
  /**
   * Read a file as raw bytes (gated by `fs`).
   *
   * Bytes, not text: a UTF-8 string cannot represent an arbitrary byte stream
   * (invalid sequences decode to U+FFFD, irreversibly), so a text-shaped API
   * could never hold an image, an audio clip, or an archive. Text callers decode
   * explicitly — `new TextDecoder().decode(await readFile(p))` — the same shape
   * VS Code's `workspace.fs.readFile` has.
   *
   * **Whole-file**: the content is materialized in memory, so it is rejected
   * above {@link MODULE_FILE_BYTES_LIMIT}. Media meant for playback should not
   * come through here at all — `ModuleContext.storageUrl` yields a ranged URL a
   * media element streams without ever loading the file (见 18 三层模型).
   */
  readFile(path: string): Promise<Uint8Array>;
  /**
   * Write raw bytes (gated by `fs`). Encode text explicitly
   * (`new TextEncoder().encode(text)`); rejected above
   * {@link MODULE_FILE_BYTES_LIMIT}, see {@link readFile}.
   */
  writeFile(path: string, content: Uint8Array): Promise<void>;
  /**
   * Delete a file under the module storage root (gated by `fs`).
   * Missing path is a no-op (idempotent).
   */
  deleteFile(path: string): Promise<void>;
  /** Open a URL in the OS default browser (gated by `open-external`). */
  openExternal(url: string): Promise<void>;
  /**
   * Open an HTTP(S) URL in the desktop built-in browser (gated by
   * `open-browser`, 见 22). Rejects on runtimes without an implementation.
   */
  openBrowser(options: BrowserOpenOptions): Promise<void>;
  /**
   * Open a local file/folder with the OS default handler (gated by
   * `open-external`). **Absolute OS path, desktop-only** — the web host injects
   * a warn + no-op (见 18). The cross-runtime way to hand a file to the user is
   * {@link saveStorageFile}. Backed by the main-process `shell.openPath` (the
   * ext-host utilityProcess has no `shell`).
   *
   * `false` = the OS did not open it (the usual cause is that the file is gone;
   * `shell.openPath` also refuses a type it has no handler for). **Do not drop
   * this answer**: a stale path is the common case — the caller probed existence
   * when it rendered, and the user deletes files between render and click — and
   * a discarded `false` is a button that does nothing at all.
   */
  openPath(path: string): Promise<boolean>;
  /**
   * Reveal a local file in the OS file manager (gated by `open-external`).
   * **Absolute OS path, desktop-only** — same web no-op as {@link openPath}.
   * Ask `runtimeSupports(context.runtime, 'nativeFileManager')` before offering
   * a "show in folder" entry; otherwise prefer {@link saveStorageFile}.
   * Backed by the main-process `shell.showItemInFolder`.
   *
   * `false` carries the same meaning and the same warning as {@link openPath}'s.
   * Electron's `showItemInFolder` is itself void and fails silently on a missing
   * path, so the host checks the file is there before revealing — that check is
   * the only thing that can distinguish "revealed" from "nothing happened".
   */
  showItemInFolder(path: string): Promise<boolean>;
  /**
   * Hand a file the module owns to the **user** — a save dialog on the desktop,
   * a browser download on the web (gated by `open-external`, 见 18).
   *
   * Takes a path **relative to the module's storage root**, unlike
   * {@link openPath} / {@link showItemInFolder}, which take absolute OS paths and
   * are therefore desktop-only by construction. That is the point: this is the
   * one shape of "give the user this file" that both runtimes can honour, so a
   * module offering a download does not need to know which runtime it is on.
   *
   * **Not subject to {@link MODULE_FILE_BYTES_LIMIT}**: neither runtime reads the
   * bytes into the host — desktop copies the file, web points an anchor at the
   * storage URL. This is therefore the supported route for a large file, the
   * counterpart to {@link ModuleContext.storageUrl} for playback.
   *
   * `suggestedName` pre-fills the dialog / `download` attribute; the file's own
   * name is used when omitted. Resolves once the request has been handed off,
   * **not** once the user picks a destination — cancelling a save dialog is a
   * normal outcome, not an error, and the web side cannot observe the outcome at
   * all. Fail closed for empty / traversing paths.
   */
  saveStorageFile(relativePath: string, suggestedName?: string): Promise<void>;
  /**
   * Ask the user which folder a file that does not exist yet should be written
   * into (gated by `open-external`). Desktop shows a directory dialog and
   * resolves to the chosen absolute directory, or `null` when cancelled. The
   * file keeps its own name; `suggestedName` is not shown. The web host has no
   * OS path to hand back and resolves `null`.
   *
   * This is the half {@link saveStorageFile} cannot cover: the bytes are not in
   * the module root yet, so the native engine needs a directory before it
   * starts writing. Cancelling is a normal outcome, not an error.
   */
  pickSavePath(suggestedName?: string): Promise<string | null>;
  /**
   * Copy a file that is already on disk into a folder the user picks
   * (gated by `open-external`). Desktop shows a directory dialog and copies
   * under the file's own name (`suggestedName`, or the source base name). The
   * bytes stay out of the host heap. An existing file at that path is a
   * failure, not an overwrite. `cancelled` is the dialog; `failed` is a
   * missing source, a name collision, or a copy error (logged). The web host
   * resolves `failed` — hand an in-root file to the browser with
   * {@link saveStorageFile} instead.
   */
  copyToSavePath(
    sourcePath: string,
    suggestedName?: string,
  ): Promise<'saved' | 'cancelled' | 'failed'>;
  /**
   * Enumerate desktop screen/window sources for screen sharing (desktop-only,
   * backed by the main-process `desktopCapturer`). The web host injects a warn +
   * empty list (见 18). Pure enumeration, no side effect — the caller shows a
   * picker and the user decides which source to share.
   */
  screenSources(): Promise<ScreenSourceInfo[]>;
  /**
   * 指定下一次屏幕共享要采的源（desktop-only）：UI 在源选择器里选定后上报，主进程暂存，
   * 供下一次 `getDisplayMedia` 的 handler 消费。Electron 42 移除了渲染进程
   * `chromeMediaSourceId` 约束指定源，必须由主进程决定采哪个源。web 端 no-op。
   */
  prepareScreenShare(sourceId: string): Promise<void>;
}

/**
 * @deprecated Renamed to {@link AppNotification} (见 `notification-contract.ts`)
 * now that the same payload can be presented in-app as well as by the OS. The
 * fields are unchanged, so this alias is source-compatible.
 */
export type OSNotification = AppNotification;

/** EventBus event: a module asked the platform to show a notification (§5.15). */
export const NOTIFICATION_EVENT = 'platform.notification';

/** Payload of `NOTIFICATION_EVENT`: the requesting module and its message. */
export interface NotificationMessage {
  moduleId: string;
  message: string;
}

/** EventBus event: a module asked the platform to update the app badge (§5.15). */
export const BADGE_EVENT = 'platform.badge';

/** Payload of `BADGE_EVENT`: the requesting module and the badge count (0 clears). */
export interface BadgeUpdate {
  moduleId: string;
  count: number;
}

/**
 * Per-module isolated key-value storage exposed via `context.storage`
 * (架构方案 §5.13). Each module gets its own namespace; one module can never
 * read or write another's data. Reads are synchronous (the namespace is
 * preloaded into memory on activation, mirroring `context.getConfig`); writes
 * are async because they persist through the storage backend. State lives here
 * in the service layer; UI projects it via services rather than writing
 * directly (§5.13).
 */
export interface ModuleStorage {
  /** Synchronously read a stored value (from the preloaded cache). */
  get<T>(key: string): T | undefined;
  /** Snapshot of all stored entries for this module. */
  getAll(): Record<string, unknown>;
  /** All stored keys for this module. */
  keys(): string[];
  /** Persist a value, then resolve once written to the backend. */
  update(key: string, value: unknown): Promise<void>;
  /** Remove a stored value, then resolve once written to the backend. */
  delete(key: string): Promise<void>;
}

/**
 * The storage backend namespace a module's isolated data lives under
 * (架构方案 §5.13). Single source of truth so the host service and any future
 * tooling agree on the layout (e.g. `module:im`).
 */
export function moduleStorageNamespace(moduleId: string): string {
  return `module:${moduleId}`;
}

/**
 * Cross-process contract for the offline service (架构方案 §5.14). Lives
 * single-instance in the extension host and is consumed by the renderer via an
 * auto RPC proxy. The renderer is the source of truth for connectivity (it reads
 * `navigator.onLine`) and reports it here; the host re-broadcasts changes on the
 * EventBus (`NETWORK_STATUS_EVENT`) so every window and module observes one
 * shared network state (§4.3).
 */
export const OFFLINE_CHANNEL = 'offline';

/** EventBus event: the shared network status changed (§5.14/§4.3). */
export const NETWORK_STATUS_EVENT = 'platform.network';

/** Payload of `NETWORK_STATUS_EVENT`: the current connectivity. */
export interface NetworkStatus {
  online: boolean;
}

export interface IOfflineService {
  /** The current shared network status (host single source of truth). */
  getNetworkStatus(): Promise<boolean>;
  /** Report connectivity from a window (`navigator.onLine`); drives broadcast. */
  reportNetworkStatus(online: boolean): Promise<void>;
}

/**
 * Per-module offline capability exposed via `context.offline` (架构方案 §5.14).
 * Provides network-status awareness plus an offline operation queue persisted in
 * the module's isolated storage (§5.13). Queued operations drain through the
 * module-registered `process` handler when connectivity returns (FIFO; a
 * failing op halts the drain and is retried on the next online transition).
 */
export interface ModuleOffline {
  /** Whether the platform currently considers itself online. */
  isOnline(): boolean;
  /** Subscribe to network status changes. Returns a disposer. */
  onDidChangeNetwork(listener: (online: boolean) => void): () => void;
  /** Queue an operation to run when online; persisted across restarts. */
  enqueue(op: unknown): Promise<void>;
  /** Snapshot of the operations still queued for this module. */
  pending(): unknown[];
  /**
   * Register the single handler that drains queued operations. Invoked per op in
   * FIFO order while online; throwing halts the drain (the op stays queued).
   */
  process(handler: (op: unknown) => void | Promise<void>): void;
  /** Subscribe to drain completion (queue emptied). Returns a disposer. */
  onDidDrain(listener: () => void): () => void;
}

/**
 * Runs a worker the module ships, backing {@link ModuleContext.worker} (见 18).
 *
 * Only present on runtimes where the module cannot do this itself — see the
 * context field for why the browser is one and the desktop is not.
 */
export interface ModuleWorkerHost {
  /**
   * Start `relativePath` as a module-type worker and return a channel to it.
   *
   * Takes a **module-relative path**, resolved by the platform exactly like
   * {@link ModuleContext.assetUrl} (paths carrying a scheme and traversing paths
   * are refused). Resolving it here rather than accepting a URL is what keeps a
   * module from pointing the platform at somebody else's code, and keeps whoever
   * performs the load from having to know where module roots live.
   *
   * Resolves `undefined` when the worker could not be started at all — on the
   * web the ordinary case is "no page is connected to host it", which is a
   * normal state (every tab closed), not an error. Retry by calling again.
   *
   * **Not deduplicated by default.** Two calls start two workers. A module's
   * service is already a singleton in the host, so keeping one handle is its
   * own business and the platform does not second-guess it.
   *
   * Pass `{ exclusive: true }` when a second worker must not exist beside an
   * earlier one — including one left by a previous host. The page terminates
   * every worker it already hosts for this module before constructing the new
   * one. IM keeps the default, because each workspace has its own engine.
   */
  spawn(
    relativePath: string,
    options?: { exclusive?: boolean },
  ): Promise<ModuleWorker | undefined>;
}

/**
 * A live worker started through {@link ModuleWorkerHost.spawn}, and the channel
 * to it (见 18).
 *
 * **The channel is direct.** Whoever created the worker handed one end of a
 * `MessageChannel` to it and the other to the host, so messages do not hop
 * through that page — it is out of the data path once the handover is done.
 *
 * The worker's own script sees only the plain web idiom for receiving a channel:
 *
 * ```js
 * self.onmessage = (e) => { const port = e.ports[0]; ... };
 * ```
 *
 * There is no platform envelope around that first message, so a module's worker
 * carries no knowledge of this platform and stays runnable anywhere.
 */
export interface ModuleWorker {
  /**
   * Send a message. `transfer` is a transfer list (e.g. an `ArrayBuffer`), so
   * bulk payloads move without a copy; it is `unknown[]` only because this
   * package is compiled without DOM/WebWorker libs.
   */
  post(message: unknown, transfer?: unknown[]): void;
  /** Subscribe to messages from the worker. Returns a disposer. */
  onMessage(listener: (data: unknown) => void): () => void;
  /**
   * Subscribe to the worker going away. Returns a disposer.
   *
   * Fires when the worker dies for any reason the platform can observe —
   * including **the page hosting it being closed**, which on the web is an
   * ordinary event rather than a fault. The platform does **not** restart it:
   * only the module knows what re-creating its worker entails (re-init,
   * credentials, resync), so recovery is a `spawn` call the module makes.
   *
   * A dead channel is otherwise silent: a `MessagePort` whose peer is gone
   * accepts `post` without erroring, which is exactly why this event exists.
   *
   * The listener is told **why** (见 {@link WorkerCloseInfo}); ignoring the
   * argument is fine for a module that recovers the same way regardless.
   */
  onClose(listener: (info: WorkerCloseInfo) => void): () => void;
  /** Terminate the worker and release the channel. Idempotent. */
  dispose(): void;
}

/**
 * Why a {@link ModuleWorker} went away (见 18).
 *
 * The platform can tell these apart and the two mean different things to
 * whoever owns the worker, so it **says which** instead of leaving a module to
 * guess. Without it the only honest thing a module could write in its log is
 * "the channel closed" — and a module that guessed instead logged every death
 * as a closed page, which sent an engine-crash investigation looking at a page
 * that was open the whole time (that bug is what prompted this field).
 *
 * - `page-gone` — the document that constructed the worker went away, taking it
 *   along. On the web this is **ordinary**, not a fault: no page open means no
 *   worker, and spawning again works once one is back.
 * - `worker-failed` — the worker's own script died (threw at load, or raised an
 *   uncaught error the host observed). Respawning tends to fail the same way.
 */
export interface WorkerCloseInfo {
  readonly reason: 'page-gone' | 'worker-failed';
  /**
   * What the host observed, when it has anything to add — an error message for
   * `worker-failed`. Diagnostics only; never load-bearing.
   */
  readonly detail?: string;
}

/**
 * Reserved data-synchronization extension point (架构方案 §5.14). The platform
 * does NOT implement sync yet; this interface is declared so a future provider
 * (incremental sync, conflict resolution) can plug in without changing callers.
 * Intentionally unwired in this slice.
 */
export interface ISyncProvider {
  /** Push local changes for a namespace to the remote. */
  push(namespace: string, changes: unknown): Promise<void>;
  /** Pull remote changes for a namespace. */
  pull(namespace: string): Promise<unknown>;
  /** Resolve a conflict between local and remote values (optional strategy). */
  resolveConflict?(local: unknown, remote: unknown): unknown;
}

/** Decision for a permission: undecided (`prompt`), `granted`, or `denied`. */
export type PermissionState = 'granted' | 'denied' | 'prompt';

/** A persisted permission decision for a module (架构方案 §5.6). */
export interface PermissionGrant {
  moduleId: string;
  permission: Permission;
  state: PermissionState;
}

/**
 * A pending authorization prompt pushed from the broker to every window
 * (`PERMISSION_PROMPT_EVENT`). The renderer answers via `respond(id, granted)`.
 */
export interface PermissionPrompt {
  id: string;
  moduleId: string;
  permission: Permission;
}

/**
 * Cross-process contract for the permission broker (架构方案 §5.6). Lives
 * single-instance in the extension host and is consumed by the renderer via an
 * auto RPC proxy. Authorization prompts flow host -> renderer over the EventBus
 * (`PERMISSION_PROMPT_EVENT`); the renderer answers back over this channel.
 */
export const PERMISSION_CHANNEL = 'permission';

/** EventBus event: the broker asks the user to authorize a pending request. */
export const PERMISSION_PROMPT_EVENT = 'permission.prompt';

/** EventBus event: a pending request was resolved (so other windows dismiss). */
export const PERMISSION_RESOLVED_EVENT = 'permission.resolved';

/**
 * EventBus event: a persisted permission decision changed — granted, denied, or
 * revoked (架构方案 §5.6). Broadcast so a permission-management UI in any window
 * refreshes its list (§4.3). Distinct from `PERMISSION_RESOLVED_EVENT`, which is
 * about dismissing a specific pending prompt.
 */
export const PERMISSION_CHANGED_EVENT = 'permission.changed';

/** Payload of `PERMISSION_RESOLVED_EVENT`: which request was answered, and how. */
export interface PermissionResolved {
  id: string;
  granted: boolean;
}

export interface IPermissionService {
  /** Answer a pending prompt identified by `requestId`. */
  respond(requestId: string, granted: boolean): Promise<void>;
  /** All persisted decisions (for a management UI). */
  list(): Promise<PermissionGrant[]>;
  /** Drop a persisted decision, reverting the permission to undecided. */
  revoke(moduleId: string, permission: Permission): Promise<void>;
}

// ── OverlaySurface（架构方案 §5.8）──────────────────────────────────────────────
// 一等的"浮层表面"：与布局区（LayoutArea）正交，portal 到工作台顶层。首期两种
// presenter（modal / drawer），两类内容模型：headless 承载模块视图（平台只给行为
// 壳）、声明档（confirm/alert/prompt，平台给完整默认 UI）。触发双向——service 侧经
// `context.overlay` 跨进程发起（结果回传），UI 视图经 `ViewProps.overlay` in-process
// 发起。承载与回传闭环对标权限 broker（host 发起 → renderer 弹 → respond 回传）。

/**
 * 承载模块视图的浮层外形（modal 居中 / drawer 侧滑 / popover 锚定）。
 *
 * `popover` 与前两者的差别不是外观而是**语义**：它不铺遮罩、不夺焦点、不阻断底下的界面，
 * 而是「贴着某个东西看一眼」。所以它必须带 {@link OverlayViewOptions.anchor}——没有锚点的
 * 「锚定浮层」只能退回居中，那就是 modal，不该多一个档。平台**不**再套一层定位：被承载视图
 * 自己用调用方放进 `params` 的矩形落位（碰撞与进出场都在那一层），退场结束后再 `dismiss`。
 *
 * 它是为「点搜索结果看一张资料卡」这类动作存在的：那种场合下压暗全窗、把用户刚敲的查询词连
 * 同结果列表一起收掉，代价远大于收益（见 `SearchHitAnchor`）。
 */
export type OverlayPresenter = 'modal' | 'drawer' | 'popover';

/**
 * `popover` presenter 的锚点：视口坐标系的一个矩形（CSS 像素）。平台用它判定这是锚定浮层，
 * 不拿它给壳定位；被承载视图从 `params` 里读到同一矩形后自己贴着它摆。
 *
 * 收矩形而不是 DOM 节点，因为发起方常在**扩展宿主**（跨进程，传不了节点）。形状与
 * `SearchHitAnchor` 一致，故搜索命中行那条链路可以原样转手。
 */
export interface OverlayAnchorRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** drawer presenter 的滑入方向。 */
export type OverlaySide = 'left' | 'right' | 'top' | 'bottom';

/**
 * modal presenter 的尺寸档。`sm`–`xl` 是居中卡片（有遮罩与关闭键）；`full` 铺满当前
 * 窗口、无遮罩、无平台关闭键与标题壳（视图自绘全部 UI）。仍是 overlay，不是裸壳游离窗。
 */
export type OverlaySize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/** 打开一个「模块视图」型 overlay 的选项（headless：平台只给行为壳）。 */
export interface OverlayViewOptions {
  /** 要承载的模块视图 id（需在其模块 manifest 里以 `area: 'overlay'` 声明）。 */
  viewId: string;
  /** 透传给被承载视图的不透明参数（结构化可克隆）。 */
  params?: unknown;
  /** 平台壳上的标题（可选；视图也可自绘标题）。 */
  title?: string;
  /**
   * modal 尺寸档（默认 `md`）。`full` 铺满当前窗口、去掉遮罩与平台关闭键；
   * 传入的 `title` 也不再画到平台壳上（视图自绘）。
   */
  size?: OverlaySize;
  /** drawer 滑入方向（默认 `right`），仅 drawer presenter 生效。 */
  side?: OverlaySide;
  /**
   * `popover` presenter 的锚点矩形（视口坐标）。**该档必填**：缺了它就没有「贴着谁」可言，
   * 承载会拒绝呈现并 resolve `undefined`（见 `overlay-host`），而不是居中变成 modal。
   * 壳不按这个矩形摆放。调用方把同一矩形放进 `params`，视图自己的定位器贴边。
   */
  anchor?: OverlayAnchorRect;
  /**
   * 是否允许 Esc / 点外面关闭（默认 true）。
   * modal / drawer 由壳执行。popover 壳没有这层点击，inline 视图读
   * {@link ViewOverlayApi.dismissable}，为 false 时不因点外面或 Esc 调用 `dismiss`。
   */
  dismissable?: boolean;
}

/**
 * `openModal` 的选项。在 {@link OverlayViewOptions} 之上多一个窗口聚焦开关。
 * drawer / popover / 声明档没有这一项，也不会为了弹层去激活窗口。
 */
export interface OverlayModalOptions extends OverlayViewOptions {
  /**
   * 呈现时，若这张弹层所在的窗口不在前台，是否把它叫到前台。
   *
   * 省略为 `true`：桌面把该窗 show、restore、再 focus（最小化或关进托盘也会重新出现）；
   * 已经在前台则不动。Web 上尝试聚焦该页，浏览器拒绝时弹层仍打开，并记一条失败日志。
   * 显式 `false` 仍打开弹层并等待关闭，只是不激活窗口。
   */
  focus?: boolean;
}

export interface OverlayConfirmOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  /** 确认按钮渲染为危险样式（如删除）。 */
  destructive?: boolean;
}

export interface OverlayAlertOptions {
  title?: string;
  message?: string;
  confirmText?: string;
}

export interface OverlayPromptOptions {
  title?: string;
  message?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
}

/** {@link OverlaySelectOptions} 的一个候选项。 */
export interface OverlaySelectOption {
  /** 选中后回传的值。 */
  value: string;
  /** 展示文案（调用方自行本地化）。 */
  label: string;
  /** 次要说明，渲染在标签下方。 */
  description?: string;
}

export interface OverlaySelectOptions {
  title?: string;
  message?: string;
  /** 候选项；空数组呈现为空态，用户只能取消。 */
  options: readonly OverlaySelectOption[];
  /** 过滤框占位文案。 */
  placeholder?: string;
  cancelText?: string;
}

/**
 * 跨进程 / 跨入口统一的 overlay 打开请求（EventBus 上 {@link OVERLAY_OPEN_EVENT}
 * 的 payload 内嵌此结构）。`role` 判别内容模型，presenter/side/size 描述外形。
 */
export type OverlayRequest =
  | ({ role: 'view'; presenter: 'modal' } & OverlayModalOptions)
  | ({
      role: 'view';
      presenter: 'drawer' | 'popover';
    } & OverlayViewOptions)
  | ({ role: 'confirm' } & OverlayConfirmOptions)
  | ({ role: 'alert' } & OverlayAlertOptions)
  | ({ role: 'prompt' } & OverlayPromptOptions)
  | ({ role: 'select' } & OverlaySelectOptions);

/**
 * overlay 的解析结果（不透明，随 role 而定）：view=视图 resolve 的值或 undefined
 * （被 dismiss）；confirm=boolean；alert=undefined；prompt/select=string 或 null
 * （取消）。
 */
export type OverlayResult = unknown;

/**
 * overlay 打开能力（`context.overlay` 与 `ViewProps.overlay` 共用）。每个方法返回
 * 一个在 overlay 关闭时 resolve 的 Promise，携带对应结果类型。
 */
export interface OverlayApi {
  /**
   * 在居中 modal 壳内承载一个模块视图；`size: 'full'` 时铺满当前窗口且无遮罩/关闭键。
   * resolve 视图回传的结果（dismiss 则 undefined）。
   *
   * `focus` 省略为 true：呈现这张弹层的窗口不在前台时把它叫到前台。见 {@link OverlayModalOptions.focus}。
   */
  openModal(options: OverlayModalOptions): Promise<OverlayResult>;
  /** 在侧滑 drawer 壳内承载一个模块视图；resolve 同上。 */
  openDrawer(options: OverlayViewOptions): Promise<OverlayResult>;
  /**
   * 把一个模块视图承载成**不铺遮罩**的浮层：不夺焦点、不阻断底下的界面
   * （见 {@link OverlayPresenter}）。`anchor` 必填——没有它这个档没有意义，承载会拒绝呈现
   * 并 resolve `undefined`，而不是悄悄居中变成一个 modal。壳本身不定位，视图用放进
   * `params` 的同一矩形自己摆，退场结束后再 `dismiss`。
   *
   * 用途是「贴着刚点的那个东西看一眼」：全局搜索命中行开资料卡走的就是这条（矩形由平台经
   * `SearchHitAnchor` 追加给命令，模块再写入 `params`）。
   */
  openPopover(
    options: OverlayViewOptions & { anchor: OverlayAnchorRect },
  ): Promise<OverlayResult>;
  /** 声明式确认框；resolve `true`/`false`。 */
  confirm(options: OverlayConfirmOptions): Promise<boolean>;
  /** 声明式提示框；在用户确认后 resolve。 */
  alert(options: OverlayAlertOptions): Promise<void>;
  /** 声明式输入框；resolve 输入的字符串，取消则 `null`。 */
  prompt(options: OverlayPromptOptions): Promise<string | null>;
  /**
   * 声明式单选框（带过滤）；resolve 选中项的 `value`，取消则 `null`。
   *
   * 与 `prompt` 同属「命令自己收参」这条路：带参命令不在 manifest 里声明参数
   * schema，需要取值时自行唤起这些 overlay（复杂流程则用 `openModal` 自建，
   * 架构方案 §5.9）。
   */
  select(options: OverlaySelectOptions): Promise<string | null>;
}

/**
 * `ViewProps.overlay` 的形状：在 {@link OverlayApi} 之上，附带**仅当本视图正被 overlay
 * 承载时**才有效的自关闭能力（否则为 undefined）。被承载视图用它回传结果并关闭。
 */
export interface ViewOverlayApi extends OverlayApi {
  /** 以结果关闭承载本视图的 overlay（仅 headless 承载时存在）。 */
  resolve?: (result?: OverlayResult) => void;
  /** 无结果关闭承载本视图的 overlay（仅 headless 承载时存在）。 */
  dismiss?: () => void;
  /**
   * 这次承载是否允许点外面 / Esc 关掉（仅 headless 承载时存在）。
   * 省略请求时为 true。popover 由视图自己的浮层遵守。
   */
  dismissable?: boolean;
}

/**
 * The shape of `ViewProps.settings` (见 05 / 23) — this module's own declared
 * settings, for a module that renders its own settings page.
 *
 * Async throughout because the configuration service lives in the extension host:
 * the layered read is over RPC, and a write has to be persisted before anyone
 * should believe it. Pairs with {@link CONFIG_CHANGED_EVENT} via `onEvent`, which
 * is how a page stays right when the same key is changed in another window.
 */
export interface ViewSettingsApi {
  /** The effective value of one of this module's declared settings. */
  get(key: string): Promise<unknown>;
  /** Write it into its target scope layer, validated against the declared type. */
  set(key: string, value: unknown): Promise<void>;
  /** Drop this module's user override for `key`, falling back to the default. */
  reset(key: string): Promise<void>;
}

/** overlay broker 跨进程通道（renderer 经 auto RPC 代理消费，见 §5.2）。 */
export const OVERLAY_CHANNEL = 'overlay';

/** EventBus 事件：host 侧发起一个 overlay，请 renderer 呈现（payload: {id,request}）。 */
export const OVERLAY_OPEN_EVENT = 'overlay.open';

/** EventBus 事件：某 overlay 已解析（其它窗口据此关闭同 id 的呈现）。 */
export const OVERLAY_RESOLVED_EVENT = 'overlay.resolved';

/** {@link OVERLAY_OPEN_EVENT} 的 payload：待呈现的 overlay 及其请求。 */
export interface OverlayOpen {
  id: string;
  request: OverlayRequest;
}

/** {@link OVERLAY_RESOLVED_EVENT} 的 payload：哪个请求已解析。 */
export interface OverlayResolved {
  id: string;
}

/**
 * overlay broker 的跨进程契约（架构方案 §5.8）。单实例居于扩展宿主，renderer 经
 * auto RPC 代理消费。host 侧发起的 overlay 经 EventBus（{@link OVERLAY_OPEN_EVENT}）
 * 推到 renderer；renderer 经本通道回传结果。对标 {@link IPermissionService}。
 */
export interface IOverlayService {
  /** 回传一个 host 侧发起的 overlay 的结果，解析其挂起的 Promise。 */
  respond(requestId: string, result: OverlayResult): Promise<void>;
}

/**
 * 应用根门控（App Gate / Root Surface，架构方案 §5.8）——与布局区、OverlaySurface
 * 正交的第三种呈现维度：在门控条件满足前，用某模块视图**全屏替代**工作台
 * （Workbench 完全不挂载），放行后再进入工作台。内核只维护「当前 active gate」这
 * 一 policy-free 状态；「何时竖起 / 放行」是业务策略，由 source 模块经
 * {@link GateApi} 驱动（如 onboarding 编排 account + org-menu 的登录/选空间流程）。
 */
export interface ActiveGate {
  /** 门控 id（同一门 open/close 的键，如 `'onboarding'`）。 */
  id: string;
  /** 承载门控页的视图 id（该视图以 `area: 'gate'` 声明，不进任何布局区）。 */
  viewId: string;
  /** 视图所属模块 id（渲染端据此懒激活并挂载）。 */
  moduleId: string;
  /** 传给门控视图的不透明参数（模块自定义）。 */
  params?: unknown;
}

/** gate broker 跨进程通道（renderer 经 auto RPC 代理消费，见 §5.2）。 */
export const GATE_CHANNEL = 'gate';

/**
 * EventBus 事件：当前 active gate 发生变化（竖起 / 放行）。payload 见
 * {@link GateChanged}——`gate` 为 null 表示无门控（可进入工作台）。
 */
export const GATE_CHANGED_EVENT = 'gate.changed';

/** {@link GATE_CHANGED_EVENT} 的 payload：变化后的当前 active gate（或 null）。 */
export interface GateChanged {
  gate: ActiveGate | null;
}

/**
 * 门控打开能力（`context.gate`，架构方案 §5.8）。source 模块据此竖起 / 放行门控；
 * 与 {@link OverlayApi} 同为 source-only（沙箱不给）。open 幂等（同 id 覆盖），
 * close 只对当前门 id 生效。
 */
export interface GateApi {
  /** 竖起门控：用 `viewId` 视图全屏替代工作台，直到 close 同 id。 */
  open(options: { id: string; viewId: string; params?: unknown }): void;
  /** 放行门控（若当前 active gate 为 `id`）；无 active gate 时 no-op。 */
  close(id: string): void;
}

/**
 * gate broker 的跨进程契约（架构方案 §5.8）。单实例居于扩展宿主，renderer 经 auto
 * RPC 代理消费。boot 时用 {@link getActiveGate} 回读当前态——门控可能在 renderer
 * 连上前就已竖起（onStartup 模块），故必须能同步查询而非仅靠事件。对标
 * {@link IOverlayService}。
 */
export interface IGateService {
  /** 回读当前 active gate（无则 null）。 */
  getActiveGate(): Promise<ActiveGate | null>;
}

/**
 * Transport-agnostic RPC envelope (架构方案 §5.2). The same shapes flow over
 * MessagePort (desktop) and could later flow over WebSocket (web/remote).
 */
export interface RpcError {
  message: string;
  stack?: string;
  /**
   * The thrown error's `name` (constructor-ish identity), so the caller can
   * branch on the error *kind* instead of pattern-matching prose.
   */
  name?: string;
  /**
   * Own primitive-valued properties of the thrown error — the machine-readable
   * verdict a caller needs (a native return code, an HTTP status, a domain error
   * id). Without this an RPC error arrives as prose only, and callers end up
   * regex-parsing `message` to decide what to do (见 `02` 通信层).
   */
  data?: Record<string, string | number | boolean | null>;
}

export interface RpcRequest {
  kind: 'request';
  id: number;
  method: string;
  args: unknown[];
}

export interface RpcResponse {
  kind: 'response';
  id: number;
  result?: unknown;
  error?: RpcError;
}

export interface RpcEvent {
  kind: 'event';
  event: string;
  data: unknown;
}

export type RpcMessage = RpcRequest | RpcResponse | RpcEvent;

/**
 * Module activation contract (架构方案 §5.3/§5.5). Lives in `shared` so both the
 * extension host (which builds the context) and the SDK (which re-exports it to
 * module authors) depend on a single common definition.
 */
export type CommandHandler = (...args: unknown[]) => unknown;

/**
 * A module registered in the extension host (manifest-only metadata; no activation).
 * Used by in-process modules that need to enumerate the runtime module set (e.g.
 * app-center merging store apps with built-in / bundle-installed modules).
 */
export interface ListedModule {
  manifest: ModuleManifest;
  /** True when sourced from the dynamic bundle cache rather than repo-shipped. */
  dynamic: boolean;
}

export interface PlatformApi {
  getManifest(id: string): ModuleManifest | undefined;
  /**
   * All valid modules known to the extension host (manifest-only, no activation).
   * Mirrors the internal discovery set after manifest validation / devOnly filtering.
   */
  listModules(): ListedModule[];
  /**
   * Reverse-lookup another module's exported API namespace (架构方案 §5.5),
   * aligned with VSCode's `getExtension().activate()`: async because it lazily
   * activates the owning module on a miss (via its `onApi:<namespace>`
   * activation event) so a not-yet-active provider still resolves. Resolves the
   * live API, or `undefined` when no module exported the namespace. There is no
   * version range: callers detect the capabilities they need on the returned
   * object. No permission gate: `getApi` is in-process only (live objects never
   * cross a process boundary), and in-process `source` modules are trusted
   * (§5.6 信任模型) — sandboxed surfaces cannot reach it at all.
   */
  getApi<T>(namespace: string): Promise<T | undefined>;
  /**
   * Invoke a contributed command by id (架构方案 §5.9) — the request/response
   * path to another module that does not need a live object. Complements
   * {@link getApi}: that one hands back the provider's real object (callbacks,
   * `AbortSignal`, native handles all work, in-process only), this one addresses
   * a command by string and is therefore constrained to structured-cloneable
   * arguments and results, which is what lets the same call shape exist on every
   * surface (`ViewProps.executeCommand`, the sandbox SDK, and the sandbox
   * service tier of 见 24). A module's own commands are reachable too, so a
   * service can drive its own `createInvokeRouter` bridge without a second
   * entry point.
   *
   * **Gated exactly like the sandbox face** (`executeCommandAsModule`, 见 6 /
   * §5.6), which is deliberate rather than inherited: commands are the one
   * cross-module surface whose owner can mark entries `requiresPermission`, and
   * splitting the judgment by *where the caller runs* would leave that marking
   * meaning two different things. So: a command in the caller's own namespace is
   * always allowed; a cross-module command that **no** manifest declares fails
   * closed (nobody chose default-open for it); an owner-gated command needs the
   * caller to declare `command:<id>` and be granted it. Denials reject with
   * {@link CommandDeniedError}. Unlike the gates on `requestPermission` /
   * `capabilities`, this one is *not* short-circuited for in-process callers.
   *
   * A command that no module registered rejects with the diagnosis of *why*
   * (typo vs. declared but not registered on this runtime), not a bare miss.
   */
  executeCommand(id: string, ...args: unknown[]): Promise<unknown>;
  /**
   * Navigate a target surface to an opaque `location` (架构方案 §5.8 跳转, doc
   * 44). `target` is a namespaced surface id — `view:<viewId>`,
   * `external:<moduleId>`, or `window:<windowId>`; `location` is a
   * structured-cloneable, module-defined payload the target interprets (the
   * platform only routes it + focuses/presents the target). Source modules call
   * it with full power; sandboxed surfaces use the gated
   * {@link IGatedModuleService.navigate}.
   */
  navigate(target: string, location: unknown): Promise<void>;
  /**
   * Reset every workbench window's editor area (架构方案 §5.7 视图缓存): drop the
   * keep-alive tree and MRU cache, then reopen `workbench.startupEditorView`.
   *
   * For a module that owns a **data scope** — a workspace, an organisation, a
   * signed-in account — to call when that scope changes, because editor state
   * built against the previous one is stale. The platform deliberately does not
   * model the scope itself: it neither knows nor asks what changed, which is what
   * keeps this one mechanism instead of a workspace-shaped special case in the
   * shell.
   *
   * Fire-and-forget like {@link navigate}: it broadcasts
   * {@link WORKBENCH_EDITOR_SCOPE_RESET_EVENT} and resolves without waiting for
   * any window to act (there may not be one yet).
   */
  resetEditorScope(): Promise<void>;
  /**
   * Close the platform settings window. A module that owns a data scope calls
   * this when that scope changes in a way that makes the open preferences
   * stale: the signed-in session ended, or the current workspace changed.
   *
   * Only that window. Other detached surfaces, the built-in browser and
   * capture editors stay. A fresh login does not call this. Fire-and-forget
   * like {@link resetEditorScope}.
   */
  closeSettingsWindow(): Promise<void>;
}

/**
 * The generic module-defined contribution point registry (见 17 / 架构方案
 * §5.5), handed to a module via {@link ModuleContext.contributions}. Lets a
 * module (a) contribute a runtime handler/value to a point, and (b) — as the
 * point's owner — enumerate declared contributions (without activating the
 * contributors) and lazily resolve a contributor's runtime value. The kernel is
 * business-agnostic: it never interprets a point id or an entry's `data`.
 */
export interface ContributionRegistryFacade {
  /**
   * All declared contributions to `point` across modules, from manifest only —
   * does NOT activate the contributors (mirrors `listContributions`). Entries
   * gated by a permission the contributor lacks are omitted (default-open).
   */
  getContributions(point: string): ResolvedContribution[];
  /**
   * Curations declared against `point`, from manifests only. Does not activate
   * modules and does not filter {@link getContributions}. The owner passes a
   * scope id to `applyContributionCuration` after it has merged its own items.
   */
  getCurations(point: string): ResolvedContributionCuration[];
  /**
   * Bind this (calling) module's runtime handler/live value for a contribution
   * it declared. Call from `activate()`; the value is reclaimed on deactivate.
   * The value may be any in-process value (function, object) — it never crosses
   * a process boundary.
   */
  register(point: string, id: string, value: unknown): void;
  /**
   * Lazily activate the module that declared the entry `key` under `point`
   * (via its `onContribution:<point>` activation event / direct activation),
   * then return the runtime value it {@link register}ed. Resolves `undefined`
   * when the entry is unknown or the contributor bound nothing.
   */
  resolveContribution(point: string, key: string): Promise<unknown>;
  /**
   * Observe contribution changes for `point` in-process — fires when a
   * contributing module is (un)registered or a runtime value is (un)bound. Lets
   * an owner re-read `getContributions`. Returns a disposer.
   */
  onDidChangeContributions(point: string, listener: () => void): Disposer;
}

export interface ModuleContext {
  readonly platform: PlatformApi;
  registerCommand(id: string, handler: CommandHandler): void;
  exportApi<T>(namespace: string, api: T): void;
  /**
   * Open a platform overlay (架构方案 §5.8 OverlaySurface): a modal / drawer
   * hosting one of this module's views, or a declarative confirm/alert/prompt.
   * Cross-process — the request is presented in the renderer and the result is
   * carried back, so `await` resolves with the user's outcome. Available to
   * `source` modules (trusted, in-process); sandboxed surfaces do not get it.
   */
  readonly overlay: OverlayApi;
  /**
   * Open / close / focus detached OS windows (架构方案 §5.8 通用 Detachable)。
   * Relayed to the main process (fire-and-forget). Available to `source` modules;
   * sandboxed surfaces do not get it, mirroring {@link overlay}.
   */
  readonly window: WindowApi;
  /**
   * Read the launchable projection and change what sits in the rail (见 21).
   * Available to `source` modules, mirroring {@link overlay} / {@link window}.
   */
  readonly launcher: LauncherApi;
  /**
   * Report unread/pending state and ask for the user's attention (见 23 / §5.15).
   * `registerProvider` feeds the app-level badge (dock / taskbar / favicon);
   * `request` asks the platform to pull the user back — flashing the taskbar
   * button, bouncing the dock, alternating the browser tab's title.
   *
   * Available to `source` modules, mirroring {@link launcher}. A sandboxed
   * module still reaches the badge half through `capabilities.setBadge`, but
   * never the pulse.
   */
  readonly attention: AttentionApi;
  /**
   * Contribute to the single taskbar progress bar (见 23 / 需求 §4.5.2).
   * In-process only, like {@link attention}: a sandboxed surface has no
   * `ModuleContext`, and this face is not opened as a capability.
   *
   * Colour is not a parameter — the OS paints from a semantic mode. Deactivate
   * must revoke the provider (or call the disposer) or a leftover bar never
   * finishes.
   */
  readonly progress: ProgressApi;
  /**
   * Contribute Windows jump-list rows (见 23 / 需求 §4.5.4). Each row is a
   * module-relative deep-link path; the platform prefixes scheme + moduleId
   * and delivers it through the existing `onUrl` path (见 20). In-process only.
   */
  readonly jumpList: JumpListApi;
  /**
   * Report how this module's own connection is doing (见 23 / 需求 §4.6.1).
   * Folded with the OS-level network fact into one application state, which the
   * tray paints as a disconnected badge. In-process only, like {@link progress}.
   */
  readonly connectivity: ConnectivityApi;
  /**
   * Contribute one segment of the app caption — the line of native chrome that
   * says who is signed in and where (见 23 托盘 / 需求 §4.6.2). Text only, and
   * in-process only, like {@link progress}.
   */
  readonly caption: CaptionApi;
  /**
   * Read / write the merged app-link set (见 21). Same inventory the settings
   * "链接应用" panel edits. Available to `source` modules, mirroring
   * {@link launcher}. Provider-owned rows come back `editable: false` and
   * `update` / `remove` reject them — the client cannot rewrite a push.
   *
   * Changes broadcast {@link APP_LINKS_CHANGED_EVENT}; observe them with
   * {@link onEvent}. `refresh` stays on the settings RPC
   * ({@link IAppLinkService}) — a module does not poke other providers.
   */
  readonly appLinks: AppLinkApi;
  /**
   * 装 / 卸下发模块（见 12）。落在与设置页同一个宿主账本上。
   *
   * **绝大多数模块拿不到这个字段**（它是 optional 而不是必有，正是为此）：`dynamic` 的模块
   * 一律没有，仓内模块也要显式声明 {@link MODULE_INSTALL_PERMISSION} 才有。读之前先判空，
   * 就像判 `context.worker`。
   */
  readonly bundles?: BundleApi;
  /**
   * Raise / release the application root gate (架构方案 §5.8 App Gate): full-screen
   * a view in place of the workbench until released. Policy-free at the kernel —
   * the module decides when to gate (e.g. onboarding gating on login/workspace).
   * Available to `source` modules (trusted, in-process); sandboxed surfaces do not
   * get it, mirroring {@link overlay}.
   */
  readonly gate: GateApi;
  /**
   * The generic module-defined contribution point registry (见 17). Contribute a
   * runtime value to a point, or — as an owner — enumerate/resolve declared
   * contributions. Declarative discovery is activation-free; behavior binds at
   * activation.
   */
  readonly contributions: ContributionRegistryFacade;
  /**
   * Publish an event onto the platform EventBus (架构方案 §5.15). Delivered to
   * all connected windows for cross-window state broadcast (§4.3).
   */
  emitEvent(event: string, data?: unknown): void;
  /**
   * Handle deep links addressed to this module (见 20):
   * `hibro://<moduleId>/<path>` on desktop, `<origin>/<moduleId>/<path>` on web.
   * Returns a disposer; a second registration replaces the first.
   *
   * Requires `activationEvents: ["onUrl"]` in the manifest — that declaration is
   * what lets the platform wake this module when a link arrives (and what stops
   * an arbitrary URL from waking modules that never opted in).
   *
   * **In-process only** (like {@link ModuleContext.gate} / `overlay`): a link may
   * carry a one-time credential such as an OAuth `code`, so it is never handed to
   * a sandboxed surface.
   */
  registerUrlHandler(handler: ModuleUrlHandler): Disposer;
  /**
   * Handle platform pushes on the channels this module declared in
   * `contributes.pushChannels` (见 26). Returns a disposer; a second
   * registration replaces the first.
   *
   * The declaration is what lets the platform wake this module when a push
   * arrives — a module that declared no channel is never activated by one.
   *
   * **In-process only** (like {@link ModuleContext.registerUrlHandler}): a push
   * carries whatever the channel's backend sends, so it is never handed to a
   * sandboxed surface. Re-emit with {@link emitEvent} to reach your own UI.
   */
  registerPushHandler(handler: ModulePushHandler): Disposer;
  /**
   * Serve the main-world channels this module declared in
   * `contributes.pageWorldScripts` (见 22).
   *
   * **In-process source modules only, and absent where injection does not
   * exist** (web hosts browser pages in cross-origin iframes). Reading
   * `undefined` is how a module learns its provider will never be reachable on
   * this runtime, instead of registering a channel nothing can ever call.
   */
  pageWorld?: PageWorldApi;
  /**
   * Hand the platform one inbound push envelope for routing (见 26).
   *
   * The **provide** side of the push axis, and the only one that is closed:
   * `registerPushHandler` above is open to every module, because receiving on a
   * channel you declared is self-limiting. Delivering is not — a module that
   * could hand in any envelope could forge pushes on another module's channel
   * and impersonate its backend, which is exactly what `matchPushOwner`'s
   * fail-closed ownership exists to prevent.
   *
   * **Absent unless this module is the configured provider**
   * (`platform.pushProvider`,
   * and never for a dynamically delivered module). Missing rather than
   * always-failing, same shape as {@link ModuleContext.bundles}: reading
   * `undefined` says "not you" at the one place that can act on it.
   *
   * The frame is validated here, not there ({@link parsePlatformPushFrame}), and
   * `source` is stamped by the host from its own runtime — which intake carried
   * a push is a fact about the process, not something the caller may claim.
   * Resolves whether the owning module took it.
   */
  deliverPlatformPush?(frame: unknown): Promise<boolean>;
  /**
   * Drive the resident desktop agent (见 26): hand it the current WSS access
   * token on login / refresh / logout, and the current workspace when it
   * changes. Method names and payloads are the agent's own, so nothing is
   * renamed on the way to the wire.
   *
   * Absent unless this module is the configured provider (same as
   * {@link deliverPlatformPush}) **and** the shell relays these onward, which
   * only the desktop one does — on web the connection lives in this very
   * process, so a provider there talks to its client directly and needs no
   * relay. Withheld rather than accepted and dropped, so testing for it answers
   * "is there an agent on the other end".
   */
  platformService?: PlatformServiceRelay;
  /**
   * Lend the platform your client's application version check (见 25 / 26).
   *
   * The platform owns update state, cadence and prompt; on a runtime where the
   * platform connection lives **inside this module** (the browser's wasm client)
   * it cannot reach the endpoint without you. Registering makes the workbench's
   * update check run through your client; a second registration replaces the
   * first, and the disposer gives the seat up.
   *
   * The checker must resolve with the offer, resolve `undefined` for「已是最新」,
   * and **reject when the check itself failed** — folding a failure into
   * `undefined` would have the workbench tell the user they are up to date on the
   * strength of a request that never arrived.
   *
   * Absent unless this module is the configured provider (same gate as
   * {@link deliverPlatformPush}) **and** the runtime has no platform client of
   * its own — the desktop's lives in a resident process the shell already talks
   * to, so nothing there needs lending. Withheld rather than
   * accepted-and-ignored.
   */
  registerAppUpdateChecker?(checker: ModuleAppUpdateChecker): Disposer;
  /**
   * Read the effective configuration value for `key` (架构方案 §5.12). Synchronous
   * because the configuration service is in-process with the extension host.
   */
  getConfig(key: string): unknown;
  /**
   * Read the effective product/deployment configuration value for `key` (见 05
   * 产品配置). Distinct from {@link getConfig} (end-user settings): product config
   * is resolved at boot from `product.json` / `product.local.json` / env / argv
   * layered over module-declared defaults, read-only at runtime, and never shown
   * in the settings UI. Synchronous because it is in-process with the host.
   */
  getProductConfig(key: string): unknown;
  /**
   * The current effective locale (见 14 / §5.11), e.g. `en-US` (`system` already
   * resolved to a concrete tag). Async because the `NlsService` lives
   * single-instance in the host and resolves the preference against the OS
   * locale. To react to language changes, subscribe to `LOCALE_CHANGED_EVENT`
   * via {@link onEvent} (payload `{ preference, locale }`). Use
   * {@link getLocalePreference} for the raw user setting.
   */
  getLocale(): Promise<Locale>;
  /**
   * The user's locale preference (见 14 / §5.11): `system` (unresolved) or an
   * explicit locale. Companion to {@link getLocale} (the resolved locale).
   */
  getLocalePreference(): Promise<LocalePreference>;
  /**
   * Whether the user is looking at the app right now (见 23 §2). Synchronous:
   * every shell reports the fact into this process, so reading it is a field
   * access — the same reason {@link getConfig} is synchronous.
   *
   * **Not for deciding whether to notify.** That gate stays in the platform and
   * a module states its side of it with `urgency` (`low` for an event its own UI
   * already shows, 见 23 §2); second-guessing it here is exactly how a message
   * to the open conversation ended up suppressed while the window sat in the
   * tray. What this is for is work that only makes sense **at the moment the
   * user comes back**, which no urgency can express — IM withdraws the
   * notification for the conversation still on screen, because looking at it is
   * reading it (需求 §4.4.3). That moment arrives on
   * `APP_VISIBILITY_CHANGED_EVENT` via {@link onEvent}; this getter is for a
   * module activating mid-session, which would otherwise be blind until the
   * next flip (same pull/subscribe pairing as {@link getLocale}).
   */
  isAppVisible(): boolean;
  /**
   * Hold `workbench.captureRegion` (见 28). While any hold is outstanding the
   * command returns `{ action: 'cancel' }` and does not open a session. The
   * global shortcut stays registered. Release is idempotent. In-process only:
   * the sandbox context does not include this method.
   *
   * The platform does not know why the hold exists. Push the returned function
   * onto `subscriptions` if the hold should die with the module.
   */
  holdCapture(): () => void;
  /**
   * Contribute an ordered configuration scope layer (架构方案 §5.12 分层), e.g. a
   * `workspace` layer that shadows the `user` layer. Returns a disposer that
   * unregisters the layer. The service re-broadcasts `CONFIG_CHANGED_EVENT` on
   * registration and whenever the layer signals `onDidChange`.
   */
  registerScopeLayer(layer: ScopeLayer): () => void;
  /**
   * Contribute a read-only source of app links (见 21), e.g. the set an
   * organisation administrator pushes. Returns a disposer — push it onto
   * `subscriptions` so it is released on deactivate.
   *
   * In-process only (like `gate` / `overlay` / `registerUrlHandler`): a provider
   * is a bag of functions, and the platform stays unaware of where the links
   * came from.
   */
  registerAppLinkProvider(provider: AppLinkProvider): () => void;
  /**
   * Contribute an arbitrary launchable source (见 21), e.g. a future app-center
   * catalogue. Returns a disposer — push it onto `subscriptions`.
   *
   * In-process only (like `registerAppLinkProvider`).
   */
  registerLaunchItemProvider(provider: LaunchItemProvider): () => void;
  /**
   * Supply a dynamic badge for one of this module's manifest activityBar items.
   * Business data remains module-owned; the platform renders and broadcasts it.
   * Returns a disposer — push it onto `subscriptions`.
   */
  registerLaunchBadgeProvider(
    itemId: string,
    provider: LaunchBadgeProvider,
  ): () => void;
  /**
   * Contribute a rail-layout preset layer (见 21), e.g. a role or space preset.
   * Always merges **below** the user layer — a preset supplies initial values,
   * and `locked` is the only thing it can force.
   */
  registerRailPresetLayer(layer: RailPresetLayer): () => void;
  /**
   * Publish a `when` context key so other modules' manifests can gate on this
   * module's business state (见 03 §5.9) — e.g. org-menu publishing
   * `org-menu.spaceType`, which `org-console` names in its activityBar `when`.
   *
   * Push rather than a provider, unlike the two `register…Layer` methods above:
   * `when` is evaluated synchronously in the renderer, so the host holds a
   * snapshot and broadcasts it. See {@link WhenContextApi.setContextKey} for the
   * namespace rules; keys are dropped automatically on deactivate.
   */
  setContextKey: WhenContextApi['setContextKey'];
  /**
   * Contribute this module's segment of the workbench partition key (见 03).
   *
   * The host joins every present segment into one opaque string and broadcasts
   * `PARTITION_CHANGED_EVENT`. It does not interpret the value. One module has
   * one segment: registering again replaces the previous one. Returns a
   * disposer; deactivation drops the segment either way.
   *
   * In-process only, like {@link registerScopeLayer}: `read()` is synchronous.
   * Nothing in the workbench reads the key yet.
   */
  registerPartitionSegment(segment: PartitionSegment): () => void;
  /**
   * Observe effective configuration changes in-process (架构方案 §5.12). Lets a
   * module react to value changes (the renderer-side counterpart is the
   * `CONFIG_CHANGED_EVENT` broadcast). Returns a disposer.
   */
  onDidChangeConfiguration(
    listener: (change: ConfigChange) => void,
  ): () => void;
  /**
   * Subscribe to a platform EventBus event by name (架构方案 §5.15). Lets a
   * service module react to events emitted by other modules or by a sandboxed
   * caller's reverse `emit`. Ungated: in-process `source` modules are trusted
   * (§5.6 信任模型), so any event is receivable without declaration — the
   * protected-event ACL ({@link canReceiveEvent}) is enforced at the sandboxed
   * EventBus boundary (iframe/external) only. Push the returned disposer onto
   * `subscriptions` so it is released on deactivate.
   */
  onEvent(event: string, handler: (data: unknown) => void): () => void;
  /**
   * Request a capability (架构方案 §5.6). In-process `source` modules are
   * trusted (§5.6 信任模型), so this always resolves `true` — the permission
   * control flow (declare + user grant) only gates sandboxed surfaces at their
   * gated connections. Kept on the contract so module code can stay
   * permission-aware without branching on where it happens to run.
   */
  requestPermission(permission: Permission): Promise<boolean>;
  /**
   * Synchronously check a permission (架构方案 §5.6). Always `true` for
   * in-process `source` modules (trusted, see {@link requestPermission}).
   */
  hasPermission(permission: Permission): boolean;
  /**
   * Platform system capabilities (architecture §5.6/§5.15). For in-process
   * `source` modules (trusted, §5.6 信任模型) calls run directly without a
   * permission gate; the auto-authorizing variant (prompt on first use,
   * `CapabilityDeniedError` when denied) applies to sandboxed callers reaching
   * the same facade over their gated connection.
   */
  readonly capabilities: PlatformCapabilities;
  /**
   * Structured logger for this module (见 15 / §5.16). Records are
   * scoped to this module id and forwarded to the platform's central log sink
   * (main process), where they appear in the unified diagnostics view alongside
   * platform and crash records. Prefer this over `console.*` so module
   * diagnostics are aggregated and survive across processes.
   */
  readonly log: ModuleLogger;
  /**
   * This module's isolated key-value storage (架构方案 §5.13). Namespaced per
   * module; another module's data is never visible here.
   */
  readonly storage: ModuleStorage;
  /**
   * Identifier of the filesystem root this module owns (架构方案 §5.13). The `fs`
   * capability (`capabilities.readFile`/`writeFile`/`deleteFile`) is confined to
   * it, and those calls take **module-relative** paths — they never need this
   * value.
   *
   * **Opaque on every runtime, and never a path** (见 18): the shape follows the
   * backend (`nodefs:/…` on desktop, `opfs:/…` in the browser) precisely so that
   * joining onto it, `dirname`ing it, or handing it to a child process produces
   * something visibly broken instead of a plausible-looking fake path. The only
   * guarantee is presence: **empty means no filesystem root** (in-memory host),
   * in which case fs calls fail closed.
   *
   * Use it to key or label a module's own data, never to address a file. Reading
   * and writing goes through the fs capability with module-relative paths; a real
   * OS path — for something outside the platform, like a native child process —
   * comes from {@link storageNativePath}.
   */
  readonly storageUri: string;
  /**
   * The module filesystem root in the path currency an in-process engine on
   * this runtime understands (见 18 / 19):
   * - desktop: the real OS path (`<userData>/hibro-modules/<id>`), same string
   *   as {@link storageNativePath}
   * - web: OPFS-root-relative `hibro-modules/<id>`
   * - no filesystem: `''` (same sentinel as {@link storageUri})
   *
   * This is the question a module with its own engine actually asks — "where do
   * I tell the engine to put files so they sit in the same tree as the fs
   * capability and `@storage`". Hand it to `root_path`. Do **not** reach for
   * {@link storageNativePath} for that job: that field is absent in the browser
   * on purpose, and folding the empty value into `''` is how an engine ends up
   * writing at the OPFS root.
   *
   * Keep using {@link storageNativePath} only when the path must leave the
   * platform (Node `fs`, an on-disk migration). Sandbox / iframe hosts do not
   * expose this field (same policy as `storageNativePath`): those modules use
   * the fs capability.
   */
  readonly storageFileRoot: string;
  /**
   * The module's filesystem root as a real OS path, on runtimes that have one
   * (见 18). `undefined` in the browser (no OS paths exist there) and in an
   * in-memory host.
   *
   * **Reading it commits the module to desktop**, so it must say so via
   * `ModuleManifest.runtimes`. It exists for the narrow set of cases where a path
   * genuinely has to leave the platform — Node `fs`, an on-disk migration —
   * and deliberately *not* as a general-purpose alternative to the fs
   * capability, which works on both runtimes. An engine that should also run
   * in the browser reads {@link storageFileRoot} instead.
   *
   * Branching on presence rather than on a runtime name is the same degradation
   * shape the renderer bridges use (见 19 手法三): the question a module actually
   * has is "is there an OS path here", not "am I on desktop".
   */
  readonly storageNativePath?: string;
  /**
   * The user's OS "Downloads" folder, on runtimes that have one (见 18 / 19).
   * `undefined` in the browser and in a host with no OS paths — read it by
   * branching on presence, the same way {@link storageNativePath} is read
   * (手法三), and declare `runtimes` accordingly.
   *
   * This is a destination *outside* every module root, so it is deliberately
   * narrow. The platform's answer to "hand this file to the user" remains
   * {@link PlatformCapabilities.saveStorageFile} — a save dialog on the desktop,
   * a browser download on the web — and that stays the answer whenever the bytes
   * are already on disk. This exists for the case where they are not yet: a
   * native engine that writes the file itself has to be told the destination
   * *before* it downloads, and routing it through the module root only to copy
   * it outward afterwards would move every byte twice.
   *
   * **Never guess it as `~/Downloads`.** On Windows it is a Known Folder the user
   * may have redirected to another drive, and only the main process can resolve
   * it; that is the whole reason the platform carries it here.
   */
  readonly downloadsPath?: string;
  /**
   * Absolute URL a media element (or `fetch`) can use to read a file under this
   * module's storage root (见 12 / 18): desktop
   * `hibro-assets://<id>/@storage/<rel>`, web `<origin>/_mod/<id>/@storage/<rel>`.
   * Built via {@link buildModuleStorageUrl} with the host's `assetBase`, so the
   * relative path shape is identical on both ends.
   *
   * **Async on purpose**: today's hosts answer synchronously, but a future page-side
   * `blob:` fallback (if a Service Worker cannot serve) must mint the URL from
   * bytes — a sync signature would make that a breaking change. Fail closed with
   * `undefined` for empty / traversing paths (same rules as
   * {@link buildModuleStorageUrl}).
   *
   * Not on `context.storage` (that is the KV domain) and not a capability (no
   * side effect, no permission gate). Named `storageUrl` rather than
   * `storageFileUrl` to stay short next to {@link ModuleContext.storageUri}.
   */
  storageUrl(relativePath: string): Promise<string | undefined>;
  /**
   * Absolute URL for a file this module **ships** — its code/asset root, as
   * opposed to {@link storageUrl}'s data root (见 12 / 18): desktop
   * `hibro-assets://<id>/<rel>`, web `<origin>/_mod/<id>/<rel>`.
   *
   * The service-side outlet of {@link resolveViewUrl}, which until now only ran
   * at the contributions boundary (view `url`s, `entry.ui`, icons). Without it a
   * module that needs to *fetch* one of its own shipped files — a wasm engine, a
   * nested worker script, a data table — has to hand-write `hibro-assets://…` or
   * `/_mod/…`, which is exactly the per-runtime scheme knowledge `assetBase`
   * exists to keep out of modules (见 18).
   *
   * **Async for the same reason as {@link storageUrl}**: a delivered module's
   * assets live in the browser's OPFS, and serving them may have to fall back to
   * minting a `blob:` from bytes. A sync signature would make that a breaking
   * change later.
   *
   * Takes a **module-relative path only**. Unlike `resolveViewUrl` — which passes
   * anything carrying a scheme straight through, because a view may legitimately
   * declare `https://…` — this rejects them: a module asking for "my own file"
   * and getting back an arbitrary URL it passed in is a footgun, not a feature.
   * Traversing/absolute paths fail closed with `undefined`.
   *
   * **Path segments are not percent-encoded** (this inherits `resolveViewUrl`;
   * {@link buildModuleStorageUrl} does encode). The asymmetry is deliberate: the
   * code root holds *build outputs* whose names the module author controls, while
   * the storage root holds *runtime* filenames the module never chose. A shipped
   * asset named with a space or `#` will therefore produce a broken URL — name
   * build outputs conservatively.
   */
  assetUrl(relativePath: string): Promise<string | undefined>;
  /**
   * Absolute URL a media element (or `fetch`) can use to read a picture or video
   * that lies **outside every module root** (见 12 / §5.13):
   * `hibro-assets://<id>/@local/<token>`.
   *
   * The two siblings above address files the platform placed ({@link storageUrl}
   * the data root, {@link assetUrl} the code root). This one exists because
   * zero-copy send leaves a file where the *user* put it: the service looks the
   * path up from a `file_id`, then asks here so the bubble can render without
   * copying the file inward.
   *
   * **The renderer never sees the path, and cannot mint a URL for one.** The
   * main process swaps the path for an unguessable token (bound to this
   * moduleId) and keeps the mapping in memory. Serving still refuses anything
   * whose extension is not an image or video (`isServableLocalMedia`).
   *
   * `undefined` when this runtime has no OS paths
   * ({@link RuntimeCapabilityProfile.nativeFilePath}: always so in a browser),
   * when the path is relative / carries `..`, or when the grant is refused.
   * Callers must have a non-path route to the same pixels for that case.
   *
   * Async because minting the token is a round-trip to the process that serves
   * the scheme.
   */
  localFileUrl(absolutePath: string): Promise<string | undefined>;
  /**
   * Copy an image at an absolute OS path onto the system clipboard (desktop).
   * Absent on runtimes with no OS paths — feature-detect with
   * `'writeImagePath' in context`.
   *
   * The path stays in the service process: a view that needs "copy this image"
   * asks its own command with a `file_id`, and that command looks the path up
   * then calls this. Backed by `nativeImage.createFromPath` in the main process.
   */
  writeImagePath?(absolutePath: string): Promise<boolean>;
  /**
   * Which runtime this module is running on (见 18 / 19).
   *
   * **Ask it what a runtime can do, not which one it is**: pair it with
   * {@link runtimeSupports} (`runtimeSupports(context.runtime, 'nativeFileManager')`)
   * rather than writing `context.runtime === 'web'`. The comparison states *which
   * runtime is special* instead of *what the code needs*, so it goes stale the
   * moment a third runtime appears — the exact failure {@link RuntimeCapabilityProfile}
   * exists to prevent.
   *
   * This is not the sniffing `19` forbids. That rule targets guessing the
   * environment from its debris (`typeof process`, a UA string); the runtime is a
   * declared fact of this host, and reading it here is how a module avoids
   * guessing.
   *
   * **Inline views do not see this on `ViewProps`.** When a process-in UI needs
   * a runtime answer, the module exposes it as its own command or API — the
   * shape `account.getLoginMethods()` uses so its login UI stays one codebase
   * across runtimes (见 18). Sandboxed iframe / external pages read the same
   * host-declared value from {@link PlatformHandshake.runtime}.
   */
  readonly runtime: ModuleRuntime;
  /**
   * Which OS the user is on (见 18 / 19): `'darwin' | 'win32' | 'linux'`.
   *
   * Answers "which OS-flavored UI / which native package", **not** "can I
   * spawn / self-update / draw window chrome". Those are capabilities
   * (`runtimeSupports`, `'writeImagePath' in context`, update driver
   * booleans). A browser on macOS is still `'darwin'`.
   *
   * Inline views read the same fact via {@link readHostOs} (`hibroHost.os`);
   * sandboxed pages read {@link PlatformHandshake.os}.
   */
  readonly os: HostOs;
  /**
   * Run a worker this module ships, somewhere it gets its own event loop
   * (见 18 / 19). **Absent unless the runtime needs the platform's help**, which
   * today means: present on web, absent on the desktop.
   *
   * The asymmetry is not an omission. On the desktop a module already spawns its
   * own child process or `worker_threads` from the extension host — the platform
   * has nothing to add. In a browser the host is a SharedWorker, whose global has
   * **neither `Worker` nor `SharedWorker`**, so it cannot construct one at all;
   * only a page can, and only the host knows which pages exist. Hence a relay,
   * the same shape as `openExternal` / `saveStorageFile` / `window.open`.
   *
   * Read it the way {@link storageNativePath} is read — **branch on whether it is
   * there**, not on which runtime you are (手法三 / 见 19). A module that needs a
   * worker on one runtime and a child process on the other ships both in its
   * per-runtime entries and never compares runtime names.
   */
  readonly worker?: ModuleWorkerHost;
  /**
   * Offline capability for this module (架构方案 §5.14): network-status
   * awareness plus a persisted operation queue that drains when connectivity
   * returns.
   */
  readonly offline: ModuleOffline;
  /**
   * Disposers to run when the module is deactivated (架构方案 §5.4, VSCode-style
   * `context.subscriptions`). Push anything that must be released on teardown —
   * e.g. the disposer returned by `onDidChangeConfiguration`, timers, listeners.
   * Platform-owned registrations (contributed commands, exported APIs) are
   * reclaimed by the host automatically and need not be pushed here.
   */
  readonly subscriptions: Disposer[];
}

export type ActivateFn = (context: ModuleContext) => void | Promise<void>;

/**
 * Module teardown hook (架构方案 §5.4). Invoked when the module is deactivated
 * (its last contributed view became invisible). Release any resources not in
 * `context.subscriptions`; the host then disposes `subscriptions` and reclaims
 * contributed commands / exported APIs so a later activation starts clean.
 */
export type DeactivateFn = (context: ModuleContext) => void | Promise<void>;

export interface ModuleExports {
  activate?: ActivateFn;
  deactivate?: DeactivateFn;
}

/**
 * External-process control handed to a renderer view (架构方案 §3.1/§5.8), so a
 * module can paint its **own** control panel instead of the platform's default
 * `kind: 'external'` one — e.g. an inline landing page that only spawns the
 * process when the user presses a button.
 *
 * Scoped to the calling module by construction: every method takes an
 * `entry.external` **entry id**, never a process key, and the renderer host
 * composes the key from the hosting view's `moduleId`
 * ({@link externalProcessKey}) — so a module cannot address another module's
 * process. Omit `entryId` for a single-entry module (its key is the bare module
 * id). An unknown entry id resolves to no launch spec and simply reports a
 * not-running status.
 *
 * Renderer views only. A sandboxed iframe/external face has no equivalent: it
 * would cross the trust boundary, so it stays fail-closed.
 */
export interface ViewExternalApi {
  /**
   * Spawn the process (idempotent — a running one is left alone) and return its
   * status. On a two-phase module this also fetches the lazy `dist/` first (见 12).
   */
  launch(entryId?: string): Promise<ExternalStatus>;
  /** Bring the external app's window to the foreground (best-effort, §5.8). */
  focus(entryId?: string): Promise<void>;
  /** Gracefully shut the process down and stop supervising it. */
  close(entryId?: string): Promise<void>;
  /** Read the current lifecycle snapshot (running/pid + last reverse call). */
  status(entryId?: string): Promise<ExternalStatus>;
  /**
   * Re-read the status whenever the platform's process table changes (a process
   * started or exited), so a custom panel can reflect run state without polling.
   * Returns a disposer; a no-op where no external bridge is wired (e.g. web build).
   */
  onDidChangeStatus(
    listener: (status: ExternalStatus) => void,
    entryId?: string,
  ): Disposer;
}

/**
 * Renderer-side UI activation contract (架构方案 §5.7/§5.8). Source modules ship
 * a separate UI entry that runs in the renderer process (distinct from the
 * service entry that runs in the extension host). The view component type is
 * kept React-agnostic (`(props) => unknown`) so this contract stays in the
 * platform-free `shared` layer while remaining structurally compatible with a
 * React function component; the renderer casts it back when mounting.
 */
/** Presentation data supplied only when a view is hosted in the activityBar. */
export interface ActivityBarHostPresentation {
  readonly kind: 'activityBar';
  readonly expanded: boolean;
  readonly title: string;
  readonly showExpandedTitle: boolean;
}

export type ViewHostPresentation = ActivityBarHostPresentation;

export interface ViewProps {
  moduleId: string;
  viewId: string;
  /**
   * Optional host-owned presentation context. Custom activityBar views use this
   * to place the expanded title inside their own interactive trigger.
   */
  host?: ViewHostPresentation;
  /** Invoke a contributed command (routes to the extension host over RPC). */
  executeCommand: (id: string, ...args: unknown[]) => Promise<unknown>;
  /**
   * Gesture-bound renderer actions. `openBrowser` must be called directly from
   * a trusted source UI event handler so Web can synchronously pre-open its
   * browser shell; service/iframe/external callers use `capabilities.openBrowser`.
   */
  userActivation: UserActivationApi;
  /**
   * Open an http(s) link wherever it belongs (见 20 外链归属路由) — the module
   * owning that address space, else the built-in browser. Resolves whether a
   * module took it, so a view can tell the user when nothing did.
   *
   * This is what a view renders links through instead of `<a target="_blank">`:
   * an anchor leaves the app (or, on the desktop, opens a bare window), and a
   * view has no business knowing which module owns a given address. Keep the
   * `href` for copy/middle-click affordances and `preventDefault()` the click.
   */
  openLink: (url: string) => Promise<boolean>;
  /**
   * Ask which module claims an http(s) address (见 20 外链归属路由), without
   * presenting anything; `null` when nobody claims it or the claim is contested.
   *
   * For views that must reach the owning module *before* a click — rendering a
   * link's card from its owner's data, say. Routing a click the user already
   * made stays {@link openLink}: it activates and delivers, this only answers.
   */
  resolveUrlOwner: (url: string) => Promise<string | null>;
  /**
   * Replace this document with `url` (见 18 / 20). Web OAuth has to leave the
   * current tab so the IdP can return to `redirect_uri` on the same browsing
   * context; `openLink` would present the address without leaving, and
   * `openExternal` would open a new tab. The navigation guard lets this one hop
   * through and keeps blocking everything else.
   *
   * Desktop login does not use this — it opens the system browser.
   */
  navigateAway: (url: string) => void;
  /**
   * Translate one of this module's own message keys (见 14 / §5.11),
   * formatted as ICU against the active locale. The key is the module's bare key
   * (e.g. `panel.title`); the host scopes it to this module's catalog
   * (`<moduleId>:<key>`) and re-renders the view on locale changes. Messages come
   * from the module's `nls/<locale>.json`; a missing key degrades to the key.
   */
  t: (key: string, args?: Record<string, unknown>) => string;
  /**
   * The active BCP 47 locale {@link t} formats against (e.g. `zh-CN`). Views
   * that format dates, numbers or region names with `Intl` pass it through so
   * they follow the user's language rather than the OS locale.
   */
  locale: string;
  /**
   * Resolve a contribution field written as `%key%` against the contributor's
   * catalog (`<moduleId>:<key>`, 见 14 / §5.11). A plain string is returned
   * unchanged. A missing translation degrades to the inner key name. Contribution
   * point owners use this for another module's manifest data; {@link t} only
   * sees this view's own catalog.
   */
  title: (moduleId: string, raw: string) => string;
  /**
   * The latest navigation location targeting this view (架构方案 §5.8 跳转, doc
   * 44), or `undefined` if it has not been navigated. An opaque, module-defined
   * payload (the platform routes but never interprets it); the view reads it to
   * position itself (e.g. select a conversation, scroll to an anchor). Updated on
   * each `navigate(view:<viewId>, …)`; the renderer re-renders the view, and a
   * location that arrived before the view mounted is delivered on mount.
   */
  location?: unknown;
  /**
   * Detach this view — or a fragment of it — into its own window (架构方案 §5.8
   * 通用 Detachable). Platform capability injected by the renderer (like
   * `executeCommand`); omitting `fragmentId` detaches the whole view (`migrate`).
   * A fragment's render source is registered on the renderer side via
   * `registerDetachable(fragmentId, …)`.
   */
  detach: (options?: ViewDetachOptions) => Promise<void>;
  /** Attach a detached surface of this view back (close its window) (§5.8). */
  attach: (handle: DetachHandle) => Promise<void>;
  /** Focus the window hosting a detached surface of this view (§5.8). */
  focusDetached: (handle: DetachHandle) => Promise<void>;
  /**
   * Surfaces of this view currently detached into their own windows, with their
   * `mode` (§5.8). Re-rendered on change. For a **fragment** the platform cannot
   * know where the region sits inline, so the owning view renders its own origin
   * behaviour from this — typically: hide the region and show a placeholder when
   * a `migrate` fragment is present, leave it untouched for `mirror`. (Whole-view
   * `migrate` placeholder is handled by the platform's editor tab.)
   */
  detached: DetachedSurfaceInfo[];
  /**
   * Renderer-side consumer of module-defined contribution points (见 17). Lets
   * this view — as a point's owner — enumerate declared contributions, subscribe
   * to changes, and lazily resolve a contributor's renderer-side value (e.g. a
   * React component). Injected by the platform.
   */
  readonly contributions: ContributionConsumer;
  /**
   * Read and write **this module's own** declared settings (见 05 / 23), so a
   * module can carry its own settings page rather than contributing rows to the
   * platform's settings panel.
   *
   * The pair to `contributes.settings[].ownedUi`: that flag takes a key out of the
   * generic list, this is how the module then renders it. Values still live in the
   * configuration service, so scopes, type validation, reset and the
   * cross-window `CONFIG_CHANGED_EVENT` all keep working — keeping a module's user
   * settings in `context.storage` instead would give up every one of those.
   *
   * Scoped by the platform, not by the caller: the module id comes from this
   * view's own registration (the same path `t` is scoped through) and the
   * "declared by that module" check runs in the extension host. Asking for
   * someone else's key rejects.
   */
  readonly settings: ViewSettingsApi;
  /**
   * Open a platform overlay (架构方案 §5.8 OverlaySurface) from a view: same
   * openers as the service-side `context.overlay`, resolved in-process (no
   * round-trip). When this view is itself hosted inside an overlay, `resolve` /
   * `dismiss` are present so it can close itself with (or without) a result.
   */
  readonly overlay: ViewOverlayApi;
  /**
   * Drive this module's own `entry.external` processes (架构方案 §3.1/§5.8) —
   * launch / focus / close / status — so any view can carry its own control UI
   * instead of the platform's default `external` panel. Scoped to the calling
   * module; see {@link ViewExternalApi}.
   */
  readonly external: ViewExternalApi;
  /**
   * Opaque params handed to this view when it is hosted inside an OverlaySurface
   * (架构方案 §5.8) — the `params` passed to `openModal`/`openDrawer`. `undefined`
   * for a normally-docked view. Module-defined shape; the platform only routes it.
   */
  readonly params?: unknown;
  /**
   * Instance discriminator when this view is opened as one of several instances
   * of the same `viewId` (架构方案 §5.7 视图多实例) — e.g. a chat `sessionId`.
   * `undefined` for a singleton view. The platform routes it as identity only;
   * the view resolves its live state from the owning service by this key.
   */
  readonly instanceKey?: string;
  /**
   * Which window this rendering is mounted in (见 8). `workbench` is the main
   * window; `detached` is a detached surface, its web inline stand-in, or a
   * module shell. Distinct from {@link detached}, which lists surfaces popped
   * out of the origin view. Fixed at mount. The view decides what a click does
   * with it; the platform does not.
   */
  readonly hostedIn: SurfaceHostedIn;
  /**
   * Whether this view is currently visible to the user (架构方案 §5.7 视图缓存).
   * `undefined`/`true` = visible; `false` = the view is mounted but hidden as a
   * kept-alive editor cache entry (its React tree and DOM are preserved). A view
   * with periodic work (polling, animation) should pause it while hidden. Not a
   * lifecycle signal: activation/deactivation still follow mount/unmount (§5.4).
   */
  readonly visible?: boolean;
  /**
   * Generation of the latest activity-bar click that opened this view while it
   * was not already the active one (见 21). `0` or an unchanged value means
   * this show was not that click. Re-clicking an already-active item does not
   * bump it; that gesture is `registerRailActivation`.
   */
  readonly activityBarOpenGeneration?: number;
  /**
   * Subscribe to a platform EventBus broadcast by name (架构方案 §5.2 listen /
   * §5.15) — the general publish/subscribe counterpart of the request/response
   * {@link ViewProps.executeCommand}, mirroring the ext-host
   * `ModuleContext.onEvent`. The handler receives the structured-cloned payload
   * of a `context.emitEvent(name, payload)` raised by any module; returns a
   * disposer the view MUST call on cleanup. Use it to reactively update from
   * host-pushed events (e.g. a message-arrived notify) instead of polling. The
   * subscription is re-bound automatically if the host channel reconnects.
   * Receipt policy follows {@link canReceiveEvent}: own-namespace and
   * non-protected events are receivable; the protected-event declaration gate
   * is enforced at the sandboxed EventBus boundary (iframe/external) — a
   * renderer view is trusted in-process source code (§5.6 信任模型).
   *
   * Declared property-style (not a method) so destructuring in a view does not
   * trip `@typescript-eslint/unbound-method` — it is a standalone closure with
   * no `this`.
   */
  onEvent: (event: string, handler: (data: unknown) => void) => Disposer;
}

/**
 * Renderer-side consumer of module-defined contribution points (见 17), handed
 * to an owner view via {@link ViewProps.contributions}. The counterpart of the
 * ext-host {@link ContributionRegistryFacade}, but consumer-only and split by
 * where each half lives: declared metadata comes over RPC (serializable), while
 * `resolveContribution` returns a renderer-side value (React component) bound by
 * the contributor's *UI* module — it never crosses a process boundary. To invoke
 * a contribution's ext-host behavior instead, use its entry `command` via
 * {@link ViewProps.executeCommand}.
 */
export interface ContributionConsumer {
  /**
   * Declared contributions to `point` across modules (metadata only, over RPC;
   * does not activate contributors). Mirrors the ext-host aggregation.
   */
  getContributions(point: string): Promise<ResolvedContribution[]>;
  /**
   * Curations declared against `point`, from manifests only. Does not activate
   * modules and does not filter {@link getContributions}. The owner passes a
   * scope id to `applyContributionCuration` after it has merged its own items.
   */
  getCurations(point: string): Promise<ResolvedContributionCuration[]>;
  /**
   * Lazily activate the entry's contributor *UI* module, then return the
   * renderer-side value it registered via
   * {@link ModuleUiContext.registerContribution} (e.g. a React component).
   * Resolves `undefined` when nothing was bound. Takes the whole entry (it
   * carries `moduleId` + `key`, avoiding key parsing).
   */
  resolveContribution(entry: ResolvedContribution): Promise<unknown>;
  /**
   * Observe contribution changes for `point`; fires when a contributing module
   * is (un)registered so the owner can re-read {@link getContributions}. Returns
   * a disposer. A no-op when the platform did not wire a change source.
   */
  onDidChangeContributions(point: string, listener: () => void): Disposer;
}

export type ViewComponent = (props: ViewProps) => unknown;

/**
 * {@link ViewOverlayApi} 在「本视图确实被 overlay 承载」前提下的收窄：`resolve` /
 * `dismiss` 必有（由 `OverlayHost` 经 `overlayControl` 注入）。
 */
export interface HostedOverlayApi extends ViewOverlayApi {
  /** 以结果关闭承载本视图的 overlay。 */
  resolve: (result?: OverlayResult) => void;
  /** 无结果关闭承载本视图的 overlay。 */
  dismiss: () => void;
  /** 这次承载是否允许点外面 / Esc 关掉。popover 由视图自己的浮层遵守。 */
  dismissable: boolean;
}

/**
 * `area: 'overlay'` 视图的 props（架构方案 §5.8）。overlay 视图与普通视图共用
 * {@link ModuleUiContext.registerView} 原语，但交互契约不同：它是一次「取值—回传—关闭」
 * 的会话，入参走 {@link params}、出参走 {@link overlay}.resolve/dismiss，因此这里
 *
 * - 把 `params` 与 `overlay.resolve`/`overlay.dismiss`/`overlay.dismissable` 收为必填
 *   （`OverlayHost` 承载时恒注入），作者漏实现关闭路径会被类型拦住，不必写
 *   `overlay.resolve?.()`；
 * - 去掉 overlay 里无意义的位置/游离/缓存字段（`location`、`detach`/`attach`/
 *   `focusDetached`/`detached`、`instanceKey`、`visible`）——modal/drawer 由
 *   `OverlayHost` 独占承载，不进 editor 缓存也不参与游离窗口。
 *
 * 纯类型收窄、运行期与 {@link ViewProps} 同一份对象；`params` 值仍可能是 `undefined`
 * （调用方未传），只保证键存在。
 */
export interface OverlayViewProps extends Omit<
  ViewProps,
  | 'overlay'
  | 'params'
  | 'location'
  | 'detach'
  | 'attach'
  | 'focusDetached'
  | 'detached'
  | 'instanceKey'
  | 'visible'
> {
  /** 自关闭能力必有；同时仍可再打开嵌套 overlay。 */
  readonly overlay: HostedOverlayApi;
  /** `openModal`/`openDrawer` 传入的不透明入参（值可能为 `undefined`）。 */
  readonly params: unknown;
}

/**
 * overlay 视图组件类型（{@link ViewComponent} 的 overlay 收窄版）。作者可用
 * `satisfies OverlayViewComponent` 自检，仍经 `registerView` 注册。
 */
export type OverlayViewComponent = (props: OverlayViewProps) => unknown;

/**
 * Props passed to a detachable fragment's render source (架构方案 §5.8 通用
 * Detachable). The fragment renders the same in the inline region and in the
 * detached window; the platform only carries the `instanceKey`/`payload`
 * *identity* (not serialized state) — a `source` module's ext-host service is a
 * single instance, so both projections resolve the same live state from the key.
 */
export interface DetachableProps {
  moduleId: string;
  /** The owning view of this fragment. */
  viewId: string;
  /** The fragment id (as registered via `registerDetachable`). */
  fragmentId: string;
  /** Instance discriminator for multi-window (e.g. a conversation id); undefined for singletons. */
  instanceKey?: string;
  /** Opaque, module-defined identity for what to render (e.g. `{ conversationId }`). */
  payload?: unknown;
  /** Invoke a contributed command (routes to the extension host over RPC). */
  executeCommand: (id: string, ...args: unknown[]) => Promise<unknown>;
  /**
   * Open an http(s) link wherever it belongs (见 20 外链归属路由). Same
   * capability as {@link ViewProps.openLink}.
   */
  openLink: (url: string) => Promise<boolean>;
  /** Translate one of this module's own message keys (见 14 / §5.11). */
  t: (key: string, args?: Record<string, unknown>) => string;
  /**
   * Detach another fragment of the owning view (架构方案 §5.8), e.g. open a
   * further per-instance window from within a detached fragment. Same capability
   * as {@link ViewProps.detach}.
   */
  detach: (options?: ViewDetachOptions) => Promise<void>;
  /** Attach a detached surface of the owning view back (§5.8). */
  attach: (handle: DetachHandle) => Promise<void>;
  /** Focus the window hosting a detached surface of the owning view (§5.8). */
  focusDetached: (handle: DetachHandle) => Promise<void>;
  /** Surfaces of the owning view currently detached, with their `mode` (§5.8). */
  detached: DetachedSurfaceInfo[];
  /**
   * Which window this fragment rendering is mounted in (见 8). Same meaning as
   * {@link ViewProps.hostedIn}. A fragment mounts only inside a detached
   * window, so this is `detached` there.
   */
  hostedIn: SurfaceHostedIn;
  /**
   * OS 窗口控件（仅裸壳游离窗注入）。自绘标题栏/关闭按钮时使用；有壳窗走
   * `CustomHeader`，此字段为 `undefined`。
   */
  windowControls?: WindowControlsApi;
}

export type DetachableRenderer = (props: DetachableProps) => unknown;

/**
 * What the owner of an activityBar item runs when that item is activated again
 * (见 21). `itemId` is passed because the unit is an *item*, not a module: a
 * module contributing several rail entries would otherwise need one closure per
 * entry.
 *
 * Returns nothing on purpose. The platform has nothing it could do with a
 * result — there is no action to fall back to and no failure to surface — and
 * giving the return value meaning would invite policy into a dispatcher.
 */
export type RailActivationHandler = (args: { itemId: string }) => void;

export interface ModuleUiContext {
  /**
   * Register the component that renders a contributed `views` entry.
   *
   * An `area: 'overlay'` view may declare the narrowed {@link OverlayViewProps}
   * instead: the host always passes full {@link ViewProps}, so accepting the
   * narrower shape is sound — the union is only needed because a narrower
   * parameter makes the two function types mutually unassignable.
   */
  registerView(
    viewId: string,
    component: ViewComponent | OverlayViewComponent,
  ): void;
  /**
   * Register the renderer-side controller for a view hosted in a native tabbed
   * area. The controller owns domain policy; the platform only supplies
   * {@link TabHostApi} operations.
   */
  registerTabController(viewId: string, controller: TabHostController): void;
  /**
   * Register optional custom tab/header-actions chrome for a native tabbed
   * view. Values stay renderer-local and are never serialized.
   */
  registerViewTabChrome(viewId: string, chrome: ViewTabChrome): void;
  /**
   * Register the render source for a detachable fragment (架构方案 §5.8 通用
   * Detachable). Reused by both the inline region and the detached window; keyed
   * by an arbitrary module-defined fragment id.
   */
  registerDetachable(fragmentId: string, renderer: DetachableRenderer): void;
  /**
   * Bind this module's renderer-side value (e.g. a React component) for a
   * contribution it declared to `point` (见 17). The owner of `point` resolves
   * it via {@link ContributionConsumer.resolveContribution}. The value stays in
   * the renderer process (never serialized); the module must also declare the
   * matching `contributions` entry in its manifest so it is discoverable.
   */
  registerContribution(point: string, id: string, value: unknown): void;
  /**
   * Handle re-activation of one of this module's own activityBar items (见 21):
   * the platform invokes `handler` when the user clicks an item that is already
   * active. The item's declared `action` still runs — this is additive, so the
   * rail's click contract is unchanged.
   *
   * Renderer-local like the rest of this family, which is the point: the state
   * such a gesture acts on (a list's scroll position, a "where did I get to"
   * cursor) lives in the mounted view, and routing the click out to the host and
   * back would put a process boundary around a scroll.
   *
   * The only member of this family returning a {@link Disposer}, because it is
   * the only one a *component* registers: bind it on mount and release it on
   * unmount, so "a handler exists" stays synonymous with "the view is mounted"
   * and the platform never dispatches into a view that is not there.
   */
  registerRailActivation(
    itemId: string,
    handler: RailActivationHandler,
  ): Disposer;
}

export type UiActivateFn = (context: ModuleUiContext) => void | Promise<void>;

/**
 * Renderer-side teardown hook (架构方案 §5.4/§5.8), the UI counterpart of
 * {@link DeactivateFn}. Invoked when the module's last view becomes invisible;
 * the renderer registry then unregisters the module's view components so a later
 * re-activation re-registers them cleanly.
 */
export type UiDeactivateFn = (context: ModuleUiContext) => void | Promise<void>;

export interface ModuleUiExports {
  activate?: UiActivateFn;
  deactivate?: UiDeactivateFn;
}

const VALID_VIEW_CACHE_POLICIES: readonly ViewCachePolicy[] = [
  'keep-alive',
  'none',
];

const VALID_VIEW_KINDS: readonly ViewRenderKind[] = [
  'inline',
  'iframe',
  'external',
  'remote',
];

/** {@link validateManifest} 的发现期上下文（manifest 本身看不出来的事实）。 */
export interface ValidateManifestOptions {
  /**
   * 该模块是否运行期安装/链接（下发、侧载、dev link），即 {@link ModuleFacets} 的
   * `isFirstParty` 取反。只影响「inline 视图有无 UI 供给」这一条：dynamic 模块的唯一
   * 供给通道是 `entry.ui`，而仓内模块由壳层编译期 glob 供给、manifest 看不到，无法在
   * 纯函数里判定。缺省 `false`（按仓内模块处理，宽松）。
   */
  dynamic?: boolean;
}

function pushSettingProblems(
  problems: string[],
  setting: ModuleSettingContribution,
  views: ViewContribution[] | undefined,
  settings: SettingContribution[] | undefined,
): void {
  const viewIds = new Set(
    (Array.isArray(views) ? views : [])
      .map((view) => view.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0),
  );
  const settingKeys = new Set(
    (Array.isArray(settings) ? settings : [])
      .map((entry) => entry.key)
      .filter(
        (key): key is string => typeof key === 'string' && key.length > 0,
      ),
  );

  const nonEmpty = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

  /**
   * Shared shape check for one `setting.<field>` list: an array of objects
   * with a local id that is unique and has no `.` (the global id is
   * `<moduleId>.<id>`). Returns the entries that passed, for field checks.
   */
  const entries = <T extends { id?: unknown }>(
    field: string,
    list: T[] | undefined,
  ): { id: string; entry: T }[] => {
    if (list === undefined) {
      return [];
    }
    if (!Array.isArray(list)) {
      problems.push(`invalid contributes.setting.${field}: must be an array`);
      return [];
    }
    const seen = new Set<string>();
    const out: { id: string; entry: T }[] = [];
    for (const entry of list) {
      const id = entry?.id;
      if (!nonEmpty(id)) {
        problems.push(`invalid contributes.setting.${field} entry: missing id`);
        continue;
      }
      if (id.includes('.')) {
        problems.push(
          `invalid contributes.setting.${field}["${id}"]: id must not contain "."`,
        );
        continue;
      }
      if (seen.has(id)) {
        problems.push(
          `invalid contributes.setting.${field}: duplicate id "${id}"`,
        );
        continue;
      }
      seen.add(id);
      out.push({ id, entry });
    }
    return out;
  };

  const orderProblem = (field: string, id: string, order: unknown): void => {
    if (
      order !== undefined &&
      (typeof order !== 'number' || !Number.isFinite(order))
    ) {
      problems.push(
        `invalid contributes.setting.${field}["${id}"].order: must be a finite number`,
      );
    }
  };

  for (const { id, entry } of entries('groups', setting.groups)) {
    if (!nonEmpty(entry.title)) {
      problems.push(
        `invalid contributes.setting.groups["${id}"]: missing title`,
      );
    }
    orderProblem('groups', id, entry.order);
  }

  for (const { id, entry } of entries('pages', setting.pages)) {
    if (!nonEmpty(entry.group)) {
      problems.push(
        `invalid contributes.setting.pages["${id}"]: missing group`,
      );
    }
    if (!nonEmpty(entry.title)) {
      problems.push(
        `invalid contributes.setting.pages["${id}"]: missing title`,
      );
    }
    if (entry.icon === undefined || entry.icon === '') {
      problems.push(`invalid contributes.setting.pages["${id}"]: missing icon`);
    }
    orderProblem('pages', id, entry.order);
  }

  for (const { id, entry } of entries('sections', setting.sections)) {
    if (!nonEmpty(entry.page)) {
      problems.push(
        `invalid contributes.setting.sections["${id}"]: missing page`,
      );
    }
    orderProblem('sections', id, entry.order);
    const hasView = entry.viewId !== undefined;
    const hasKeys = entry.keys !== undefined;
    if (hasView === hasKeys) {
      problems.push(
        `invalid contributes.setting.sections["${id}"]: declare exactly one of viewId or keys`,
      );
      continue;
    }
    if (hasView) {
      if (!nonEmpty(entry.viewId) || !viewIds.has(entry.viewId)) {
        problems.push(
          `invalid contributes.setting.sections["${id}"].viewId: view "${String(entry.viewId)}" is not declared by this module`,
        );
      }
      continue;
    }
    if (!Array.isArray(entry.keys) || entry.keys.length === 0) {
      problems.push(
        `invalid contributes.setting.sections["${id}"].keys: must be a non-empty array`,
      );
      continue;
    }
    for (const key of entry.keys) {
      if (typeof key !== 'string' || !settingKeys.has(key)) {
        problems.push(
          `invalid contributes.setting.sections["${id}"].keys: "${String(key)}" is not declared in contributes.settings`,
        );
      }
    }
  }

  for (const { id, entry } of entries('actions', setting.actions)) {
    if (!nonEmpty(entry.group)) {
      problems.push(
        `invalid contributes.setting.actions["${id}"]: missing group`,
      );
    }
    if (!nonEmpty(entry.title)) {
      problems.push(
        `invalid contributes.setting.actions["${id}"]: missing title`,
      );
    }
    if (!nonEmpty(entry.command)) {
      problems.push(
        `invalid contributes.setting.actions["${id}"]: missing command`,
      );
    }
    const confirm = entry.confirm;
    if (
      confirm !== undefined &&
      (typeof confirm !== 'object' ||
        confirm === null ||
        !nonEmpty(confirm.title) ||
        !nonEmpty(confirm.body) ||
        !nonEmpty(confirm.confirm))
    ) {
      problems.push(
        `invalid contributes.setting.actions["${id}"].confirm: title, body and confirm are required`,
      );
    }
    orderProblem('actions', id, entry.order);
  }

  const header = setting.header;
  if (header !== undefined) {
    if (
      typeof header !== 'object' ||
      header === null ||
      !nonEmpty(header.viewId) ||
      !viewIds.has(header.viewId)
    ) {
      problems.push(
        `invalid contributes.setting.header.viewId: view "${String(header?.viewId)}" is not declared by this module`,
      );
    }
  }
}

/**
 * Validate a module manifest against the platform contract (架构方案 §5.3/§5.4).
 * Returns a list of human-readable problems; an empty array means the manifest
 * is compatible. Pure function (no platform dependency) so it can run in any
 * process. The Module Manager skips modules that fail validation.
 */
export function validateManifest(
  manifest: ModuleManifest,
  platformVersion: string = PLATFORM_VERSION,
  protocolVersion: string = PROTOCOL_VERSION,
  options: ValidateManifestOptions = {},
): string[] {
  const problems: string[] = [];

  if (!manifest.id || typeof manifest.id !== 'string') {
    problems.push('missing required field: id');
  } else if (isReservedModuleId(manifest.id)) {
    // The module id *is* the deep-link address and the asset URL authority
    // (见 20), and the platform reserves the `_` prefix for its own paths
    // (`/_mod/`, `hibro-assets://_runtime/`). Rejecting here is what makes that
    // a closed rule rather than a reserved-word list that grows.
    problems.push(
      `invalid id "${manifest.id}": must not start with "${PLATFORM_PATH_PREFIX}" (reserved for platform paths)`,
    );
  } else if (manifest.id === HOST_MODULE_ID) {
    problems.push(
      `invalid id "${manifest.id}": reserved for host-directed events`,
    );
  } else if (isUnsafeModuleId(manifest.id)) {
    // The id is also a directory name under hibro-modules/ and a URL authority
    // (见 07 / 20). `.` / `..` / separators / drive letters / C0 would let
    // path.join escape the storage root — `removeRoot('..')` would rm userData.
    problems.push(
      `invalid id "${manifest.id}": must not be "." or ".." or contain path separators, ":", or control characters`,
    );
  }
  if (!manifest.version || typeof manifest.version !== 'string') {
    problems.push('missing required field: version');
  }
  // Dev-only marker (见 12 模块发现过滤): a discovery-time flag, must be a plain
  // boolean when present so a typo (e.g. a string) never silently ships a demo.
  if (manifest.devOnly !== undefined && typeof manifest.devOnly !== 'boolean') {
    problems.push('invalid devOnly: must be a boolean when declared');
  }
  // Runtime declaration (见 18 Web 运行端): same reasoning as `devOnly`, but the
  // failure is quieter — a typo like `["Web"]` would fall back to desktop-only
  // and the module would simply vanish from the web build with no other symptom.
  if (manifest.runtimes !== undefined) {
    if (!Array.isArray(manifest.runtimes) || manifest.runtimes.length === 0) {
      problems.push(
        'invalid runtimes: must be a non-empty array when declared',
      );
    } else {
      const unknown = manifest.runtimes.filter(
        (runtime) => !MODULE_RUNTIMES.includes(runtime),
      );
      if (unknown.length > 0) {
        problems.push(
          `invalid runtimes: unknown runtime(s) ${unknown.map((r) => JSON.stringify(r)).join(', ')} (expected ${MODULE_RUNTIMES.join(' | ')})`,
        );
      }
    }
  }
  // Service tier (见 24). Fail closed: a typo like `"Host"` would otherwise read
  // as absent and quietly put a first-party service into the sandbox, where it
  // fails on its first Node call with nothing pointing back at the manifest.
  if (
    manifest.serviceTier !== undefined &&
    !SERVICE_TIERS.includes(manifest.serviceTier)
  ) {
    problems.push(
      `invalid serviceTier ${JSON.stringify(manifest.serviceTier)} (expected ${SERVICE_TIERS.join(' | ')})`,
    );
  }
  // Supported build targets (见 12). Absent = every architecture. A present
  // list is the catalog/launch/dist authority, so a typo or an OS-only key
  // must fail here rather than be read as "supports nothing" or "every arch
  // of that OS".
  let declaredTargets: Set<string> | undefined;
  if (manifest.targets !== undefined) {
    if (!Array.isArray(manifest.targets) || manifest.targets.length === 0) {
      problems.push('invalid targets: must be a non-empty array when declared');
    } else {
      declaredTargets = new Set();
      for (const key of manifest.targets) {
        if (typeof key !== 'string' || !isDistBundleTargetKey(key)) {
          problems.push(
            `invalid targets entry "${String(key)}": must be "<platform>-<arch>" (e.g. "win32-x64", "darwin-arm64")`,
          );
          continue;
        }
        if (declaredTargets.has(key)) {
          problems.push(`invalid targets: duplicate "${key}"`);
          continue;
        }
        declaredTargets.add(key);
      }
    }
  }

  // Per-runtime service map (见 19 运行端分层). A typo'd key (`"browser"` instead
  // of `"web"`) would otherwise resolve to `undefined` and turn activation into a
  // silent no-op on that runtime — the module's commands would register nowhere
  // and fail as "command not found", with nothing pointing back at the manifest.
  const service = manifest.entry?.service;
  if (service !== undefined && typeof service !== 'string') {
    if (typeof service !== 'object' || Array.isArray(service)) {
      problems.push(
        'invalid entry.service: must be a path string or a per-runtime map',
      );
    } else {
      for (const [runtime, path] of Object.entries(service)) {
        if (!MODULE_RUNTIMES.includes(runtime as ModuleRuntime)) {
          problems.push(
            `invalid entry.service: unknown runtime ${JSON.stringify(runtime)} (expected ${MODULE_RUNTIMES.join(' | ')})`,
          );
        } else if (typeof path !== 'string' || path.trim().length === 0) {
          problems.push(
            `invalid entry.service.${runtime}: must be a non-empty path string`,
          );
        }
      }
    }
  }

  // External address ownership (见 20 外链归属路由). Fail closed rather than
  // advisory: a rejected declaration would silently keep sending the module's own
  // links to a browser, while a wildcard one accepted by mistake would let this
  // module intercept clicks meant for others.
  const ownership = manifest.contributes?.urlOwnership;
  if (ownership !== undefined) {
    if (!Array.isArray(ownership)) {
      problems.push('invalid contributes.urlOwnership: must be an array');
    } else {
      const seen = new Set<string>();
      for (const prefix of ownership) {
        if (!isValidUrlOwnershipPrefix(prefix)) {
          problems.push(
            `invalid contributes.urlOwnership entry ${JSON.stringify(prefix)}: must be an exact http(s) origin plus a path prefix, without wildcards, query or hash (e.g. "https://docs.example.com/doc/")`,
          );
          continue;
        }
        if (seen.has(prefix)) {
          problems.push(
            `invalid contributes.urlOwnership: duplicate entry "${prefix}"`,
          );
        }
        seen.add(prefix);
      }
    }
  }

  // Push channel ownership (见 26). Fail closed for the same reason as
  // `urlOwnership`: an unnoticed bad declaration means this module's pushes
  // silently never arrive, and a too-broad one would capture another module's.
  const pushChannels = manifest.contributes?.pushChannels;
  if (pushChannels !== undefined) {
    if (!Array.isArray(pushChannels)) {
      problems.push('invalid contributes.pushChannels: must be an array');
    } else {
      const seen = new Set<string>();
      for (const channel of pushChannels) {
        if (!isValidPushChannel(channel)) {
          problems.push(
            `invalid contributes.pushChannels entry ${JSON.stringify(channel)}: must be a lowercase name of alphanumerics plus ".", "-" or "_", starting with an alphanumeric (e.g. "im")`,
          );
          continue;
        }
        if (seen.has(channel)) {
          problems.push(
            `invalid contributes.pushChannels: duplicate entry "${channel}"`,
          );
        }
        seen.add(channel);
      }
    }
  }

  // Main-world scripts (见 22). Fail closed hardest of the three: this bundle
  // runs unsandboxed inside untrusted pages, so a malformed declaration must
  // stop the module from loading rather than degrade to "injects nothing" — the
  // latter reads as a dapp integration bug and gets debugged in the wallet.
  const pageWorldScripts = manifest.contributes?.pageWorldScripts;
  if (pageWorldScripts !== undefined) {
    if (!Array.isArray(pageWorldScripts)) {
      problems.push('invalid contributes.pageWorldScripts: must be an array');
    } else {
      const seen = new Set<string>();
      for (const script of pageWorldScripts) {
        if (typeof script !== 'object' || script === null) {
          problems.push(
            'invalid contributes.pageWorldScripts entry: must be an object with "id" and "entry"',
          );
          continue;
        }
        if (!isValidPageWorldChannelId(script.id)) {
          problems.push(
            `invalid contributes.pageWorldScripts entry id ${JSON.stringify(script.id)}: must be a lowercase name of alphanumerics plus ".", "-" or "_", starting with an alphanumeric (e.g. "wallet.provider")`,
          );
          continue;
        }
        if (!isValidPageWorldEntry(script.entry)) {
          problems.push(
            `invalid contributes.pageWorldScripts entry "${script.id}": entry must be a module-relative "./" path without ".." segments (e.g. "./pageworld/index.ts")`,
          );
          continue;
        }
        if (seen.has(script.id)) {
          problems.push(
            `invalid contributes.pageWorldScripts: duplicate entry id "${script.id}"`,
          );
        }
        seen.add(script.id);
      }
    }
  }

  // Declaring a build-time-only key is self-contradictory (见 05): declaring it
  // means the module reads it at runtime, while the `_` marker means it is
  // stripped from every shipped artifact and from `getProductConfig`. Left
  // unchecked the module would read its declared default in the repo and get
  // `undefined` in a package — a divergence that only surfaces after release.
  for (const key of Object.keys(manifest.contributes?.productConfig ?? {})) {
    if (isBuildOnlyConfigKey(key)) {
      problems.push(
        `invalid contributes.productConfig key "${key}": segments starting with "_" mark build-time-only values, which modules can never read`,
      );
    }
  }

  const platformRange = manifest.engines?.platform;
  if (platformRange && !satisfies(platformVersion, platformRange)) {
    problems.push(
      `incompatible engines.platform: requires "${platformRange}" but platform is "${platformVersion}"`,
    );
  }

  // Protocol is a hard gate too (架构方案 §5.2): a module declaring an SDK
  // protocol range it cannot run against is skipped, so an old extension never
  // boots on an incompatible-protocol platform (G9).
  const protocolRange = manifest.engines?.protocol;
  if (protocolRange && !satisfies(protocolVersion, protocolRange)) {
    problems.push(
      `incompatible engines.protocol: requires "${protocolRange}" but protocol is "${protocolVersion}"`,
    );
  }

  // The output root (§5.3/§5.4.1) is joined onto a module's on-disk root to
  // locate runtime artifacts, so a declared value must be a contained relative
  // path: reject absolutes and any `..` segment that could escape the module
  // directory (path-traversal guard for the loader/packager).
  if (manifest.output !== undefined) {
    const output = manifest.output;
    if (typeof output !== 'string' || output.length === 0) {
      problems.push('invalid output: must be a non-empty relative path');
    } else if (/^([a-zA-Z]:)?[\\/]/.test(output)) {
      problems.push(`invalid output: must be relative, got "${output}"`);
    } else if (output.split(/[\\/]/).some((segment) => segment === '..')) {
      problems.push(`invalid output: must not contain "..", got "${output}"`);
    }
  }

  // Child-process entries (架构方案 §3.1/§5.8): the array form declares several
  // processes for one module, so each needs its own non-empty unique `id` — that
  // id keys the process (`<moduleId>#<id>`) and is what an `external` view binds
  // to. An unnamed or duplicated entry there would make the binding ambiguous.
  const externalEntries = listExternalEntries(manifest.entry);
  if (Array.isArray(manifest.entry?.external)) {
    const seen = new Set<string>();
    for (const entry of externalEntries) {
      if (!entry.id || typeof entry.id !== 'string') {
        problems.push(
          'invalid entry.external: every entry of the array form needs an "id"',
        );
        continue;
      }
      if (seen.has(entry.id)) {
        problems.push(`invalid entry.external: duplicate id "${entry.id}"`);
      }
      seen.add(entry.id);
    }
  }
  // Launch overrides are addressed by the same build-target key as
  // `distBundle.platform` (见 12). An OS-only key (`win32`/`darwin`/`linux`)
  // cannot tell a catalog which architectures the base package actually
  // contains, so it is rejected rather than treated as "every arch of that OS".
  const checkExternalPlatform = (entry: ExternalEntry): void => {
    const targets = entry.platform;
    if (targets === undefined) {
      return;
    }
    const field = entry.id
      ? `entry.external["${entry.id}"].platform`
      : 'entry.external.platform';
    if (
      typeof targets !== 'object' ||
      targets === null ||
      Array.isArray(targets)
    ) {
      problems.push(
        `invalid ${field}: must be an object keyed by "<platform>-<arch>"`,
      );
      return;
    }
    const keys = Object.keys(targets);
    if (keys.length === 0) {
      problems.push(
        `invalid ${field}: must declare at least one "<platform>-<arch>" target`,
      );
      return;
    }
    for (const key of keys) {
      if (!isDistBundleTargetKey(key)) {
        problems.push(
          `invalid ${field} key "${key}": must be "<platform>-<arch>" (e.g. "win32-x64", "darwin-arm64")`,
        );
        continue;
      }
      if (declaredTargets && !declaredTargets.has(key)) {
        problems.push(`invalid ${field} key "${key}": not listed in targets`);
      }
    }
  };
  for (const entry of externalEntries) {
    checkExternalPlatform(entry);
  }

  // A bundle declaration (§5.4.1/§5.6) feeds the dynamic-download pipeline, so
  // its supply-chain fields are gated up-front (fail closed): a malformed URL
  // or digest must never reach the downloader. Shared by `bundle` (whole module)
  // and `distBundle` (lazy dist, 见 12 两阶段下载).
  //
  // Only the shape is checked. Whether a declaration is *useful* depends on how
  // the module was discovered, not on the manifest: for a statically compiled
  // module the shell's build-time glob owns its code, so a `bundle` there is
  // simply unused — while a packaged module carrying a local `distBundle.path`
  // is a supported two-phase delivery (见 12).
  const checkBundleFields = (
    bundle: BundleDeclaration,
    field: string,
  ): void => {
    if (typeof bundle.url !== 'string' || !isHttpUrl(bundle.url)) {
      problems.push(
        `invalid ${field}.url: must be an http(s) URL, got "${String(bundle.url)}"`,
      );
    }
    if (typeof bundle.sha256 !== 'string' || !isValidSha256(bundle.sha256)) {
      problems.push(
        `invalid ${field}.sha256: must be a 64-character hex sha256 digest`,
      );
    }
    if (
      bundle.size !== undefined &&
      (!Number.isSafeInteger(bundle.size) || bundle.size <= 0)
    ) {
      problems.push(
        `invalid ${field}.size: must be a positive integer (bytes)`,
      );
    }
    if (
      bundle.signature !== undefined &&
      (typeof bundle.signature !== 'string' || bundle.signature.length === 0)
    ) {
      problems.push(
        `invalid ${field}.signature: must be a non-empty string when declared`,
      );
    }
  };
  if (manifest.bundle !== undefined) {
    checkBundleFields(manifest.bundle, 'bundle');
  }
  // One lazy dist archive (见 12 两阶段下载): exactly one of a remote `url`
  // (allowlist + mandatory sha256) or a local module-relative `path` (self-carried
  // archive, trusted like a sideload — sha256 optional). Reject shape errors up
  // front so the loader never fetches an ambiguous/traversing source. Applied
  // identically to a bare declaration and to each entry of a `platform` map, so
  // a per-target archive can never be held to a laxer standard than a single one.
  const checkDistBundleSource = (d: DistBundleSource, field: string): void => {
    const hasUrl = d.url !== undefined;
    const hasPath = d.path !== undefined;
    if (hasUrl === hasPath) {
      problems.push(
        `invalid ${field}: declare exactly one of "url" (remote) or "path" (local)`,
      );
    }
    if (hasUrl) {
      if (typeof d.url !== 'string' || !isHttpUrl(d.url)) {
        problems.push(
          `invalid ${field}.url: must be an http(s) URL, got "${String(d.url)}"`,
        );
      }
      if (typeof d.sha256 !== 'string' || !isValidSha256(d.sha256)) {
        problems.push(
          `invalid ${field}.sha256: a remote dist bundle requires a 64-character hex sha256 digest`,
        );
      }
    }
    if (hasPath) {
      if (typeof d.path !== 'string' || d.path.length === 0) {
        problems.push(
          `invalid ${field}.path: must be a non-empty relative path`,
        );
      } else if (/^([a-zA-Z]:)?[\\/]/.test(d.path)) {
        problems.push(
          `invalid ${field}.path: must be relative, got "${d.path}"`,
        );
      } else if (d.path.split(/[\\/]/).some((segment) => segment === '..')) {
        problems.push(
          `invalid ${field}.path: must not contain "..", got "${d.path}"`,
        );
      }
      // sha256 is optional for a local (trusted) archive; validate only if given.
      if (
        d.sha256 !== undefined &&
        (typeof d.sha256 !== 'string' || !isValidSha256(d.sha256))
      ) {
        problems.push(
          `invalid ${field}.sha256: must be a 64-character hex sha256 digest when declared`,
        );
      }
    }
    if (
      d.size !== undefined &&
      (!Number.isSafeInteger(d.size) || d.size <= 0)
    ) {
      problems.push(
        `invalid ${field}.size: must be a positive integer (bytes)`,
      );
    }
  };
  // A dist bundle is either one archive for every target, or a `platform` map
  // holding one per `${process.platform}-${process.arch}` build target (见 12).
  // The two are exclusive: a declaration carrying both would leave "which one
  // wins" to whoever reads it next, and `resolveDistBundleTarget` deliberately
  // has no such precedence.
  if (manifest.distBundle !== undefined) {
    const d = manifest.distBundle;
    const targets = d.platform;
    if (targets === undefined) {
      checkDistBundleSource(d, 'distBundle');
    } else if (
      typeof targets !== 'object' ||
      targets === null ||
      Array.isArray(targets)
    ) {
      problems.push(
        'invalid distBundle.platform: must be an object keyed by "<platform>-<arch>"',
      );
    } else if (d.url !== undefined || d.path !== undefined) {
      problems.push(
        'invalid distBundle: declare either a single "url"/"path" or a "platform" map, not both',
      );
    } else {
      const keys = Object.keys(targets);
      if (keys.length === 0) {
        problems.push(
          'invalid distBundle.platform: must declare at least one "<platform>-<arch>" target',
        );
      }
      for (const key of keys) {
        if (!isDistBundleTargetKey(key)) {
          problems.push(
            `invalid distBundle.platform key "${key}": must be "<platform>-<arch>" (e.g. "win32-x64", "darwin-arm64")`,
          );
          continue;
        }
        if (declaredTargets && !declaredTargets.has(key)) {
          problems.push(
            `invalid distBundle.platform key "${key}": not listed in targets`,
          );
        }
        const target = targets[key];
        if (typeof target !== 'object' || target === null) {
          problems.push(
            `invalid distBundle.platform.${key}: must be an object declaring "url" or "path"`,
          );
          continue;
        }
        checkDistBundleSource(target, `distBundle.platform.${key}`);
      }
    }
  }

  // Exported API namespaces (架构方案 §5.5): each entry drives cross-module
  // reverse lookup and derives an `onApi:<namespace>` activation event, so a
  // malformed or duplicate namespace would break resolution — reject up front.
  if (manifest.exportsApi !== undefined) {
    if (!Array.isArray(manifest.exportsApi)) {
      problems.push('invalid exportsApi: must be an array of declarations');
    } else {
      const seen = new Set<string>();
      for (const decl of manifest.exportsApi) {
        if (!decl.namespace || typeof decl.namespace !== 'string') {
          problems.push('invalid exportsApi entry: missing namespace');
          continue;
        }
        if (seen.has(decl.namespace)) {
          problems.push(
            `invalid exportsApi: duplicate namespace "${decl.namespace}"`,
          );
        }
        seen.add(decl.namespace);
      }
    }
  }

  // View cache policy (架构方案 §5.7 视图缓存): an unknown value would silently
  // fall back to the platform default, hiding a typo — reject it up front.
  // View render kind + placement (架构方案 §5.8 兼容矩阵): the effective kind
  // ({@link resolveViewKind}) must be backed by an actual supply channel
  // (供给能力 × kind, below) and hostable by its area (kind×area).
  // Any module may mix kinds — a module with `entry.external` can also declare
  // `entry.ui` + an inline view as its own launcher page — so the gate is purely
  // what the manifest supplies. Fail closed so an unsupported combination never
  // reaches the renderer.
  const views = manifest.contributes?.views;
  if (views !== undefined && Array.isArray(views)) {
    const facets = deriveModuleFacets(manifest, { dynamic: options.dynamic });
    for (const view of views) {
      if (
        view.cache !== undefined &&
        !VALID_VIEW_CACHE_POLICIES.includes(view.cache)
      ) {
        problems.push(
          `invalid views["${view.id}"].cache: ${String(view.cache)} (expected one of ${VALID_VIEW_CACHE_POLICIES.join(', ')})`,
        );
      }

      if (view.kind !== undefined && !VALID_VIEW_KINDS.includes(view.kind)) {
        problems.push(
          `invalid views["${view.id}"].kind: ${String(view.kind)} (expected one of ${VALID_VIEW_KINDS.join(', ')})`,
        );
        continue;
      }
      // 供给能力 × kind (架构方案 §5.8 兼容矩阵), on the effective kind: each kind
      // needs the channel that actually feeds it, else the surface would hang on
      // Loading / render nothing:
      //  - `inline` ← renderer React code. For a dynamic module (下发/侧载/dev
      //    link) that is only module-level `entry.ui` — `ModuleManager` resolves
      //    the inline view's url from it and ignores a view-level `url` — so a
      //    missing entry is fatal. A repo module is fed by the shell's
      //    compile-time `ui/index.tsx` glob, which the manifest cannot see, so
      //    it is left to the advisory `checkEntryConventions`.
      //  - `iframe` ← a `url` (the sandboxed frame src).
      //  - `external` ← `entry.external` (the process the panel controls).
      //  - `remote` is a reserved placeholder with no supply channel yet.
      const kind = resolveViewKind(view, manifest);
      if (kind === 'inline' && options.dynamic && !facets.hasRendererUi) {
        problems.push(
          `invalid views["${view.id}"]: an inline view requires renderer UI code, but this module declares no "entry.ui"`,
        );
      }
      if (kind === 'iframe' && !view.url) {
        problems.push(
          `invalid views["${view.id}"]: an iframe view requires a "url"`,
        );
      }
      if (kind === 'external') {
        if (!facets.hasExternalProcess) {
          problems.push(
            `invalid views["${view.id}"]: an external view requires an "entry.external" process to control`,
          );
        } else if (!findExternalEntry(manifest.entry, view.externalEntry)) {
          // The panel drives exactly one process, so its binding must resolve:
          // either the named entry exists, or the module has a lone entry to
          // default to. Several entries + no name is ambiguous, not defaultable.
          problems.push(
            view.externalEntry !== undefined
              ? `invalid views["${view.id}"].externalEntry: no entry.external declares id "${view.externalEntry}"`
              : `invalid views["${view.id}"]: this module declares ${String(externalEntries.length)} entry.external entries, so the view must name one via "externalEntry"`,
          );
        }
      }

      // kind × area (架构方案 §5.8 兼容矩阵), on the effective kind:
      //  - `external` has no inline content, only a process control panel, and is
      //    only wired into the `editor` area.
      //  - `overlay` (modal/drawer) hosts `inline` or `iframe`: an inline view
      //    settles via the `overlayControl` prop, an iframe view settles via the
      //    SDK overlay channel (host-initiated open → broker → OVERLAY_RESOLVED
      //    broadcast), so neither needs the other's path.
      //  - `activityBar` (custom launcher) hosts only inline React — a launcher
      //    must draw a rail button, not a page.
      //  - `remote` is a reserved placeholder that renders "unavailable" in any
      //    non-overlay/non-launcher area, so it is left unconstrained here.
      if (kind === 'external') {
        if (view.area !== 'editor') {
          problems.push(
            `invalid views["${view.id}"]: external views are only supported in the "editor" area, got "${view.area}"`,
          );
        }
      } else if (
        view.area === 'overlay' &&
        kind !== 'inline' &&
        kind !== 'iframe'
      ) {
        problems.push(
          `invalid views["${view.id}"]: overlay views must be inline or iframe, got kind "${kind}"`,
        );
      } else if (view.area === 'activityBar' && kind !== 'inline') {
        problems.push(
          `invalid views["${view.id}"]: activityBar launcher views must be inline, got kind "${kind}"`,
        );
      } else if (view.area === 'shell' && kind !== 'inline') {
        problems.push(
          `invalid views["${view.id}"]: shell views must be inline, got kind "${kind}"`,
        );
      }
    }
  }

  const setting = manifest.contributes?.setting;
  if (setting !== undefined) {
    if (
      typeof setting !== 'object' ||
      setting === null ||
      Array.isArray(setting)
    ) {
      problems.push('invalid contributes.setting: must be an object');
    } else {
      pushSettingProblems(
        problems,
        setting,
        Array.isArray(views) ? views : undefined,
        manifest.contributes?.settings,
      );
    }
  }

  // Top-level shells are module-owned policy/UI projected through the generic
  // renderer ShellHost. They may only target an inline view declared by the
  // same module in the dedicated `shell` area.
  const shells = manifest.contributes?.shells;
  if (shells !== undefined) {
    if (!Array.isArray(shells)) {
      problems.push('invalid shells: must be an array');
    } else {
      const seen = new Set<string>();
      for (const shell of shells) {
        const id = shell?.id;
        if (!id || typeof id !== 'string' || !/^[a-z][a-z0-9.-]*$/i.test(id)) {
          problems.push(
            `invalid shells entry: id must match /^[a-z][a-z0-9.-]*$/i`,
          );
          continue;
        }
        if (seen.has(id)) {
          problems.push(`invalid shells: duplicate id "${id}"`);
        }
        seen.add(id);

        const view = Array.isArray(views)
          ? views.find((candidate) => candidate.id === shell.viewId)
          : undefined;
        if (!view) {
          problems.push(
            `invalid shells["${id}"].viewId: view "${shell.viewId}" is not declared by this module`,
          );
          continue;
        }
        if (view.area !== 'shell') {
          problems.push(
            `invalid shells["${id}"].viewId: view "${shell.viewId}" must use area "shell"`,
          );
        }
        if (resolveViewKind(view, manifest) !== 'inline') {
          problems.push(
            `invalid shells["${id}"].viewId: shell views must be inline`,
          );
        }
      }
    }
  }

  // Contributed commands (架构方案 §5.9). Ids share one global registry and
  // ownership is resolved by scanning manifests, not by parsing the prefix — so
  // a module declaring someone else's id would inherit that module's
  // "own commands are exempt" bypass in `executeCommandAsModule`. Confining each
  // module to its own namespace (the rule `canEmitEvent` already applies to
  // events) closes that, and keeps `workbench.` free for the platform.
  const commands = manifest.contributes?.commands;
  if (commands !== undefined) {
    if (!Array.isArray(commands)) {
      problems.push('invalid commands: must be an array');
    } else {
      const seen = new Set<string>();
      for (const command of commands) {
        const id = command?.id;
        if (!id || typeof id !== 'string') {
          problems.push('invalid commands entry: missing id');
          continue;
        }
        if (seen.has(id)) {
          problems.push(`invalid commands: duplicate id "${id}"`);
        }
        seen.add(id);
        if (id.startsWith(PLATFORM_COMMAND_PREFIX)) {
          problems.push(
            `invalid commands["${id}"]: the "${PLATFORM_COMMAND_PREFIX}" namespace is reserved for platform built-in commands`,
          );
        } else if (
          typeof manifest.id === 'string' &&
          manifest.id.length > 0 &&
          !isInModuleNamespace(manifest.id, id)
        ) {
          problems.push(
            `invalid commands["${id}"]: must be "${manifest.id}" or start with "${manifest.id}."`,
          );
        }
        // Same reasoning as the module-level `runtimes` check: a typo'd runtime
        // would silently drop the command from that host's listing with no other
        // symptom.
        if (command.runtimes !== undefined) {
          if (
            !Array.isArray(command.runtimes) ||
            command.runtimes.length === 0
          ) {
            problems.push(
              `invalid commands["${id}"].runtimes: must be a non-empty array when declared`,
            );
          } else {
            const unknown = command.runtimes.filter(
              (runtime) => !MODULE_RUNTIMES.includes(runtime),
            );
            if (unknown.length > 0) {
              problems.push(
                `invalid commands["${id}"].runtimes: unknown runtime(s) ${unknown.map((r) => JSON.stringify(r)).join(', ')} (expected ${MODULE_RUNTIMES.join(' | ')})`,
              );
            }
          }
        }
      }
    }
  }

  // Submenu placeholders (架构方案 §5.3). Uniqueness is per-module here;
  // cross-module collisions are first-wins + advisory at `listContributions`
  // (naming is conventional `<moduleId>.<name>`, not a load gate — a foreign
  // id is how a typo would otherwise look identical to "I never declared it").
  // Label is required — the parent row has no command to borrow a title from.
  const submenus = manifest.contributes?.submenus;
  if (submenus !== undefined) {
    if (!Array.isArray(submenus)) {
      problems.push('invalid submenus: must be an array');
    } else {
      const seen = new Set<string>();
      for (const submenu of submenus) {
        const id = submenu?.id;
        if (!id || typeof id !== 'string') {
          problems.push('invalid submenus entry: missing id');
          continue;
        }
        if (seen.has(id)) {
          problems.push(`invalid submenus: duplicate id "${id}"`);
        }
        seen.add(id);
        if (!submenu.label || typeof submenu.label !== 'string') {
          problems.push(`invalid submenus["${id}"]: missing label`);
        }
      }
    }
  }

  // Menu placements (架构方案 §5.3). Structural XOR only — a placement that
  // names a command or submenu nobody declares is left to the resolver, same
  // as today's dangling-command log, so a typo stays distinguishable from a
  // `when` that did not hold (见 02: menus 不校引用).
  const menus = manifest.contributes?.menus;
  if (menus !== undefined) {
    if (typeof menus !== 'object' || menus === null || Array.isArray(menus)) {
      problems.push(
        'invalid menus: must be an object of location → placements',
      );
    } else {
      for (const [location, items] of Object.entries(menus)) {
        if (!Array.isArray(items)) {
          problems.push(`invalid menus["${location}"]: must be an array`);
          continue;
        }
        for (const item of items) {
          const raw = item as { command?: unknown; submenu?: unknown };
          const hasCommand =
            typeof raw.command === 'string' && raw.command.length > 0;
          const hasSubmenu =
            typeof raw.submenu === 'string' && raw.submenu.length > 0;
          if (hasCommand === hasSubmenu) {
            problems.push(
              `invalid menus["${location}"]: each placement needs exactly one of "command" or "submenu"`,
            );
          }
        }
      }
    }
  }

  // Module-defined contribution points (见 17): points this module owns must
  // have unique, non-empty ids, since they drive cross-module contribution
  // aggregation and the `contribution:<id>` permission gate.
  const points = manifest.contributes?.contributionPoints;
  if (points !== undefined) {
    if (!Array.isArray(points)) {
      problems.push('invalid contributionPoints: must be an array');
    } else {
      const seen = new Set<string>();
      for (const point of points) {
        if (!point.id || typeof point.id !== 'string') {
          problems.push('invalid contributionPoints entry: missing id');
          continue;
        }
        if (seen.has(point.id)) {
          problems.push(
            `invalid contributionPoints: duplicate id "${point.id}"`,
          );
        }
        seen.add(point.id);
      }
    }
  }

  // Entries this module contributes to points (见 17): each needs a target
  // `point` + an `id`; (point,id) must be unique within this module so runtime
  // binding keys don't collide. Point existence is NOT checked — a module may
  // legitimately contribute to a point owned by a different module.
  const contributions = manifest.contributes?.contributions;
  if (contributions !== undefined) {
    if (!Array.isArray(contributions)) {
      problems.push('invalid contributions: must be an array');
    } else {
      const seen = new Set<string>();
      for (const entry of contributions) {
        if (!entry.point || typeof entry.point !== 'string') {
          problems.push('invalid contributions entry: missing point');
          continue;
        }
        if (!entry.id || typeof entry.id !== 'string') {
          problems.push(
            `invalid contributions entry for point "${entry.point}": missing id`,
          );
          continue;
        }
        const key = `${entry.point}::${entry.id}`;
        if (seen.has(key)) {
          problems.push(
            `invalid contributions: duplicate (point, id) "${entry.point}"/"${entry.id}"`,
          );
        }
        seen.add(key);
      }
    }
  }

  // Curations of module-defined points (见 17). Each entry needs a point and
  // exactly one of allow/deny. Whether a scope applies it is a runtime concern
  // (`applyContributionCuration`); a duplicate point in this module is reported
  // there, not here, so the module still loads.
  const curations = manifest.contributes?.contributionCurations;
  if (curations !== undefined) {
    if (!Array.isArray(curations)) {
      problems.push('invalid contributionCurations: must be an array');
    } else {
      for (const entry of curations) {
        if (!entry.point || typeof entry.point !== 'string') {
          problems.push('invalid contributionCurations entry: missing point');
          continue;
        }
        const hasAllow = entry.allow !== undefined;
        const hasDeny = entry.deny !== undefined;
        if (hasAllow === hasDeny) {
          problems.push(
            `invalid contributionCurations entry for point "${entry.point}": exactly one of "allow" or "deny"`,
          );
          continue;
        }
        const keys = hasAllow ? entry.allow : entry.deny;
        if (!Array.isArray(keys)) {
          problems.push(
            `invalid contributionCurations entry for point "${entry.point}": "allow" and "deny" must be arrays of keys`,
          );
          continue;
        }
        if (hasDeny && keys.length === 0) {
          problems.push(
            `invalid contributionCurations entry for point "${entry.point}": "deny" must list at least one key`,
          );
        }
        for (const key of keys) {
          if (typeof key !== 'string' || key.length === 0) {
            problems.push(
              `invalid contributionCurations entry for point "${entry.point}": keys must be non-empty strings`,
            );
          }
        }
      }
    }
  }

  // Global-search sections (见 `search-contribution.ts`): each needs an id
  // unique within the module and a `command` this same manifest declares.
  //
  // The command check is the load-bearing one. Undeclared means no
  // `onCommand:<id>` activation event, so a module that is not already active
  // can never be pulled up to answer — and the symptom is the worst kind:
  // the section is simply always empty, indistinguishable from "no hits".
  const searchSections = manifest.contributes?.search;
  if (searchSections !== undefined) {
    if (!Array.isArray(searchSections)) {
      problems.push('invalid search: must be an array');
    } else {
      const declaredCommands = new Set(
        (manifest.contributes?.commands ?? []).map((command) => command.id),
      );
      const seen = new Set<string>();
      for (const section of searchSections) {
        if (!section.id || typeof section.id !== 'string') {
          problems.push('invalid search entry: missing id');
          continue;
        }
        if (seen.has(section.id)) {
          problems.push(`invalid search: duplicate id "${section.id}"`);
        }
        seen.add(section.id);
        if (!section.title || typeof section.title !== 'string') {
          problems.push(`invalid search entry "${section.id}": missing title`);
        }
        if (!section.command || typeof section.command !== 'string') {
          problems.push(
            `invalid search entry "${section.id}": missing command`,
          );
          continue;
        }
        if (!declaredCommands.has(section.command)) {
          problems.push(
            `invalid search entry "${section.id}": command "${section.command}" is not declared in contributes.commands`,
          );
        }
      }
    }
  }

  // Global-search filters. A `picker` needs an `optionsCommand`, and it is
  // checked against the same declared set for the same reason a section's
  // command is: without `onCommand:<id>` an inactive module can never be pulled
  // up, and the picker would just look like it has no candidates.
  const searchFilters = manifest.contributes?.searchFilters;
  if (searchFilters !== undefined) {
    if (!Array.isArray(searchFilters)) {
      problems.push('invalid searchFilters: must be an array');
    } else {
      const declaredCommands = new Set(
        (manifest.contributes?.commands ?? []).map((command) => command.id),
      );
      const seen = new Set<string>();
      for (const filter of searchFilters) {
        if (!filter.id || typeof filter.id !== 'string') {
          problems.push('invalid searchFilters entry: missing id');
          continue;
        }
        if (seen.has(filter.id)) {
          problems.push(`invalid searchFilters: duplicate id "${filter.id}"`);
        }
        seen.add(filter.id);
        if (!filter.title || typeof filter.title !== 'string') {
          problems.push(
            `invalid searchFilters entry "${filter.id}": missing title`,
          );
        }
        if (filter.type !== 'picker' && filter.type !== 'dateRange') {
          problems.push(
            `invalid searchFilters entry "${filter.id}": type must be "picker" or "dateRange"`,
          );
          continue;
        }
        if (filter.type !== 'picker') {
          continue;
        }
        if (
          !filter.optionsCommand ||
          typeof filter.optionsCommand !== 'string'
        ) {
          problems.push(
            `invalid searchFilters entry "${filter.id}": a picker needs an optionsCommand`,
          );
          continue;
        }
        if (!declaredCommands.has(filter.optionsCommand)) {
          problems.push(
            `invalid searchFilters entry "${filter.id}": optionsCommand "${filter.optionsCommand}" is not declared in contributes.commands`,
          );
        }
      }
    }
  }

  return problems;
}

/**
 * Build the SDK handshake for a module (架构方案 §5.2 版本协商 + 能力探测). Pure:
 * the live versions, host runtime and capability list are injected, so it is
 * unit-testable without a running host. `compatible` is true when the module's
 * declared `engines` ranges (if any) are satisfied by the live versions; absent
 * ranges impose no constraint. The capability list is passed through for
 * feature detection on the consumer side; `runtime` is the host-declared
 * {@link ModuleRuntime} (`desktop` | `web`) and {@link HostOs}.
 */
export function buildHandshake(
  engines: ModuleManifest['engines'],
  opts: {
    platformVersion: string;
    protocolVersion: string;
    runtime: ModuleRuntime;
    os: HostOs;
    capabilities: string[];
  },
): PlatformHandshake {
  const reasons: string[] = [];
  if (engines?.platform && !satisfies(opts.platformVersion, engines.platform)) {
    reasons.push(
      `platform "${opts.platformVersion}" does not satisfy "${engines.platform}"`,
    );
  }
  if (engines?.protocol && !satisfies(opts.protocolVersion, engines.protocol)) {
    reasons.push(
      `protocol "${opts.protocolVersion}" does not satisfy "${engines.protocol}"`,
    );
  }
  return {
    platformVersion: opts.platformVersion,
    protocolVersion: opts.protocolVersion,
    runtime: opts.runtime,
    os: opts.os,
    capabilities: opts.capabilities,
    compatible: reasons.length === 0,
    ...(reasons.length > 0 ? { reason: reasons.join('; ') } : {}),
  };
}

/**
 * Advisory (non-fatal) consistency check for a module's `entry` (架构方案 §5.3).
 *
 * Statically discovered (in-repo / packaged, i.e. non-`dynamic`) modules are
 * loaded from the conventional paths (`SOURCE_MODULE_ENTRY`) through the shell's
 * build-time glob, which ignores `entry`, so a declared `entry` that diverges
 * from the convention is misleading. This pure function surfaces such
 * divergences as human-readable warnings (empty array when fine).
 *
 * Callers must only run it for statically discovered modules — for `dynamic`
 * ones (downloaded / sideloaded / dev-linked) `entry` is authoritative, so there
 * is nothing to reconcile. `external` entries are never affected: they are
 * launched from `entry.external` in both cases.
 *
 * Unlike `validateManifest`, the returned messages are advisory: the Module
 * Manager logs them but does NOT reject the module (it still loads from the
 * conventional path).
 */
export function checkEntryConventions(manifest: ModuleManifest): string[] {
  const warnings: string[] = [];
  const { service, ui } = manifest.entry ?? {};
  // Service: the manifest is authoritative (见 19 清单优先). A prebuilt path
  // (`./dist/service.js`) is loaded as declared — it is not "ignored". A
  // `./src/…` path that is not among the conventional candidates will be loaded
  // from disk rather than bundled, which is almost certainly a mistake (the
  // desktop glob is fixed on `src/index.ts`), so that still gets an advisory.
  if (typeof service === 'string') {
    if (
      service.startsWith('./src/') &&
      service !== SOURCE_MODULE_ENTRY.service
    ) {
      warnings.push(
        `entry.service "${service}" is a source path this runtime does not bundle; declare the conventional "${SOURCE_MODULE_ENTRY.service}" or a prebuilt artifact`,
      );
    }
  } else if (service) {
    for (const runtime of MODULE_RUNTIMES) {
      const declared = service[runtime];
      if (!declared) {
        continue;
      }
      const candidates = sourceServiceEntryCandidates(runtime);
      if (declared.startsWith('./src/') && !candidates.includes(declared)) {
        warnings.push(
          `entry.service.${runtime} "${declared}" is a source path this runtime does not bundle; declare a conventional ${candidates.map((path) => `"${path}"`).join(' or ')} or a prebuilt artifact`,
        );
      }
    }
  }
  if (ui && ui !== SOURCE_MODULE_ENTRY.ui) {
    warnings.push(
      `entry.ui "${ui}" is ignored for statically bundled modules; code is loaded from the conventional "${SOURCE_MODULE_ENTRY.ui}"`,
    );
  }
  return warnings;
}

/**
 * Manifest fields the platform has removed (架构方案 §3). A leftover declaration
 * is harmless — unknown fields are ignored, never rejected, so an old manifest
 * keeps loading — but silently ignoring it hides a stale contract from its
 * author, so callers surface these as advisory warnings (empty array when clean).
 *
 * Unlike {@link checkEntryConventions} this applies to **every** module however it
 * was discovered: the stale declarations live mostly in downloaded/sideloaded
 * manifests, whose authors are exactly the people who need the notice.
 */
export function checkRemovedManifestFields(manifest: ModuleManifest): string[] {
  const warnings: string[] = [];
  const fields = manifest as unknown as Record<string, unknown>;
  if (fields.integration !== undefined) {
    warnings.push(
      `"integration" was removed and is ignored: a module has no single render label any more — declare each view's render kind (views[].kind) instead; its execution model follows from "entry"`,
    );
  }
  const exportsApi = fields.exportsApi;
  if (
    Array.isArray(exportsApi) &&
    exportsApi.some(
      (entry) =>
        entry !== null &&
        typeof entry === 'object' &&
        'apiVersion' in (entry as Record<string, unknown>),
    )
  ) {
    warnings.push(
      `"exportsApi[].apiVersion" was removed and is ignored: getApi matches a namespace only — express capability differences by feature detection at the call site`,
    );
  }
  return warnings;
}

/**
 * Advisory when a contributed view id sits outside the owning module's
 * namespace (见 03). The recommended shape matches commands
 * ({@link isInModuleNamespace}): the id *is* the module id, or starts with
 * `<moduleId>.`. A mismatch still loads — unlike commands, this is not a
 * `validateManifest` failure — so an existing manifest keeps working while the
 * author sees the drift. No camelCase rewrite is attempted; any id outside the
 * namespace gets the same warning.
 *
 * Applies to every module however it was discovered (same as
 * {@link checkRemovedManifestFields}): sideloaded packages are where the
 * mismatched ids live. Empty when `views` is absent or every id is in
 * namespace. A missing module id is left to `validateManifest`.
 */
export function checkViewIdNamespace(manifest: ModuleManifest): string[] {
  const moduleId = manifest.id;
  const views = manifest.contributes?.views;
  if (typeof moduleId !== 'string' || moduleId.length === 0) {
    return [];
  }
  if (!Array.isArray(views)) {
    return [];
  }
  const warnings: string[] = [];
  for (const view of views) {
    const id = view?.id;
    if (typeof id !== 'string' || isInModuleNamespace(moduleId, id)) {
      continue;
    }
    warnings.push(
      `views["${id}"]: id is outside module "${moduleId}"'s namespace; declare "${moduleId}" or an id starting with "${moduleId}."`,
    );
  }
  return warnings;
}
